import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Assignment, ClassMember, AssignmentCategory, AssignmentStudentStatus, AssignmentStatusValue, RoutineAssignment, RoutineAssignmentRecord, RoutineAssignmentStudentStatus, RoutineAssignmentDateStatus } from '../types/alarm';
import { routineAssignmentService } from '../services/routineAssignmentService';
import { RoutineAssignmentConsolidatedService } from '../services/routineAssignmentConsolidatedService';
import { GeneralAssignmentConsolidatedService } from '../services/generalAssignmentConsolidatedService';
import { assignmentMemoService } from '../services/assignmentMemoService';
import { AssignmentRewardService } from '../services/assignmentRewardService';
import { ProjectAssignmentRewardService } from '../services/projectAssignmentRewardService';
import { GeneralAssignmentRewardService } from '../services/generalAssignmentRewardService';
import { Toast } from './Toast';
import StudentDetailModal from './StudentDetailModal';
import ConfirmModal from './ConfirmModal';
import { getPointsByConfirmedPercent } from '../utils/assignmentPoints';
import {
  buildGeneralAssignmentsExportHtml,
  buildProjectAssignmentsExportHtml,
  formatAssignmentDueDate,
  openAssignmentPrintWindow,
  sortStudentsForPrint,
  AssignmentPrintStudent
} from '../utils/assignmentPrintUtils';
import { projectAssignmentService } from '../services/projectAssignmentService';
import { isStandardPlanOrAbove } from '../utils/licenseLimits';

interface AssignmentCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (studentStatuses: AssignmentStudentStatus[], selectedDate?: Date) => void;
  mode: 'general' | 'routine' | 'project';
  // 일반 과제용 props
  assignment?: Assignment;
  // 루틴 과제용 props (일반 과제와 동일한 구조)
  routine?: RoutineAssignment;
  // 프로젝트 과제용 props
  projectAssignment?: any;
  projectSubTasks?: any[];
  selectedSubTaskId?: string | null;
  currentSubTaskIndex?: number;
  onSubTaskChange?: (newIndex: number) => void;
  // 공통 props
  members: ClassMember[];
  category?: AssignmentCategory;
  existingStatuses?: AssignmentStudentStatus[];
  defaultPoints?: number;
  statusDefaultPoints?: {
    pending: number;
    confirmed: number;
    delayed: number;
    exempted: number;
  };
  /** pendingCount: 미확인(pending)+제출확인(submitted) 합계(과제 블럭·목록과 동일). submittedCount는 선택적으로 함께 갱신 */
  onPendingCountUpdate?: (pendingCount: number, submittedCount?: number) => void;
  // 루틴 과제 데이터 로드용 props
  userId?: string;
  classId?: string;
  userType?: string;
  memberGroup?: string;
  // 🎯 선택된 날짜 (루틴 과제 체크 모달에서 사용)
  selectedDate?: Date;
  // 🎯 커스텀 날짜 (DateInputModal에서 전달받은 날짜)
  customDate?: Date;
  // 🎯 저장 버튼 텍스트 (기본값: "저장")
  saveButtonText?: string;
}

const AssignmentCheckModal: React.FC<AssignmentCheckModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  assignment,
  routine,
  projectAssignment,
  projectSubTasks = [],
  selectedSubTaskId,
  currentSubTaskIndex = 0,
  onSubTaskChange,
  members,
  category,
  existingStatuses = [],
  defaultPoints = 1,
  statusDefaultPoints,
  onPendingCountUpdate,
  userId,
  classId,
  userType,
  memberGroup,
  selectedDate,
  customDate,
  saveButtonText = "저장"
}) => {
  // 현재 과제 정보 (mode에 따라 결정)
  const currentAssignment = mode === 'routine' ? routine : (mode === 'project' ? projectAssignment : assignment);
  
  // 현재 선택된 하위 과제 정보 (프로젝트 모드용)
  const currentSubTask = mode === 'project' && projectSubTasks && projectSubTasks[currentSubTaskIndex] 
    ? projectSubTasks[currentSubTaskIndex].subTask 
    : null;

  // 디버깅: 프로젝트 모드에서 하위 과제 정보 확인
  if (mode === 'project') {
    console.log('🔍 AssignmentCheckModal - 프로젝트 모드 디버깅:', {
      projectSubTasks: projectSubTasks?.map((pt, index) => ({
        index,
        hasSubTask: !!pt.subTask,
        subTask: pt.subTask ? {
          id: pt.subTask.id,
          title: pt.subTask.title,
          points: pt.subTask.points
        } : null
      })),
      currentSubTaskIndex,
      currentSubTask: currentSubTask ? {
        id: currentSubTask.id,
        title: currentSubTask.title,
        points: currentSubTask.points
      } : null
    });
  }
  
  // 타입 안전성을 위한 헬퍼 함수들
  const getBasePoints = () => {
    if (mode === 'routine') {
      return routine?.basePoints || 0;
    } else if (mode === 'project' && currentSubTask) {
      return currentSubTask.points || 0;
    } else {
      return (currentAssignment as Assignment)?.basePoints || 0;
    }
  };
  
  const getAssignedStudentIds = () => {
    if (mode === 'routine') {
      return routine?.assignedStudentIds || [];
    } else if (mode === 'project') {
      return projectAssignment?.assignedStudentIds || [];
    } else {
      return (currentAssignment as Assignment)?.assignedStudentIds || [];
    }
  };
  
  // 기본값으로 사용할 basePoints (헬퍼 함수 사용)
  const defaultBasePoints = getBasePoints();

  // 과제별로 격리된 상태 관리 (빈 배열로 시작)
  const [studentStatuses, setStudentStatuses] = useState<AssignmentStudentStatus[]>([]);
  const [checkInput, setCheckInput] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false); // 🎯 사용자 입력 중인지 추적
  const [isSubmittingViaClick, setIsSubmittingViaClick] = useState(false); // 🎯 클릭 제출 중인지 추적
  const [selectedStatus, setSelectedStatus] = useState<AssignmentStatusValue>('confirmed');
  const [pointsInput, setPointsInput] = useState('0'); // 🎯 초기값을 0으로 설정
  const [isPointsEnabled, setIsPointsEnabled] = useState(false);
  const [isAutoIncrementEnabled, setIsAutoIncrementEnabled] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickStudentId, setLastClickStudentId] = useState<string | null>(null);
  
  // 인라인 포인트 편집 상태
  const [inlineEditingStudentId, setInlineEditingStudentId] = useState<string | null>(null);
  const [inlinePointsValue, setInlinePointsValue] = useState<string>('');

  const [isMemoEnabled, setIsMemoEnabled] = useState(false);
  const [memoInput, setMemoInput] = useState('');
  const [isHoveringMemo, setIsHoveringMemo] = useState(false); // 호버 중인 메모 표시 여부
  
  // 과제 노트 상태
  const [assignmentNotes, setAssignmentNotes] = useState<string>('');


  const [isMemoEditModalOpen, setIsMemoEditModalOpen] = useState(false);
  const [editingMemoStudent, setEditingMemoStudent] = useState<string | null>(null);
      const [editingMemoValue, setEditingMemoValue] = useState('');
    const [editingMemoStatus, setEditingMemoStatus] = useState<AssignmentStatusValue>('pending');
  
  // 제출 확인 데이터 상태 관리
  const [editedSubmissionLink, setEditedSubmissionLink] = useState('');
  const [editedSubmissionMessage, setEditedSubmissionMessage] = useState('');
  const [isSubmissionDeleted, setIsSubmissionDeleted] = useState(false);
  const [isEditingSubmission, setIsEditingSubmission] = useState(false);
    
    // 키보드 도우미 토글 상태
    const [isKeyboardHelperOpen, setIsKeyboardHelperOpen] = useState(false);
    
    // 선택된 멤버들에게 지급할 포인트 상태
  const [selectedPoints, setSelectedPoints] = useState(0); // 🎯 초기값을 0으로 설정
  
  // 단일 선택 시 블록의 원래 포인트 값 (최소값 계산용)
  const [originalBlockPoints, setOriginalBlockPoints] = useState<number>(0);
  
  // 사용자가 의도적으로 포인트를 변경했는지 추적하는 플래그
  const [isUserModifiedPoints, setIsUserModifiedPoints] = useState<boolean>(false);
  
  // 모달을 열었던 처음 상태를 저장 (복원용)
  const [initialStudentStatuses, setInitialStudentStatuses] = useState<AssignmentStudentStatus[]>([]);
  
  // 토스트 메시지 관련 상태
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  
  // 변경사항 추적 시스템 (최종 저장용)
  const [changeTracker, setChangeTracker] = useState<Record<string, Partial<AssignmentStudentStatus>>>({});
  
  // 제출 확인 데이터 변경사항 추적 (캐시)
  const [submissionChangeTracker, setSubmissionChangeTracker] = useState<Record<string, {
    link: string;
    message: string;
    isDeleted: boolean;
  }>>({});
  
  // 저장 완료 메시지 팝업 상태
  const [isSaveSuccessModalOpen, setIsSaveSuccessModalOpen] = useState(false);
  const [isFinalSaveInProgress, setIsFinalSaveInProgress] = useState(false);

  /** 저장 직후 서버에 반영된 보상 지급 목록 (assignment prop은 갱신되지 않을 수 있음) */
  const [generalRewardGrantedOverride, setGeneralRewardGrantedOverride] = useState<string[] | null>(null);

  useEffect(() => {
    if (isOpen && mode === 'general') {
      setGeneralRewardGrantedOverride(null);
    }
  }, [isOpen, mode, assignment?.id]);

  const isGeneralMemberRewardGranted = useCallback(
    (memberId: string) => {
      if (mode !== 'general' || !assignment) return false;
      if (generalRewardGrantedOverride !== null) {
        return generalRewardGrantedOverride.includes(memberId);
      }
      return (
        'rewardGrantedStudentIds' in assignment &&
        (assignment.rewardGrantedStudentIds?.includes(memberId) ?? false)
      );
    },
    [mode, assignment, generalRewardGrantedOverride]
  );
  
  // 학생 상세 모달 상태
  const [isStudentDetailModalOpen, setIsStudentDetailModalOpen] = useState(false);
  const [selectedStudentForDetail, setSelectedStudentForDetail] = useState<string | null>(null);
  const [currentStudentIndex, setCurrentStudentIndex] = useState<number>(0);
  // 학생 상세 모달: <> 네비 시 임시 저장, 확인 시 한꺼번에 반영
  const [studentDetailDraft, setStudentDetailDraft] = useState<Record<string, {
    status: string;
    points: number;
    memo: string;
    submissionLink?: string;
    submissionMessage?: string;
    isDeleted?: boolean;
    isImageDeleted?: boolean;
    submissionImagePath?: string;
    submissionImageUpdatedAt?: string;
    submissionImageSizeBytes?: number;
  }>>({});
  
  // 마지막으로 설정된 포인트를 추적하는 ref (복수 선택 시 사용)
  const lastSetPointsRef = useRef<number>(0);
  
  // 현재 상태값을 추적하는 ref들
  const currentStateRef = useRef<{
    selectedStudentsSize: number;
    selectedPoints: number;
    isUserModifiedPoints: boolean;
  }>({
    selectedStudentsSize: 0,
    selectedPoints: 0,
    isUserModifiedPoints: false
  });

  // 선택된 멤버가 변경될 때 포인트 필드 업데이트
  useEffect(() => {
    // 현재 상태를 ref에 저장
    currentStateRef.current = {
      selectedStudentsSize: selectedStudents.size,
      selectedPoints,
      isUserModifiedPoints
    };

    console.log('🔄 useEffect 실행 - selectedStudents 변경:', {
      selectedStudentsSize: selectedStudents.size,
      isUserModifiedPoints,
      selectedPoints
    });

    // 🎯 대상 필드 업데이트 로직 추가 (사용자 입력 중이 아닐 때만)
    if (!isUserTyping) {
      if (selectedStudents.size === 0) {
        // 선택 없음: 대상 필드 비우기
        setCheckInput('');
      } else if (selectedStudents.size === 1) {
        // 단일 선택: 해당 멤버 정보 표시
        const studentId = Array.from(selectedStudents)[0];
        const student = members.find(m => m.id === studentId);
        if (student) {
          setCheckInput(`${student.number}. ${student.name}`);
        }
      } else if (selectedStudents.size === members.length) {
        // 전체 선택: "전체" 표시
        setCheckInput('전체');
      } else {
        // 다중 선택: "선택" 표시
        setCheckInput('선택');
      }
    }

    if (selectedStudents.size === 1) {
      // 단일 선택: 해당 블록의 실제 포인트 불러오기
      const studentId = Array.from(selectedStudents)[0];
      const status = studentStatuses.find(s => s.studentId === studentId);
      if (status) {
        // 사용자가 포인트를 수정하지 않았다면 원래 값으로 설정
        if (!isUserModifiedPoints) {
          console.log('🔄 단일 선택: status에서 포인트 설정:', {
            studentId,
            earnedPoints: status.earnedPoints,
            isUserModifiedPoints
          });
          setSelectedPoints(status.earnedPoints);
          setOriginalBlockPoints(status.earnedPoints);
        }
        // 메모 입력 필드 업데이트 (사용자 입력/호버 중이 아닐 때만)
        if (!isUserTyping && !isHoveringMemo) {
          setMemoInput(status.memo || '');
        }
      } else {
        // status가 없는 경우는 과제가 할당되지 않은 경우이므로 0으로 설정
        if (!isUserModifiedPoints) {
          console.log('🔄 단일 선택: status 없음, 0으로 설정');
          setSelectedPoints(0);
          setOriginalBlockPoints(0);
        }
        if (!isUserTyping && !isHoveringMemo) {
          setMemoInput('');
        }
      }
    } else if (selectedStudents.size > 1) {
      // 복수 선택: 사용자가 포인트를 수정하지 않았다면 placeholder 상태로 설정
      if (!isUserModifiedPoints) {
        setSelectedPoints(0);
        // isUserModifiedPoints는 리셋하지 않음 (사용자 설정 보존)
      }
    } else {
      // 선택된 멤버가 없는 경우: 포인트 필드 변수값을 0으로 초기화
      console.log('🔄 선택 없음: isUserModifiedPoints 변경 전:', {
        before: isUserModifiedPoints,
        selectedStudentsSize: selectedStudents.size
      });
      setSelectedPoints(0);
      setOriginalBlockPoints(0);
      setIsUserModifiedPoints(false);
      console.log('🔄 선택 없음: isUserModifiedPoints를 false로 설정');
    }
  }, [selectedStudents, getBasePoints, studentStatuses, isUserTyping]); // isUserTyping 의존성 추가
  
    // 포인트 증가 (환경설정 단위로)
  const handleSelectedPointsIncrease = () => {
    // 🎯 루틴 과제는 과제 기본 포인트 단위, 일반 과제는 환경설정 단위
    const increment = mode === 'routine' ? (routine?.basePoints || 1) : (statusDefaultPoints?.confirmed || 1);
    
    // placeholder 상태일 때는 0으로 간주하고 계산
    const currentPoints = selectedPoints;
    const newPoints = currentPoints + increment;

    console.log('➕ 포인트 증가:', {
      currentPoints: selectedPoints,
      actualCurrentPoints: currentPoints,
      increment,
      newPoints,
      statusDefaultPoints,
      selectedStudentsSize: selectedStudents.size,
      isPlaceholder: selectedStudents.size > 1 ? '공통 포인트' : (selectedPoints === 0 ? '포인트' : '숫자')
    });

    // selectedPoints에 반영
    setSelectedPoints(newPoints);
    // 사용자가 포인트를 수정했다고 표시
    setIsUserModifiedPoints(true);
  };

  // 포인트 감소 (환경설정 단위로)
  const handleSelectedPointsDecrease = () => {
    // 🎯 루틴 과제는 과제 기본 포인트 단위, 일반 과제는 환경설정 단위
    const decrement = mode === 'routine' ? (routine?.basePoints || 1) : (statusDefaultPoints?.confirmed || 1);
    
    // placeholder 상태일 때는 0으로 간주하고 계산
    const currentPoints = selectedPoints;
    
    // 단일 선택 시에는 원래 블록 점수 이하로도 내려갈 수 있음
    let newPoints: number;
    if (selectedStudents.size === 1) {
      // 단일 선택: 0 이상이면 감소 가능 (원래 블록 점수 이하로도)
      newPoints = Math.max(0, currentPoints - decrement);
    } else {
      // 복수 선택: 0 이하로 내려가지 않음
      newPoints = Math.max(0, currentPoints - decrement);
    }

    console.log('➖ 포인트 감소:', {
      currentPoints: selectedPoints,
      actualCurrentPoints: currentPoints,
      decrement,
      newPoints,
      statusDefaultPoints,
      selectedStudentsSize: selectedStudents.size,
      isPlaceholder: selectedStudents.size > 1 ? '공통 포인트' : (selectedPoints === 0 ? '포인트' : '숫자'),
      originalBlockPoints: selectedStudents.size === 1 ? originalBlockPoints : 'N/A'
    });

    // selectedPoints에 반영
    setSelectedPoints(newPoints);
    // 사용자가 포인트를 수정했다고 표시
    setIsUserModifiedPoints(true);
  };

  // 메모 입력 필드 이벤트 핸들러 (렌더링 최적화)
  const handleMemoInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMemoInput(e.target.value);
  }, []);



  // 메모 편집 필드 이벤트 핸들러 (렌더링 최적화)
  const handleEditingMemoChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingMemoValue(e.target.value);
  }, []);

  // 배치 메모 입력 이벤트 핸들러 (렌더링 최적화)
  const handleBatchMemoChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMemoInput(e.target.value);
    // 자동 높이 조절
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  }, []);

  const handleKeyboardHelperMemoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMemoInput(e.target.value);
  }, []);

  // 포인트 초기화 함수 (ㅁ 버튼 클릭 시)
  const handlePointsReset = () => {
    console.log('🔄 ㅁ 버튼 클릭 - handlePointsReset 시작:', {
      selectedStudentsSize: selectedStudents.size,
      currentIsUserModifiedPoints: isUserModifiedPoints,
      currentSelectedPoints: selectedPoints
    });

    if (selectedStudents.size === 1) {
      // 단일 선택 시: 0으로 설정
      setSelectedPoints(0);
      // 사용자가 포인트를 수정했다고 표시
      setIsUserModifiedPoints(true);
      console.log('🔄 단일 선택: 포인트 0으로 설정, isUserModifiedPoints = true');
    } else if (selectedStudents.size > 1) {
      // 복수 선택 시: 0으로 설정 (공통 포인트가 아닌 실제 0 값)
      setSelectedPoints(0);
      // 사용자가 포인트를 수정했다고 표시
      setIsUserModifiedPoints(true);
      console.log('🔄 복수 선택: 포인트 0으로 설정, isUserModifiedPoints = true');
    } else {
      // 선택 없음: 0으로 설정
      setSelectedPoints(0);
      console.log('🔄 선택 없음: 포인트 0으로 설정');
    }
  };

  // 포인트 및 메모 적용 함수 (입력 버튼 클릭 시) - 새롭게 작성
  const handleApplyPoints = useCallback(() => {
    console.log('🔍 [입력] 버튼 클릭 - 새로운 로직 시작');

    // 1. 현재 선택된 멤버가 있는지 확인
    if (selectedStudents.size === 0) {
      console.log('❌ 선택된 멤버가 없음');
      return;
    }

    // 2. 포인트 필드의 현재 값 확인
    let currentFieldValue = selectedPoints;
    let shouldApplyPoints = true;
    
    // 복수 선택에서 '공통 포인트' 텍스트가 표시되는 경우 처리
    if (selectedStudents.size > 1 && selectedPoints === 0 && !isUserModifiedPoints) {
      // '공통 포인트' 상태에서는 포인트를 적용하지 않음
      console.log('⚠️ 공통 포인트 상태 - 포인트 미적용, 메모만 적용');
      shouldApplyPoints = false;
      currentFieldValue = 0; // 포인트는 0으로 설정
    }
    
    console.log('🔍 포인트 필드 값:', {
      currentFieldValue,
      type: typeof currentFieldValue,
      isNumber: !isNaN(currentFieldValue) && currentFieldValue >= 0,
      selectedStudentsSize: selectedStudents.size,
      isUserModifiedPoints,
      shouldApplyPoints
    });

    // 3. 포인트를 적용해야 하는 경우에만 숫자 검증
    if (shouldApplyPoints && (isNaN(currentFieldValue) || currentFieldValue < 0)) {
      console.log('❌ 포인트 필드가 숫자가 아님 - 포인트 미적용');
      return;
    }

    // 4. 선택된 멤버들의 포인트와 메모 업데이트
    const updatedStatuses = [...studentStatuses];
    let updatedCount = 0;
    const memoValue = memoInput.trim() || undefined;

    selectedStudents.forEach(studentId => {
      const existingIndex = updatedStatuses.findIndex(s => s.studentId === studentId);
      if (existingIndex >= 0) {
        // 기존 멤버 상태 업데이트
        const existingStatus = updatedStatuses[existingIndex];
        updatedStatuses[existingIndex] = {
          ...existingStatus,
          earnedPoints: shouldApplyPoints ? currentFieldValue : existingStatus.earnedPoints,
          memo: memoValue
        };
        updatedCount++;
        console.log(`🔍 기존 멤버 상태 업데이트: ${studentId}`);
      } else {
        // 서버에 멤버 문서가 없는 경우 - 새로운 멤버 상태 생성
        const newStudentStatus: AssignmentStudentStatus = {
          id: `${studentId}_${currentAssignment?.id || 'temp'}`,
          studentId,
          assignmentId: currentAssignment?.id || 'temp',
          status: 'pending', // 기본값: 미확인
          earnedPoints: shouldApplyPoints ? currentFieldValue : 0,
          memo: memoValue,
          updatedAt: new Date()
        };
        
        updatedStatuses.push(newStudentStatus);
        updatedCount++;
        console.log(`🔍 새로운 멤버 상태 생성: ${studentId}`, newStudentStatus);
      }
    });

    // 5. 상태 업데이트
    setStudentStatuses(updatedStatuses);

    // 6. 변경사항 추적 (각 멤버별로)
    selectedStudents.forEach(studentId => {
      const changes: Partial<AssignmentStudentStatus> = { memo: memoValue };
      if (shouldApplyPoints) {
        changes.earnedPoints = currentFieldValue;
      }
      trackChange(studentId, changes);
    });

    // 7. 메모 입력 필드 초기화
    setMemoInput('');

    // 8. 선택 영역 해제
    setSelectedStudents(new Set());

    console.log('✅ 포인트 및 메모 적용 완료:', {
      appliedPoints: currentFieldValue,
      appliedMemo: memoValue,
      updatedStudentCount: updatedCount,
      selectedStudentsSize: selectedStudents.size
    });
  }, [selectedStudents, selectedPoints, studentStatuses, memoInput, isUserModifiedPoints]);

  // 키보드 도우미 메모 입력 필드 포커스용 ref
  const memoInputRef = useRef<HTMLInputElement | null>(null);
  
  // 변경사항 없음 메시지 팝업 상태
  const [isNoChangesModalOpen, setIsNoChangesModalOpen] = useState(false);
  
  // 메모 삭제 경고 팝업 상태
  const [isMemoDeleteModalOpen, setIsMemoDeleteModalOpen] = useState(false);
  
  // 모달 닫기 확인 팝업 상태
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);
  
  // 메모 보기 모드 상태
  const [isMemoViewMode, setIsMemoViewMode] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [memoViewMemos, setMemoViewMemos] = useState<Record<string, string>>({});
  
  // 커스텀 과제 상태 드롭다운 상태
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isStatusFieldFocused, setIsStatusFieldFocused] = useState(false);
  
  // 화면 크기 감지 상태
  const [isMobile, setIsMobile] = useState(false);
  const [memberViewMode, setMemberViewMode] = useState<'number' | 'seat'>(() => {
    const saved = localStorage.getItem('assignmentCheckMemberViewMode');
    return saved === 'seat' ? 'seat' : 'number';
  });
  const [isSeatRotated, setIsSeatRotated] = useState(false);
  
  // 개별 과제 체크 모달 컨테이너 ref
  const memoEditContainerRef = useRef<HTMLDivElement>(null);
  
  // 현재 선택된 멤버 추적 (이전/다음 버튼용)
  const [currentSelectedStudentId, setCurrentSelectedStudentId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('assignmentCheckMemberViewMode', memberViewMode);
  }, [memberViewMode]);

  // 루틴 과제 전용 상태들 (단순한 초기값 설정)
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    // 🎯 커스텀 날짜가 있으면 해당 날짜로, 없으면 오늘 날짜로
    return customDate || new Date();
  });
  const [routineDates, setRoutineDates] = useState<Date[]>([]);
  const [currentDateIndex, setCurrentDateIndex] = useState<number>(0);

  // 현재 날짜를 ref로도 관리 (클로저 문제 방지)
  const currentDateRef = useRef<Date>(currentDate);
  const currentDateIndexRef = useRef<number>(0);
  const routineDatesRef = useRef<Date[]>([]);
  
  // currentDate가 변경될 때마다 ref 업데이트
  useEffect(() => {
    currentDateRef.current = currentDate;
  }, [currentDate]);
  
  // currentDateIndex가 변경될 때마다 ref 업데이트
  useEffect(() => {
    currentDateIndexRef.current = currentDateIndex;
  }, [currentDateIndex]);
  
  // routineDates가 변경될 때마다 ref 업데이트
  useEffect(() => {
    routineDatesRef.current = routineDates;
  }, [routineDates]);

  // 메모 표시 상태 (범례 교체용)
  const [hoveredMemo, setHoveredMemo] = useState<{
    studentId: string;
    memo: string;
  } | null>(null);



  // 모든 useCallback, useEffect, useMemo Hook들을 먼저 호출
  const updatePendingCount = useCallback(() => {
    if (onPendingCountUpdate) {
      // AssignmentsPage·AssignmentBlock: pendingCount = 미확인 + 제출확인(아직 확인 처리 안 됨)
      const pendingCount = studentStatuses.filter(
        status => status.status === 'pending' || status.status === 'submitted'
      ).length;
      const submittedCount = studentStatuses.filter(status => status.status === 'submitted').length;
      onPendingCountUpdate(pendingCount, submittedCount);
    }
  }, [studentStatuses, onPendingCountUpdate]);
  
  const updatePendingCountAfterSave = useCallback(() => {
    if (onPendingCountUpdate) {
      const pendingCount = studentStatuses.filter(
        status => status.status === 'pending' || status.status === 'submitted'
      ).length;
      const submittedCount = studentStatuses.filter(status => status.status === 'submitted').length;
      onPendingCountUpdate(pendingCount, submittedCount);
    }
  }, [studentStatuses, onPendingCountUpdate]);

  // 🎯 날짜만 비교하는 헬퍼 함수 (시간대 무시)
  const isSameDate = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  // 루틴 과제 날짜 계산 함수 (루틴 요일 + 커스텀 날짜)
  const calculateRoutineDates = useCallback(async () => {
    console.log('🔍 calculateRoutineDates 호출:', { 
      mode, 
      routine: routine ? { 
        id: routine.id,
        title: routine.title,
        startDate: routine.startDate,
        routineDays: routine.routineDays,
        dueDate: routine.dueDate,
        routineDaysType: typeof routine.routineDays,
        startDateType: typeof routine.startDate,
        dueDateType: typeof routine.dueDate
      } : null 
    });
    
    if (mode !== 'routine' || !routine || !userId || !classId) {
      console.log('❌ 루틴 모드가 아니거나 routine이 없음');
      return [];
    }
    
    console.log('📅 루틴 요일:', routine.routineDays);
    console.log('📅 시작일:', routine.startDate);
    console.log('📅 마감일:', routine.dueDate);
    
    const dates: Date[] = [];
    
    // 1. 루틴 요일 날짜 계산 (시작일~오늘 중 루틴 요일만)
    let startDate: Date;
    if (routine.startDate) {
      startDate = new Date(routine.startDate);
      startDate.setHours(0, 0, 0, 0);
    } else {
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
    }
    
    // 종료일: 오늘 또는 selectedDate 중 더 늦은 날짜
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    let endDate = today;
    
    // selectedDate가 있으면 해당 날짜도 포함되도록 endDate 확장
    if (selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      selectedDateObj.setHours(23, 59, 59, 999);
      if (selectedDateObj > endDate) {
        endDate = selectedDateObj;
        console.log('🎯 selectedDate로 종료일 확장:', endDate.toLocaleDateString('ko-KR'));
      }
    }
    
    console.log('📅 시작일:', startDate);
    console.log('📅 종료일:', endDate);
    console.log('📅 selectedDate (props):', selectedDate);
    
    const currentDate = new Date(startDate);
    
    // routineDays를 숫자 배열로 변환 (문자열 배열일 수 있음)
    const routineDaysNumbers = routine.routineDays.map((day: number | string) => 
      typeof day === 'string' ? parseInt(day, 10) : day
    );
    console.log('📅 routineDays (원본):', routine.routineDays);
    console.log('📅 routineDays (숫자 변환):', routineDaysNumbers);
    console.log('📅 시작일 vs 종료일:', { 
      startDate: startDate.toISOString(), 
      endDate: endDate.toISOString(),
      startTime: startDate.getTime(),
      endTime: endDate.getTime(),
      isStartBeforeEnd: startDate.getTime() <= endDate.getTime()
    });
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      console.log(`🔍 날짜 확인: ${currentDate.toLocaleDateString('ko-KR')} (dayOfWeek: ${dayOfWeek}, 요일: ${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      
      // 숫자로 변환된 배열에서 확인
      if (routineDaysNumbers.includes(dayOfWeek)) {
        dates.push(new Date(currentDate));
        console.log(`✅ 루틴 날짜 추가: ${currentDate.toLocaleDateString('ko-KR')} (${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
        
        // 🎯 일요일과 월요일 날짜 처리 추적
        if (dayOfWeek === 0) {
          console.log(`🔍 일요일 날짜 처리: ${currentDate.toLocaleDateString('ko-KR')} (dayOfWeek: ${dayOfWeek})`);
        }
        if (dayOfWeek === 1) {
          console.log(`🔍 월요일 날짜 처리: ${currentDate.toLocaleDateString('ko-KR')} (dayOfWeek: ${dayOfWeek})`);
        }
      } else {
        console.log(`❌ 루틴 요일에 포함되지 않음: ${currentDate.toLocaleDateString('ko-KR')} (${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log('📅 계산된 루틴 날짜들:', dates);
    
    // 2. 서버에 데이터가 있는 커스텀 날짜들 추가
    try {
      console.log('🔍 커스텀 날짜 찾기 시작');
      const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);
      const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
      
      // RoutineAssignmentDateStatus 형식으로 변환
      const routineData = {
        routine: result.routine,
        dateStatuses: result.dateStatuses.map(dateAssignment => {
          const students: Record<string, RoutineAssignmentStudentStatus> = {};
          let totalStudents = 0;
          let completedStudents = 0;
          let pendingStudents = 0;
          
          const studentStatuses = dateAssignment.studentStatuses || [];
          studentStatuses.forEach((studentStatus: any) => {
            students[studentStatus.studentId] = {
              id: studentStatus.studentId,
              studentId: studentStatus.studentId,
              assignmentId: routine.id,
              status: studentStatus.status,
              earnedPoints: studentStatus.points,
              memo: studentStatus.memo,
              updatedAt: studentStatus.updatedAt
            };
            
            totalStudents++;
            if (studentStatus.status === 'confirmed') {
              completedStudents++;
            } else if (studentStatus.status === 'pending') {
              pendingStudents++;
            }
          });
          
          return {
            dateId: dateAssignment.date,
            routineAssignmentId: routine.id,
            date: new Date(dateAssignment.date),
            isCustomDate: dateAssignment.isCustomDate,
            students,
            totalStudents,
            completedStudents,
            pendingStudents,
            updatedAt: new Date()
          };
        })
      };
      
      // 서버에 데이터가 있는 날짜들 중 루틴 날짜에 포함되지 않는 것들 찾기
      const customDates = routineData.dateStatuses
        .filter(ds => {
          const dateId = ds.dateId;
          const customDate = new Date(dateId);
          
          // 🎯 시간대 무시하고 날짜만 비교하여 중복 확인
          const isNotRoutineDate = !dates.some(routineDate => 
            isSameDate(routineDate, customDate)
          );
          
          console.log(`🔍 커스텀 날짜 확인: ${dateId}`, { 
            customDate: customDate.toLocaleDateString('ko-KR'),
            routineDatesCount: dates.length,
            isNotRoutineDate,
            routineDateMatches: dates.filter(routineDate => 
              isSameDate(routineDate, customDate)
            ).map(d => d.toLocaleDateString('ko-KR'))
          });
          
          return isNotRoutineDate;
        })
        .map(ds => new Date(ds.dateId));
      
      console.log('🔍 찾은 커스텀 날짜들:', customDates);
      
      // 커스텀 날짜들을 dates 배열에 추가
      dates.push(...customDates);
      
    } catch (error) {
      console.error('🔍 커스텀 날짜 로드 실패:', error);
    }
    
    // 날짜 순서대로 정렬
    dates.sort((a, b) => a.getTime() - b.getTime());
    
    // 🎯 최종 날짜 배열 상세 로깅
    console.log('📅 최종 날짜들 (루틴 + 커스텀):', dates.map(d => ({
      date: d.toLocaleDateString('ko-KR'),
      dayOfWeek: d.getDay(),
      dayName: ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
    })));
    
    return dates;
  }, [mode, routine, userId, classId]);

  // 적절한 시작 날짜를 찾는 함수
  const findBestStartDate = useCallback((dates: Date[]) => {
    if (dates.length === 0) return 0;
    
    // 🎯 selectedDate가 있으면 해당 날짜를 우선적으로 찾기
    if (selectedDate) {
      const targetDate = new Date(selectedDate);
      targetDate.setHours(0, 0, 0, 0);
      
      const targetIndex = dates.findIndex(date => 
        isSameDate(date, targetDate)
      );
      
      if (targetIndex >= 0) {
        console.log('📅 선택된 날짜 선택:', dates[targetIndex].toLocaleDateString('ko-KR'));
        return targetIndex;
      }
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // 오늘 날짜가 루틴 요일에 해당하는지 확인
    const todayIndex = dates.findIndex(date => 
      isSameDate(date, today)
    );
    
    if (todayIndex >= 0) {
      console.log('📅 오늘 날짜 선택:', dates[todayIndex].toLocaleDateString('ko-KR'));
      return todayIndex;
    }
    
    // 오늘 날짜가 없으면 가장 가까운 미래 날짜 찾기
    const futureIndex = dates.findIndex(date => date > today);
    if (futureIndex >= 0) {
      console.log('📅 가장 가까운 미래 날짜 선택:', dates[futureIndex]);
      return futureIndex;
    }
    
    // 모든 날짜가 과거라면 마지막 날짜 선택
    console.log('📅 마지막 날짜 선택:', dates[dates.length - 1]);
    return dates.length - 1;
  }, [selectedDate]);
  // 해당 날짜의 데이터를 로드하는 함수
  const loadDateData = useCallback(async (targetDate: Date) => {
    if (mode !== 'routine' || !routine || !members || !userId || !classId) {
      console.log('📅 데이터 로드 조건 미충족:', { mode, routine: !!routine, members: !!members, userId, classId });
      return;
    }
    
    // 추가 날짜(커스텀): 초기화 시 targetDate가 루틴 슬롯의 마지막 날로 잘못 잡힐 수 있음 → 로드 기준일은 customDate 우선
    const effectiveLoadDate = customDate ? new Date(customDate) : new Date(targetDate);
    effectiveLoadDate.setHours(0, 0, 0, 0);
    if (customDate) {
      console.log('🎯 커스텀/추가 날짜 감지 — 로드 기준일:', effectiveLoadDate.toDateString());
      setCurrentDate(effectiveLoadDate);
      currentDateRef.current = effectiveLoadDate;
    }

    try {
      const dateId =
        effectiveLoadDate.getFullYear() +
        '-' +
        String(effectiveLoadDate.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(effectiveLoadDate.getDate()).padStart(2, '0');
      console.log('📅 날짜 데이터 로드 시작:', dateId);
      console.log('📅 원본 targetDate:', targetDate);
      console.log('📅 실제 로드 날짜:', effectiveLoadDate);
      console.log('📅 생성된 dateId:', dateId);
      
      let routineData: any = null;
      
      try {
        console.log('📅 서버 데이터 로드 시도:', { userId, classId, routineId: routine.id, dateId });
        
        // 통합 문서 방식으로 루틴 과제 데이터 로드
        const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);
        const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
        
        // RoutineAssignmentDateStatus 형식으로 변환
        routineData = {
          routine: result.routine,
          dateStatuses: result.dateStatuses.map(dateAssignment => {
            const students: Record<string, RoutineAssignmentStudentStatus> = {};
            let totalStudents = 0;
            let completedStudents = 0;
            let pendingStudents = 0;
            
            const studentStatuses = dateAssignment.studentStatuses || [];
            studentStatuses.forEach((studentStatus: any) => {
              students[studentStatus.studentId] = {
                id: studentStatus.studentId,
                studentId: studentStatus.studentId,
                assignmentId: routine.id,
                status: studentStatus.status,
                earnedPoints: studentStatus.points,
                memo: studentStatus.memo,
                updatedAt: studentStatus.updatedAt
              };
              
              totalStudents++;
              if (studentStatus.status === 'confirmed') {
                completedStudents++;
              } else if (studentStatus.status === 'pending') {
                pendingStudents++;
              }
            });
            
            return {
              dateId: dateAssignment.date,
              routineAssignmentId: routine.id,
              date: new Date(dateAssignment.date),
              isCustomDate: dateAssignment.isCustomDate,
              students,
              totalStudents,
              completedStudents,
              pendingStudents,
              updatedAt: new Date()
            };
          })
        };
        
        console.log('📅 로드된 루틴 과제 데이터:', routineData);
        console.log('📅 dateStatuses 길이:', routineData.dateStatuses.length);
        console.log('📅 dateStatuses 내용:', routineData.dateStatuses);
        
        // 해당 날짜의 데이터 찾기 (정확한 매칭)
        console.log('📅 찾고 있는 dateId:', dateId);
        console.log('📅 서버에서 로드된 dateStatuses:', routineData.dateStatuses.map((ds: any) => ({ dateId: ds.dateId, studentCount: Object.keys(ds.students).length })));
        
        // 정확한 dateId 매칭
        let targetDateStatus = routineData.dateStatuses.find(
          (dateStatus: any) => dateStatus.dateId === dateId
        );
        
        console.log('📅 찾은 targetDateStatus:', targetDateStatus);
        
        if (targetDateStatus && targetDateStatus.students) {
          // 해당 날짜에 저장된 데이터가 있음
          let studentStatusesArray = Object.values(targetDateStatus.students) as AssignmentStudentStatus[];
          // 서버에는 "상태를 저장한 멤버"만 있을 수 있음 → 부여 대상 멤버 전원이 studentStatuses에 있도록 보강
          // (없으면 1~4 단축키·일괄 적용이 prev.map만 돌아 UI에 반영되지 않음)
          const assignedIds = routine.assignedStudentIds || [];
          const loadedIds = new Set(studentStatusesArray.map(s => s.studentId));
          for (const studentId of assignedIds) {
            if (!loadedIds.has(studentId)) {
              studentStatusesArray.push({
                id: `${studentId}_${routine.id}_${dateId}`,
                studentId,
                assignmentId: routine.id,
                status: 'pending' as AssignmentStatusValue,
                earnedPoints: 0,
                updatedAt: new Date(),
                memo: undefined,
                notes: undefined
              });
              loadedIds.add(studentId);
            }
          }
          // assignmentMemo 컬렉션에서 메모 병합 (통합 문서에는 memo 미포함)
          try {
            const memoMap = await assignmentMemoService.getMemoMapForAssignment(
              userId,
              classId,
              `${routine.id}-${dateId}`,
              memberGroup
            );
            if (Object.keys(memoMap).length > 0) {
              studentStatusesArray = studentStatusesArray.map(s => ({
                ...s,
                memo: memoMap[s.studentId]?.memo ?? s.memo ?? ''
              }));
            }
          } catch (e) {
            console.warn('📅 메모 로드 실패 (무시):', e);
          }
          console.log('📅 해당 날짜 저장된 데이터:', studentStatusesArray);
          console.log('📅 멤버 상태 개수:', studentStatusesArray.length);
          setStudentStatuses(studentStatusesArray);
          // 초기 비교 기준도 동일하게 설정 (닫을 때 변경 없음으로 판단되도록)
          setInitialStudentStatuses([...studentStatusesArray] as AssignmentStudentStatus[]);
          setChangeTracker({});
          setSubmissionChangeTracker({});
          
          // 메모 보기 모드의 메모 값 초기화: 해당 날짜의 studentStatuses에서 메모를 로드
          const memos: Record<string, string> = {};
          studentStatusesArray.forEach((status: any) => {
            memos[status.studentId] = status.memo || '';
          });
          setMemoViewMemos(memos);
          
          // 루틴 과제 노트 로드
          if (routineData.notes) {
            setAssignmentNotes(routineData.notes);
            console.log('📝 루틴 과제 노트 로드됨:', routineData.notes);
          }
          
          return;
        } else {
          console.log('📅 해당 날짜에 저장된 데이터가 없음 (초기값 생성 필요):', { 
            targetDateId: dateId,
            targetDateStatus: !!targetDateStatus, 
            hasStudents: targetDateStatus?.students ? Object.keys(targetDateStatus.students).length : 0 
          });
        }
      } catch (error) {
        console.error('📅 서버 데이터 로드 실패:', error);
        console.log('📅 서버 데이터 로드 실패, 초기값으로 진행');
        // 서버 데이터 로드 실패 시 초기값으로 진행
      }
      
      // 해당 날짜에 저장된 데이터가 없으면 초기값 생성
      // 각 날짜마다 독립적인 초기값을 생성 (다른 날짜의 데이터를 복사하지 않음)
      console.log('📅 새로운 날짜 초기값 생성:', dateId);

      let baseStatuses = members.map((member) => ({
        id: `${member.id}_${routine.id}_${dateId}`,
        studentId: member.id,
        assignmentId: routine.id,
        status: 'pending' as AssignmentStatusValue,
        earnedPoints: 0, // 🎯 미확인 상태는 0포인트로 설정 (routine.basePoints 사용하지 않음)
        updatedAt: new Date(),
        memo: undefined as string | undefined,
        notes: undefined
      }));
      // assignmentMemo에 저장된 메모가 있으면 병합
      try {
        const memoMap = await assignmentMemoService.getMemoMapForAssignment(
          userId,
          classId,
          `${routine.id}-${dateId}`,
          memberGroup
        );
        if (Object.keys(memoMap).length > 0) {
          baseStatuses = baseStatuses.map(s => ({
            ...s,
            memo: memoMap[s.studentId]?.memo ?? s.memo ?? ''
          }));
        }
      } catch (e) {
        console.warn('📅 메모 로드 실패 (무시):', e);
      }
      
      // 새로운 날짜의 데이터로 설정하고 변경사항 추적기 초기화
      setStudentStatuses(baseStatuses as AssignmentStudentStatus[]);
      setInitialStudentStatuses([...baseStatuses] as AssignmentStudentStatus[]);
      setChangeTracker({});
      
      // 메모 보기 모드의 메모 값 초기화: 새로운 날짜이므로 빈 메모로 초기화
      const memos: Record<string, string> = {};
      baseStatuses.forEach((status: any) => {
        memos[status.studentId] = status.memo || '';
      });
      setMemoViewMemos(memos);
      
      console.log('📅 초기값 생성 완료:', baseStatuses.length, '개');
      console.log('📅 생성된 멤버 상태:', baseStatuses);
      console.log('📅 원본 상태 설정 완료 - 변경사항 추적기 초기화됨');
      console.log('📅 현재 날짜:', dateId);
      console.log('📅 멤버별 초기 상태:', baseStatuses.map(s => ({ 
        studentId: s.studentId, 
        status: s.status, 
        earnedPoints: s.earnedPoints,
        memo: s.memo 
      })));
      
      // 날짜별 데이터 로드 완료 표시
      console.log('✅ 날짜별 데이터 로드 완료:', {
        dateId,
        studentCount: baseStatuses.length,
        hasData: baseStatuses.some(s => s.status !== 'pending' || s.memo || s.earnedPoints > 0),
        sampleData: baseStatuses.slice(0, 2).map(s => ({
          studentId: s.studentId,
          status: s.status,
          earnedPoints: s.earnedPoints,
          hasMemo: !!s.memo
        }))
      });
      
    } catch (error) {
      console.error('📅 날짜 데이터 로드 실패:', error);
      // 에러 발생 시 초기값으로 설정
      const fallbackStatuses = members.map((member) => ({
        id: `${member.id}_${routine.id}_fallback`,
        studentId: member.id,
        assignmentId: routine.id,
        status: 'pending' as AssignmentStatusValue,
        earnedPoints: routine.basePoints || 0,
        updatedAt: new Date(),
        memo: undefined
      }));
      setStudentStatuses([...fallbackStatuses]);
    }
  }, [mode, routine, members, userId, classId, memberGroup, customDate]);

  // 루틴 과제 날짜 네비게이션 함수들
  const goToNextDate = useCallback(async () => {
    if (currentDateIndex < routineDates.length - 1) {
      // 현재 날짜의 변경사항이 있으면 저장
      if (Object.keys(changeTracker).length > 0) {
        const hasChanges = studentStatuses.some(status => {
          const originalStatus = initialStudentStatuses.find((s: AssignmentStudentStatus) => s.studentId === status.studentId);
          if (!originalStatus) return true;
          return (
            status.status !== originalStatus.status ||
            status.earnedPoints !== originalStatus.earnedPoints ||
            status.memo !== originalStatus.memo ||
            status.notes !== originalStatus.notes
          );
        });
        
        if (hasChanges) {
          const shouldSave = window.confirm('현재 날짜의 변경사항이 있습니다. 저장하시겠습니까?');
          if (shouldSave) {
            // handleFinalSave 함수가 아직 정의되지 않았으므로 직접 저장 로직 실행
            try {
              if (onSave) {
                const statusesToSave = studentStatuses.filter(status => {
                  const originalStatus = initialStudentStatuses.find((s: AssignmentStudentStatus) => s.studentId === status.studentId);
                  if (!originalStatus) return true;
                  return (
                    status.status !== originalStatus.status ||
                    status.earnedPoints !== originalStatus.earnedPoints ||
                    status.memo !== originalStatus.memo ||
                    status.notes !== originalStatus.notes
                  );
                });
                
                if (statusesToSave.length > 0) {
                  await onSave(statusesToSave, currentDate);
                  setChangeTracker({});
                }
              }
            } catch (error) {
              console.error('날짜 이동 전 저장 실패:', error);
            }
          }
        }
      }
      
      const nextIndex = currentDateIndex + 1;
      const nextDate = routineDates[nextIndex];
      setCurrentDateIndex(nextIndex);
      setCurrentDate(nextDate);
      
      // ref도 업데이트
      currentDateIndexRef.current = nextIndex;
      currentDateRef.current = nextDate;
      
      // 해당 날짜의 데이터 로드
      await loadDateData(nextDate);
    }
  }, [currentDateIndex, routineDates, loadDateData, changeTracker, studentStatuses, initialStudentStatuses, onSave, currentDate]);

  const goToPrevDate = useCallback(async () => {
    if (currentDateIndex > 0) {
      // 현재 날짜의 변경사항이 있으면 저장
      if (Object.keys(changeTracker).length > 0) {
        const hasChanges = studentStatuses.some(status => {
          const originalStatus = initialStudentStatuses.find((s: AssignmentStudentStatus) => s.studentId === status.studentId);
          if (!originalStatus) return true;
          return (
            status.status !== originalStatus.status ||
            status.earnedPoints !== originalStatus.earnedPoints ||
            status.memo !== originalStatus.memo ||
            status.notes !== originalStatus.notes
          );
        });
        
        if (hasChanges) {
          const shouldSave = window.confirm('현재 날짜의 변경사항이 있습니다. 저장하시겠습니까?');
          if (shouldSave) {
            // handleFinalSave 함수가 아직 정의되지 않았으므로 직접 저장 로직 실행
            try {
              if (onSave) {
                const statusesToSave = studentStatuses.filter(status => {
                  const originalStatus = initialStudentStatuses.find((s: AssignmentStudentStatus) => s.studentId === status.studentId);
                  if (!originalStatus) return true;
                  return (
                    status.status !== originalStatus.status ||
                    status.earnedPoints !== originalStatus.earnedPoints ||
                    status.memo !== originalStatus.memo ||
                    status.notes !== originalStatus.notes
                  );
                });
                
                if (statusesToSave.length > 0) {
                  await onSave(statusesToSave, currentDate);
                  setChangeTracker({});
                }
              }
            } catch (error) {
              console.error('날짜 이동 전 저장 실패:', error);
            }
          }
        }
      }
      
      const prevIndex = currentDateIndex - 1;
      const prevDate = routineDates[prevIndex];
      setCurrentDateIndex(prevIndex);
      setCurrentDate(prevDate);
      
      // ref도 업데이트
      currentDateIndexRef.current = prevIndex;
      currentDateRef.current = prevDate;
      
      // 해당 날짜의 데이터 로드
      await loadDateData(prevDate);
    }
  }, [currentDateIndex, loadDateData, changeTracker, studentStatuses, initialStudentStatuses, onSave, currentDate]);

  // 루틴 요일 텍스트 생성 (일,월,화,수,목,금,토 순으로 정렬)
  const getRoutineDaysText = useCallback(() => {
    if (mode !== 'routine' || !routine) return '';
    
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    // 요일 순서대로 정렬 (0=일, 1=월, ..., 6=토)
    const sortedDays = [...routine.routineDays].sort((a: number, b: number) => a - b);
    return sortedDays
      .map((day: number) => dayNames[day])
      .join(', ');
  }, [mode, routine]);

  // 현재 날짜가 루틴 요일에 해당하는지 확인
  const isCurrentDateRoutineDay = useCallback(() => {
    if (mode !== 'routine' || !routine) return false;
    
    const today = new Date();
    const dayOfWeek = today.getDay();
    return routine.routineDays.includes(dayOfWeek);
  }, [mode, routine]);

  // 화면 크기 감지
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 420);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 모달이 열릴 때만 초기화 (useState로 관리하여 리렌더링 방지)
  const [isInitialized, setIsInitialized] = useState(false);
  
  // 루틴 모드 데이터 로딩 상태
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      // 과제별로 고유한 상태 초기화 (과제별 격리)
      const currentAssignmentId = mode === 'routine' ? routine?.id : assignment?.id;
      
      // existingStatuses와 members를 안전하게 사용
      if (existingStatuses && existingStatuses.length > 0) {
        setStudentStatuses([...existingStatuses]);
        setInitialStudentStatuses([...existingStatuses]);
      } else if (members && members.length > 0) {
        const newInitialStatuses = members.map((member, index) => ({
          id: `${member.id}_${currentAssignmentId || 'temp'}`,
          studentId: member.id,
          assignmentId: currentAssignmentId || 'temp',
          status: 'pending' as AssignmentStatusValue,
          earnedPoints: 0, // 🎯 미확인 상태는 0포인트로 설정
          updatedAt: new Date('2024-01-01'),
          memo: undefined
        }));
        
        setStudentStatuses([...newInitialStatuses]);
        setInitialStudentStatuses([...newInitialStatuses]);
      }
      
      setChangeTracker({});
      
      // 메모 보기 모드의 메모 값 초기화: 해당 과제의 studentStatuses에서 메모를 로드
      const memos: Record<string, string> = {};
      if (existingStatuses && existingStatuses.length > 0) {
        existingStatuses.forEach(status => {
          memos[status.studentId] = status.memo || '';
        });
      } else if (members && members.length > 0) {
        members.forEach(member => {
          memos[member.id] = '';
        });
      }
      setMemoViewMemos(memos);
      
      // body overflow 설정
      document.body.style.overflow = 'hidden';
      
      // 루틴 과제 전용: 날짜 초기화 (한 번만)
      if (mode === 'routine' && routine) {
        console.log('🚀 루틴 과제 날짜 초기화 시작');
        
        // 로딩 상태 시작
        setIsLoadingData(true);
        
        // async 함수를 즉시 실행하여 날짜 계산
        (async () => {
          try {
            let dates = await calculateRoutineDates();
            // 추가 날짜는 아직 서버/슬롯 목록에 없을 수 있음 → 목록에 넣어 인덱스·저장이 어긋나지 않게 함
            const ensureDateInList = (d: Date | undefined) => {
              if (!d) return;
              const x = new Date(d);
              x.setHours(0, 0, 0, 0);
              if (!dates.some((rd) => isSameDate(rd, x))) {
                dates = [...dates, x];
              }
            };
            ensureDateInList(selectedDate);
            ensureDateInList(customDate);
            dates.sort((a, b) => a.getTime() - b.getTime());

            if (dates.length === 0) {
              const fallback = customDate ?? selectedDate;
              const d = fallback ? new Date(fallback) : new Date();
              d.setHours(0, 0, 0, 0);
              dates = [d];
            }

            console.log('📅 계산된 날짜들 (선택/추가 병합 후):', dates);
            setRoutineDates(dates);
            routineDatesRef.current = dates;

            const anchor = customDate ?? selectedDate;
            let targetDateIndex: number;
            let targetDate: Date;

            if (anchor) {
              const anchorD = new Date(anchor);
              anchorD.setHours(0, 0, 0, 0);
              const idx = dates.findIndex((d) => isSameDate(d, anchorD));
              if (idx !== -1) {
                targetDateIndex = idx;
                targetDate = dates[idx];
                console.log('✅ 앵커 날짜(추가/선택) 찾음:', { index: targetDateIndex, date: targetDate });
              } else {
                targetDateIndex = findBestStartDate(dates);
                targetDate = dates[targetDateIndex];
                console.log('⚠️ 앵커 병합 후에도 없음 — 기본 인덱스', { targetDateIndex });
              }
            } else {
              console.log('ℹ️ 앵커 없음. 기본 날짜 사용');
              targetDateIndex = findBestStartDate(dates);
              targetDate = dates[targetDateIndex];
            }

            setCurrentDateIndex(targetDateIndex);
            setCurrentDate(targetDate);
            currentDateIndexRef.current = targetDateIndex;
            currentDateRef.current = targetDate;
            console.log('📅 시작 날짜 선택:', targetDate);

            await loadDateData(targetDate);
          } catch (error) {
            console.error('📅 날짜 초기화 실패:', error);
          } finally {
            setIsLoadingData(false);
          }
        })();
      }
      
      setIsInitialized(true);
    } else if (!isOpen) {
      document.body.style.overflow = 'auto';
      setIsInitialized(false); // 모달이 닫힐 때 초기화 플래그 리셋
    }
    
    return () => { 
      document.body.style.overflow = 'auto'; 
    };
  }, [isOpen, mode, routine?.id, assignment?.id]); // ID만 비교하여 불필요한 실행 방지

  // existingStatuses 변경 감지 (프로젝트 하위 과제 네비게이션용)
  useEffect(() => {
    if (isOpen && existingStatuses && existingStatuses.length > 0) {
      setStudentStatuses([...existingStatuses]);
      setInitialStudentStatuses([...existingStatuses]);
      setChangeTracker({});
      
      // 메모 보기 모드의 메모 값 초기화: 해당 과제의 studentStatuses에서 메모를 로드
      const memos: Record<string, string> = {};
      existingStatuses.forEach(status => {
        memos[status.studentId] = status.memo || '';
      });
      setMemoViewMemos(memos);
    }
  }, [isOpen, existingStatuses]);

  // 과제 노트 로딩을 위한 useEffect
  useEffect(() => {
    if (!isOpen) return;
    
    console.log('📝 과제 노트 로딩 시작:', { mode, assignment: !!assignment, routine: !!routine });
    
    if (mode === 'general' && assignment) {
      console.log('📝 일반 모드 - 과제 노트 확인:', {
        hasNotes: !!assignment.notes,
        notesValue: assignment.notes,
        notesType: typeof assignment.notes
      });
      
      if (assignment.notes) {
        setAssignmentNotes(assignment.notes);
        console.log('📝 일반 모드 - 과제 노트 로드됨:', assignment.notes);
      } else {
        console.log('📝 일반 모드 - 과제 노트 없음');
        setAssignmentNotes('');
      }
    } else if (mode === 'routine' && routine) {
      console.log('📝 루틴 모드 - 루틴 과제 노트 확인:', {
        hasNotes: !!routine.notes,
        notesValue: routine.notes,
        notesType: typeof routine.notes
      });
      
      if (routine.notes) {
        setAssignmentNotes(routine.notes);
        console.log('📝 루틴 모드 - 루틴 과제 노트 로드됨:', routine.notes);
      } else {
        console.log('📝 루틴 모드 - 루틴 과제 노트 없음');
        setAssignmentNotes('');
      }
    }
  }, [isOpen, mode, assignment?.notes, routine?.notes]);

  // 모달이 열릴 때 멤버 선택 초기화
  useEffect(() => {
    if (isOpen) {
      setSelectedStudents(new Set());
      console.log('🔄 모달 열림 - 멤버 선택 초기화');
    }
  }, [isOpen]);

  // 과제 상태 옵션 (제출확인은 멤버가 제출할 때만 자동 설정되므로 버튼에서 제외)
const statusOptions: { value: AssignmentStatusValue; label: string; color: string; bgColor: string }[] = [
  { value: 'pending', label: '미확인', color: '#fff', bgColor: 'var(--status-pending)' },
  { value: 'confirmed', label: '확인', color: '#fff', bgColor: 'var(--status-confirmed)' },
  { value: 'delayed', label: '지연확인', color: '#fff', bgColor: 'var(--status-delayed)' },
  { value: 'exempted', label: '예외', color: '#1f1703', bgColor: 'var(--status-exempted)' }
];

  // 멤버가 과제를 부여받았는지 확인 (일반/프로젝트/루틴 모두 assignedStudentIds 기준으로 통일)
  const isStudentAssigned = useCallback((studentId: string): boolean => {
    return getAssignedStudentIds().includes(studentId);
  }, [mode, routine, projectAssignment, currentAssignment]);

  // 멤버별 상태를 5명씩 열로 그룹화 (members와 currentAssignment만으로 계산)
  const groupedMembers = useMemo(() => {
    // currentAssignment가 없어도 루틴 모드에서는 계속 진행
    if (!currentAssignment && mode !== 'routine') return [];
    
    const perCol = 5;
    const columns: { member: ClassMember; isAssigned: boolean }[][] = [];
    
    members.forEach((member, idx) => {
      const colIdx = Math.floor(idx / perCol);
      if (!columns[colIdx]) columns[colIdx] = [];
      
      // isAssigned 계산을 헬퍼 함수 사용
      const isAssigned = getAssignedStudentIds().includes(member.id);
      
      columns[colIdx].push({ member, isAssigned });
    });
    
    return columns;
  }, [members, currentAssignment, mode, routine]);

  const seatViewData = useMemo(() => {
    const monthKeys = new Set<string>();
    members.forEach((member) => {
      if (member.seatPositions && typeof member.seatPositions === 'object') {
        Object.keys(member.seatPositions).forEach((month) => monthKeys.add(month));
      }
    });

    const sortedMonths = Array.from(monthKeys).sort();
    const selectedMonth =
      sortedMonths[sortedMonths.length - 1] ||
      `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

    const assigned: Array<{ member: ClassMember; seatRow: number; seatCol: number; isAssigned: boolean }> = [];
    const unassigned: Array<{ member: ClassMember; isAssigned: boolean }> = [];

    members.forEach((member) => {
      const isAssigned = getAssignedStudentIds().includes(member.id);
      const seatPos = member.seatPositions?.[selectedMonth];
      if (
        seatPos &&
        typeof seatPos.row === 'number' &&
        typeof seatPos.col === 'number'
      ) {
        assigned.push({ member, seatRow: seatPos.row, seatCol: seatPos.col, isAssigned });
      } else {
        unassigned.push({ member, isAssigned });
      }
    });

    const GRID_TRACKS = 16;
    const MAX_INDEX = 14;
    const rotatedAssigned = assigned.map((item) => ({
      ...item,
      seatRow: isSeatRotated ? MAX_INDEX - item.seatRow : item.seatRow,
      seatCol: isSeatRotated ? MAX_INDEX - item.seatCol : item.seatCol
    }));
    const usedRows = new Set<number>();
    const usedCols = new Set<number>();
    rotatedAssigned.forEach(({ seatRow, seatCol }) => {
      if (seatRow >= 0 && seatRow < GRID_TRACKS) usedRows.add(seatRow);
      [seatCol, seatCol + 1].forEach((col) => {
        if (col >= 0 && col < GRID_TRACKS) usedCols.add(col);
      });
    });

    const trimStartRow = usedRows.size > 0 ? Math.max(0, Math.min(...Array.from(usedRows))) : 0;
    const trimEndRow = usedRows.size > 0 ? Math.min(MAX_INDEX + 1, Math.max(...Array.from(usedRows))) : 1;
    const trimStartCol = usedCols.size > 0 ? Math.max(0, Math.min(...Array.from(usedCols))) : 0;
    const trimEndCol = usedCols.size > 0 ? Math.min(MAX_INDEX + 1, Math.max(...Array.from(usedCols))) : 1;

    const rowCount = Math.max(1, trimEndRow - trimStartRow + 1);
    const colCount = Math.max(1, trimEndCol - trimStartCol + 1);

    const rowTemplate = Array.from(
      { length: rowCount },
      (_, idx) => (usedRows.has(trimStartRow + idx) ? '40px' : '10px')
    ).join(' ');
    const colTemplate = Array.from(
      { length: colCount },
      (_, idx) => (usedCols.has(trimStartCol + idx) ? '1fr' : '0.35fr')
    ).join(' ');

    const positionedAssigned = rotatedAssigned.map((item) => ({
      ...item,
      displayRow: item.seatRow - trimStartRow,
      displayCol: item.seatCol - trimStartCol
    }));

    return {
      assigned: positionedAssigned,
      unassigned,
      rowTemplate,
      colTemplate
    };
  }, [members, mode, routine, projectAssignment, currentAssignment, isSeatRotated]);

  const seatUnassignedGroupedMembers = useMemo(() => {
    const perCol = 5;
    const columns: { member: ClassMember; isAssigned: boolean }[][] = [];
    seatViewData.unassigned.forEach(({ member, isAssigned }, idx) => {
      const colIdx = Math.floor(idx / perCol);
      if (!columns[colIdx]) columns[colIdx] = [];
      columns[colIdx].push({ member, isAssigned });
    });
    return columns;
  }, [seatViewData.unassigned]);




  
  // 이전 멤버으로 이동 (과제 체크 모달용)
  const handlePrevStudentInCheckModal = () => {
    // 2명 이상 선택된 경우 이동 불가
    if (selectedStudents.size > 1) return;
    
    let targetStudentId: string | null;
    
    if (selectedStudents.size === 1) {
      // 1명 선택된 경우: 선택된 멤버 기준으로 이동
      targetStudentId = Array.from(selectedStudents)[0];
    } else {
      // 0명 선택된 경우: 현재 선택된 멤버 ID 기준으로 이동
      targetStudentId = currentSelectedStudentId;
    }
    
    if (!targetStudentId) return;
    
    const currentIndex = members.findIndex(m => m.id === targetStudentId);
    if (currentIndex > 0) {
      const prevStudent = members[currentIndex - 1];
      
      // 선택 표시가 있는 경우에만 선택 표시 업데이트
      if (selectedStudents.size === 1) {
        setSelectedStudents(new Set([prevStudent.id]));
      }
      
      // 현재 선택된 멤버 ID 업데이트
      setCurrentSelectedStudentId(prevStudent.id);
      
      // 이전 멤버의 데이터를 필드에 로드
      loadStudentDataInCheckModal(prevStudent.id);
    }
  };
  
  // 다음 멤버으로 이동 (과제 체크 모달용)
  const handleNextStudentInCheckModal = () => {
    // 2명 이상 선택된 경우 이동 불가
    if (selectedStudents.size > 1) return;
    
    let targetStudentId: string | null;
    
    if (selectedStudents.size === 1) {
      // 1명 선택된 경우: 선택된 멤버 기준으로 이동
      targetStudentId = Array.from(selectedStudents)[0];
    } else {
      // 0명 선택된 경우: 현재 선택된 멤버 ID 기준으로 이동
      targetStudentId = currentSelectedStudentId;
    }
    
    if (!targetStudentId) return;
    
    const currentIndex = members.findIndex(m => m.id === targetStudentId);
    if (currentIndex < members.length - 1) {
      const nextStudent = members[currentIndex + 1];
      
      // 선택 표시가 있는 경우에만 선택 표시 업데이트
      if (selectedStudents.size === 1) {
        setSelectedStudents(new Set([nextStudent.id]));
      }
      
      // 현재 선택된 멤버 ID 업데이트
      setCurrentSelectedStudentId(nextStudent.id);
      
      // 다음 멤버의 데이터를 필드에 로드
      loadStudentDataInCheckModal(nextStudent.id);
    }
  };
  
  // 멤버 데이터를 필드에 로드 (과제 체크 모달용)
  const loadStudentDataInCheckModal = (studentId: string) => {
    const student = members.find(m => m.id === studentId);
    if (!student) return;
    
    // 대상 필드에만 멤버 번호/이름 설정 (과제 상태와 포인트는 변경하지 않음)
    setCheckInput(`${student.number}. ${student.name}`);
    
    // 현재 선택된 멤버 ID 업데이트
    setCurrentSelectedStudentId(studentId);
  };
  
  // 이전/다음 버튼 활성화 여부 확인 (과제 체크 모달용)
  const canNavigateToPrevStudentInCheckModal = () => {
    // 2명 이상 선택된 경우 비활성화
    if (selectedStudents.size > 1) return false;
    
    // 1명만 선택된 경우
    if (selectedStudents.size === 1) {
      const selectedStudentId = Array.from(selectedStudents)[0];
      if (!selectedStudentId) return false;
      
      const currentIndex = members.findIndex(m => m.id === selectedStudentId);
      return currentIndex > 0;
    }
    
    // 선택된 멤버가 없는 경우 (0명 선택) - 현재 선택된 멤버 ID 기반으로 이동 가능 여부 확인
    if (currentSelectedStudentId) {
      const currentIndex = members.findIndex(m => m.id === currentSelectedStudentId);
      return currentIndex > 0;
    }
    
    // currentSelectedStudentId도 없는 경우 첫 번째 멤버가므로 이전 이동 불가
    return false;
  };
  
  const canNavigateToNextStudentInCheckModal = () => {
    // 2명 이상 선택된 경우 비활성화
    if (selectedStudents.size > 1) return false;
    
    // 1명만 선택된 경우
    if (selectedStudents.size === 1) {
      const selectedStudentId = Array.from(selectedStudents)[0];
      if (!selectedStudentId) return false;
      
      const currentIndex = members.findIndex(m => m.id === selectedStudentId);
      return currentIndex < members.length - 1;
    }
    
    // 선택된 멤버가 없는 경우 (0명 선택) - 현재 선택된 멤버 ID 기반으로 이동 가능 여부 확인
    if (currentSelectedStudentId) {
      const currentIndex = members.findIndex(m => m.id === currentSelectedStudentId);
      return currentIndex < members.length - 1;
    }
    
    // currentSelectedStudentId도 없는 경우 마지막 멤버가므로 다음 이동 불가
    return false;
  };

  // studentStatuses가 변경될 때마다 pendingCount 업데이트 (저장 전까지는 지연)
  // useEffect(() => {
  //   updatePendingCount();
  // }, [studentStatuses, updatePendingCount]);

  // 화면 크기 감지
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 420);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // 모달이 열릴 때 첫 번째 멤버를 자동으로 선택
  useEffect(() => {
    if (isOpen && members.length > 0 && !currentSelectedStudentId) {
      const firstStudent = members[0];
      setCurrentSelectedStudentId(firstStudent.id);
      loadStudentDataInCheckModal(firstStudent.id);
    }
  }, [isOpen, members]);



  // 개별 멤버 상태 변경 처리 (항상 해당 상태에 따른 포인트 적용)
  const handleStatusChange = (newStatus: AssignmentStatusValue) => {
    if (!editingMemoStudent) return;
    const studentId = editingMemoStudent;
    setEditingMemoStatus(newStatus);

    let newPoints: number;
    switch (newStatus) {
      case 'pending':
        newPoints = 0; // 미확인: 0 포인트
        break;
      case 'confirmed':
        newPoints = getBasePoints(); // 확인: 과제 기본 포인트
        break;
      case 'delayed':
        newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
        break;
      case 'exempted':
        newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
        break;
      case 'submitted':
        newPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
        break;
      default:
        newPoints = 0;
    }

    // 로컬 상태 업데이트 (포인트 필드에 즉시 반영되도록)
    setStudentStatuses(prev => {
      const idx = prev.findIndex(s => s.studentId === studentId);
      if (idx >= 0) {
        return prev.map(s =>
          s.studentId === studentId
            ? { ...s, status: newStatus, earnedPoints: newPoints, updatedAt: new Date() }
            : s
        );
      }
      // 학생이 목록에 없으면 항목 추가 (방어 코드)
      const assignmentId = (currentAssignment as any)?.id || 'temp';
      return [...prev, {
        id: `${studentId}_${assignmentId}`,
        studentId,
        assignmentId,
        status: newStatus,
        earnedPoints: newPoints,
        updatedAt: new Date()
      }];
    });

    trackChange(studentId, { status: newStatus, earnedPoints: newPoints });
  };


  // 다음 활성화된 필드로 이동하는 함수
  const moveToNextEnabledField = (currentTabIndex: number) => {
    const tabOrder = [
      { tabIndex: 1, selector: '[data-field="target-input"]', enabled: true }, // 대상 - 항상 활성화
      { tabIndex: 2, selector: '[data-field="status-input"]', enabled: true }, // 과제 상태
      { tabIndex: 3, selector: '[data-field="points-input"]', enabled: isPointsEnabled }, // 포인트
      { tabIndex: 4, selector: '[data-field="memo-input"]', enabled: isMemoEnabled }, // 메모
      { tabIndex: 5, selector: '[data-field="submit-button"]', enabled: true } // 입력 버튼 - 항상 활성화
    ];

    // 현재 필드 다음부터 활성화된 필드 찾기
    for (let i = currentTabIndex; i < tabOrder.length; i++) {
      if (tabOrder[i].enabled) {
        const nextField = document.querySelector(tabOrder[i].selector) as HTMLElement;
        if (nextField) {
          nextField.focus();
          return;
        }
      }
    }
  };
  // 입력값으로 멤버 찾기 (번호 또는 두글자 이상의 이름)
  const findStudentByInput = (input: string): ClassMember | null => {
    if (!input.trim()) return null;
    
    const trimmedInput = input.trim();
    console.log('🎯 findStudentByInput 실행:', trimmedInput);
    
    // "번호. 이름" 형식 확인
    const formattedMatch = trimmedInput.match(/^(\d+)\.\s*(.+)$/);
    if (formattedMatch) {
      const [, numberStr, name] = formattedMatch;
      console.log('🎯 "번호. 이름" 형식 감지:', numberStr, name);
      
      // 번호로 검색
      const memberByNumber = members.find(m => m.number === numberStr);
      if (memberByNumber) {
        console.log('번호로 멤버 찾음:', memberByNumber);
        return memberByNumber;
      }
    }
    
    // 번호만으로 검색
    const memberByNumber = members.find(m => m.number === trimmedInput);
    if (memberByNumber) {
      console.log('번호로 멤버 찾음:', memberByNumber);
      return memberByNumber;
    }
    
    // 숫자만 입력한 경우는 여기서 끝남
    const isNumberOnly = !isNaN(Number(trimmedInput));
    if (isNumberOnly) {
      return null;
    }
    
    // 이름 검색: 두 글자 이상만 허용
    if (trimmedInput.length < 2) {
      console.log('두 글자 이상 입력 필요');
      return null;
    }
    
    // 이름에 입력한 글자가 모두 포함되는 멤버들 찾기
    const matchingMembers = members.filter(m => 
      trimmedInput.split('').every(char => m.name.includes(char))
    );
    
    if (matchingMembers.length === 0) {
      console.log('일치하는 멤버 없음');
      return null;
    } else if (matchingMembers.length > 1) {
      console.log('여러 명 매칭:', matchingMembers);
      return null;
    } else {
      console.log('이름으로 멤버 찾음:', matchingMembers[0]);
      return matchingMembers[0];
    }
  };

  // 탭 키 처리: 대상을 찾아서 필드에 표시하고 선택 (데이터 입력 안 함)
  const handleTabOnTargetField = () => {
    const student = findStudentByInput(checkInput);
    
    if (student) {
      // 대상 필드에 "번호. 이름" 형식으로 표시
      const displayText = `${student.number}. ${student.name}`;
      setCheckInput(displayText);
      console.log('🎯 탭: 멤버 찾음 - 필드에 표시:', displayText);
      
      // 멤버 선택
      setSelectedStudents(new Set([student.id]));
      console.log('🎯 탭: 멤버 선택됨:', student.name);
    } else if (checkInput.trim()) {
      alert('해당하는 멤버를 찾을 수 없습니다.');
      console.log('❌ 탭: 멤버 찾지 못함');
    }
    
    // 🎯 과제 상태 필드로 포커스 이동
    setTimeout(() => {
      const statusField = document.querySelector('input[tabindex="2"]') as HTMLElement;
      if (statusField) {
        statusField.focus();
        console.log('🎯 과제 상태 필드로 포커스 이동됨');
      }
    }, 50);
  };

  // 입력 버튼 처리: 선택된 멤버가 있으면 그 멤버에게, 없으면 입력값으로 찾아서 적용
  const handleBatchInput = () => {
    console.log('🎯 handleBatchInput 실행');
    
    let targetStudent: ClassMember | null = null;
    
    // 선택된 멤버가 있는 경우
    if (selectedStudents.size > 0) {
      const selectedStudentId = Array.from(selectedStudents)[0];
      targetStudent = members.find(m => m.id === selectedStudentId) || null;
      console.log('🎯 선택된 멤버 사용:', targetStudent?.name);
    } else if (checkInput.trim()) {
      // 선택된 멤버가 없으면 입력값으로 찾기
      targetStudent = findStudentByInput(checkInput);
      console.log('🎯 입력값으로 멤버 찾음:', targetStudent?.name);
      
      if (!targetStudent) {
        alert('해당하는 멤버를 찾을 수 없습니다.');
        return;
      }
    }
    
    if (targetStudent) {
      applyStatusToStudent(targetStudent, true); // true: 입력 버튼을 통한 호출 (기본 clear 유지)
    }
  };

  // 개별 멤버에게 상태를 적용하는 함수
  const applyStatusToStudent = (student: ClassMember, isFromInputButton: boolean, shouldClear: boolean = true) => {
    console.log('🎯 applyStatusToStudent 실행:', student);
    
    // 포인트 설정: 포인트 입력이 활성화된 경우 입력값 사용, 그렇지 않으면 과제 상태에 따른 기본 포인트 사용
    let finalPoints: number;
    if (isPointsEnabled && pointsInput && pointsInput.trim() !== '' && !isNaN(Number(pointsInput))) {
      // 포인트 입력이 활성화되고 유효한 값이 입력된 경우
      finalPoints = Number(pointsInput);
    } else {
      // 포인트 입력이 비활성화된 경우 과제 상태에 따른 기본 포인트 사용
      switch (selectedStatus) {
        case 'pending':
          finalPoints = 0; // 미확인: 0 포인트
          break;
        case 'confirmed':
          finalPoints = getBasePoints(); // 확인: 과제 기본 포인트
          break;
        case 'delayed':
          finalPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
          break;
        case 'exempted':
          finalPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
          break;
        case 'submitted':
          finalPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
          break;
        default:
          finalPoints = 0;
      }
    }

    // 멤버 상태 임시 업데이트 (저장 버튼을 눌러야 실제 반영)
    setStudentStatuses(prev => {
      const existing = prev.find(s => s.studentId === student.id);
      if (existing) {
        return prev.map(s => 
          s.studentId === student.id 
            ? { 
                ...s, 
                status: selectedStatus, 
                earnedPoints: finalPoints, 
                memo: isMemoEnabled && memoInput.trim() ? memoInput : s.memo,
                updatedAt: new Date(),
                confirmedAt: selectedStatus === 'confirmed' ? new Date() : undefined
              }
            : s
        );
      } else {
        return [...prev, {
          id: `${student.id}_${currentAssignment?.id || 'temp'}`,
          studentId: student.id,
          assignmentId: (currentAssignment?.id || 'temp') as string,
          status: selectedStatus,
          earnedPoints: finalPoints,
          memo: isMemoEnabled ? memoInput : undefined,
          updatedAt: new Date(),
          confirmedAt: selectedStatus === 'confirmed' ? new Date() : undefined
        }];
      }
    });
    
    // 변경사항 추적에 추가
    const currentStatus = studentStatuses.find(s => s.studentId === student.id);
    trackChange(student.id, {
      status: selectedStatus,
      earnedPoints: finalPoints,
      memo: isMemoEnabled && memoInput.trim() ? memoInput : currentStatus?.memo,
      confirmedAt: selectedStatus === 'confirmed' ? new Date() : undefined
    });

    // 성공 메시지
    console.log(`🎯 ${student.name}에게 임시 적용되었습니다. (저장 버튼을 눌러야 실제 반영)`);
    
    // 메모 입력 필드 초기화
    if (isMemoEnabled) {
      setMemoInput('');
    }
    
    // 🎯 연속 토글 처리
    console.log('🔍 연속 토글 상태 확인:', { 
      isAutoIncrementEnabled, 
      checkInput, 
      membersLength: members?.length,
      연속토글_활성화됨: isAutoIncrementEnabled ? 'YES' : 'NO'
    });
    
    if (isAutoIncrementEnabled) {
      console.log('=== 연속 토글 처리 시작 ===');
      console.log('현재 멤버:', student.name, '번호:', student.number);
      
      // 현재 멤버의 번호를 기반으로 다음 번호 추출
      const currentNumber = parseInt(student.number);
      console.log('현재 번호:', currentNumber);
      
      if (!isNaN(currentNumber)) {
        // 다음 번호 멤버 찾기
        const nextMember = members.find(m => parseInt(m.number) === currentNumber + 1);
        console.log('다음 번호 멤버:', nextMember);
        
        if (nextMember) {
          // 다음 번호를 대상 필드에 설정
          console.log('🎯 다음 번호 설정 전:', checkInput);
          const displayText = `${nextMember.number}. ${nextMember.name}`;
          setCheckInput(displayText);
          console.log('🎯 다음 대상 설정됨:', displayText);
          
          // 🎯 다음 번호 멤버 선택
          setSelectedStudents(new Set([nextMember.id]));
          console.log('🎯 다음 번호 멤버 선택됨:', nextMember.name);
          
          // 🎯 연속 토글: Enter 경로(shouldClear=false)에서만 대상 필드로 포커스 이동
          if (!shouldClear) {
            setTimeout(() => {
              const firstInput = document.querySelector('input[tabindex="1"]') as HTMLInputElement;
              if (firstInput) {
                firstInput.focus();
                console.log('🎯 연속 토글(Enter): 대상 입력 필드로 포커스 이동됨');
              }
            }, 100);
          }
          
          // 연속 토글의 경우 다음 번호를 설정하고 여기서 종료
          return;
        } else {
          console.log('❌ 다음 번호 멤버를 찾을 수 없음 - 입력 필드 초기화');
          if (shouldClear) {
            setCheckInput('');
          }
        }
      } else {
        console.log('❌ 현재 입력값이 숫자가 아님 - 입력 필드 초기화');
        if (shouldClear) {
          setCheckInput('');
        }
      }
    } else {
      console.log('❌ 연속 토글이 비활성화됨 - 입력 필드 초기화');
      if (shouldClear) {
        setCheckInput('');
      }
    }
    
    console.log('🎯 입력 필드 초기화됨');
    
    // 🎯 연속 토글 여부에 따른 선택 처리
    if (isAutoIncrementEnabled) {
      // 연속 토글 활성화: 다음 번호 멤버 선택 (이미 위에서 처리됨)
      console.log('🎯 연속 토글 활성화 - 다음 번호 멤버 선택됨');
    } else {
      console.log('❌ 연속 토글이 비활성화됨 - 입력 필드 초기화');

      // 🎯 상태 적용 후 필드 초기화 (마우스 클릭 시에만)
      if (shouldClear) {
        setTimeout(() => {
          setSelectedStudents(new Set());
          setCheckInput('');
          console.log('🎯 연속 토글 비활성화 - 모든 멤버 선택 해제 및 입력 필드 초기화됨');
          
          // 포커스 제거
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
            console.log('🎯 연속 토글 비활성화 - 모든 포커스 제거됨');
          }
          // 깜빡임 방지용 플래그 해제
          setIsUserTyping(false);
          setIsSubmittingViaClick(false);
          setIsUserTyping(false);
          setIsSubmittingViaClick(false);
          setIsUserTyping(false);
        }, 50);
      }
      }
  };

  // 키보드 단축키로 과제 상태 선택 (멤버별 기록 편집 모달용)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // 멤버별 기록 편집 모달이 열려있을 때만 처리
      if (!isMemoEditModalOpen || !editingMemoStudent) return;
      
      // 입력 필드에 포커스가 있을 때는 단축키 비활성화
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).contentEditable === 'true'
      )) {
        return;
      }
      
      // 숫자 키 1,2,3,4로 과제 상태 선택
      if (event.key >= '1' && event.key <= '4') {
        const index = parseInt(event.key) - 1;
        if (index >= 0 && index < statusOptions.length) {
          const selectedOption = statusOptions[index];
          handleStatusChange(selectedOption.value);
        }
      }
      
      // Enter 키로 포인트 필드로 포커스 이동
      if (event.key === 'Enter') {
        event.preventDefault();
        console.log('Enter 키 감지됨');
        console.log('memoEditContainerRef.current:', memoEditContainerRef.current);
        
        // 현재 모달 내에서 포인트 필드 찾기
        if (memoEditContainerRef.current) {
          const pointsField = memoEditContainerRef.current.querySelector('.memo-edit-points-input') as HTMLInputElement;
          console.log('찾은 포인트 필드:', pointsField);
          if (pointsField) {
            pointsField.focus();
            console.log('포인트 필드에 포커스 이동됨');
          } else {
            console.log('포인트 필드를 찾을 수 없음');
            // 다른 방법으로 찾기 시도
            const allInputs = memoEditContainerRef.current.querySelectorAll('input');
            console.log('모달 내 모든 input 요소들:', allInputs);
          }
        } else {
          console.log('memoEditContainerRef.current가 null임');
        }
      }
      
      // 화살표 키로 이전/다음 멤버 이동
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        console.log('왼쪽 화살표 키 감지됨');
        if (canNavigateToPrevStudent()) {
          handlePrevStudent();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        console.log('오른쪽 화살표 키 감지됨');
        if (canNavigateToNextStudent()) {
          handleNextStudent();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isMemoEditModalOpen, editingMemoStudent, statusOptions, memoEditContainerRef]);

  // 메인 과제 체크 모달에서 숫자 단축키로 상태 변경 (캡처 단계에서 처리)
  useEffect(() => {
    const handleMainKeyPress = (event: KeyboardEvent) => {
      // 메인 모달이 열려있을 때만 처리
      if (!isOpen) return;
      // 학생 상세 모달(더블클릭으로 연 모달)이 열려 있으면 여기서 처리하지 않음 → 모달 내부에서 1-4 처리
      if (isStudentDetailModalOpen) return;
      
      // 숫자 키(1-4)가 아니면 무시
      if (!(event.key >= '1' && event.key <= '4')) return;
      
      // 선택된 멤버가 없으면 무시
      if (selectedStudents.size === 0) return;
      
      // 입력 필드에 포커스가 있을 때는 단축키 비활성화
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).contentEditable === 'true'
      )) {
        return;
      }
      
      // 이벤트 전파 중지 (다른 핸들러에서 처리되지 않도록)
      event.preventDefault();
      event.stopPropagation();
      
      // 숫자 키(1-4)로 선택된 멤버들의 상태 변경
      const index = parseInt(event.key) - 1;
      if (index >= 0 && index < statusOptions.length) {
        const newStatus = statusOptions[index].value;

        // 항상 해당 상태에 따른 포인트 적용
        const useEarnedPoints = (() => {
          switch (newStatus) {
            case 'pending': return 0;
            case 'confirmed': return getBasePoints();
            case 'delayed': return getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
            case 'exempted': return getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
            case 'submitted': return getBasePoints();
            default: return 0;
          }
        })();

        // 선택된 멤버들의 상태 변경
        setStudentStatuses(prev => {
          const updatedStatuses = prev.map(status => {
            if (selectedStudents.has(status.studentId)) {
              return {
                ...status,
                status: newStatus,
                earnedPoints: useEarnedPoints,
                updatedAt: new Date(),
                confirmedAt: newStatus === 'confirmed' ? new Date() : status.confirmedAt
              };
            }
            return status;
          });
          return updatedStatuses;
        });

        selectedStudents.forEach(studentId => {
          setChangeTracker(prevTracker => ({
            ...prevTracker,
            [studentId]: {
              ...prevTracker[studentId],
              status: newStatus,
              earnedPoints: useEarnedPoints,
              updatedAt: new Date(),
              confirmedAt: newStatus === 'confirmed' ? new Date() : undefined
            }
          }));
        });
      }
    };

    // 캡처 단계에서 이벤트 처리 (다른 핸들러보다 먼저 실행)
    document.addEventListener('keydown', handleMainKeyPress, true);
    return () => {
      document.removeEventListener('keydown', handleMainKeyPress, true);
    };
  }, [isOpen, isStudentDetailModalOpen, selectedStudents, statusOptions, statusDefaultPoints, currentAssignment, routine, mode]);

  // 상태별 색상 가져오기
  const getStatusStyle = (status: AssignmentStatusValue) => {
    const option = statusOptions.find(opt => opt.value === status);
    return {
      backgroundColor: option?.bgColor || 'var(--color-border)',
      color: option?.color || '#000'
    };
  };

  // 멤버 번호용 상태별 색상 가져오기
  const getNumberStatusStyle = (status: AssignmentStatusValue) => {
    // submitted 상태는 버튼에는 없지만 멤버 블럭에는 검정색으로 표시
    if (status === 'submitted') {
      return {
        backgroundColor: '#000000',
        color: '#fff'
      };
    }
    
    const option = statusOptions.find(opt => opt.value === status);
    return {
      backgroundColor: option?.bgColor || 'var(--color-border)',
      color: option?.color || '#000'
    };
  };

  // 상태를 숫자로 변환 (1:미확인, 2:확인, 3:지연확인, 4:예외, 5:제출확인)
  const getStatusNumber = (status: AssignmentStatusValue): number => {
    const statusMap: { [key in AssignmentStatusValue]: number } = {
      'pending': 1,      // 미확인
      'confirmed': 2,    // 확인
      'delayed': 3,      // 지연확인
      'exempted': 4,     // 예외
      'excluded': 0,     // 제외 (사용하지 않음)
      'submitted': 5     // 제출확인
    };
    return statusMap[status] || 1;
  };
  
  // 숫자를 상태로 변환 (1:미확인, 2:확인, 3:지연확인, 4:예외)
  const getStatusFromNumber = (number: number): AssignmentStatusValue => {
    const statusMap: { [key: number]: AssignmentStatusValue } = {
      1: 'pending',      // 미확인
      2: 'confirmed',    // 확인
      3: 'delayed',      // 지연확인
      4: 'exempted'      // 예외
    };
    return statusMap[number] || 'pending';
  };

  // 상태별 이모지 가져오기
  const getStatusEmoji = (status: AssignmentStatusValue): string => {
    const emojiMap: { [key in AssignmentStatusValue]: string } = {
      'pending': '🔴',      // 미확인 - 빨간 원
      'confirmed': '🟢',    // 확인 - 초록 원
      'delayed': '🔵',      // 지연확인 - 파란 원
      'exempted': '🟡',     // 예외 - 노란 원
      'excluded': '⚪',      // 제외 - 흰 원
      'submitted': '⚫'      // 제출확인 - 검정 원
    };
    return emojiMap[status] || '⚪';
  };



  // 단축키 기능 - 숫자 키로 상태 변경
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      // 숫자 키로 상태 변경 (1: 미확인, 2: 확인, 3: 지연확인, 4: 예외)
      if (e.key >= '1' && e.key <= '4') {
        // 상태 필드에 포커스가 있을 때: 드롭다운 상태 변경
        if (isStatusFieldFocused) {
          e.preventDefault();
          const statusNumber = parseInt(e.key);
          const newStatus = getStatusFromNumber(statusNumber);
          
          // 과제 체크 모달용 상태 변경
          setSelectedStatus(newStatus);
          
          // 과제 상태에 따른 포인트 자동 설정
          let newPoints: number;
          switch (newStatus) {
            case 'pending':
              newPoints = 0; // 미확인: 0 포인트
              break;
            case 'confirmed':
              newPoints = getBasePoints(); // 확인: 과제 기본 포인트
              break;
            case 'delayed':
              newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
              break;
            case 'exempted':
              newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
              break;
            case 'submitted':
              newPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
              break;
            default:
              newPoints = 0;
          }
          setPointsInput(newPoints.toString());
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isStatusFieldFocused]);

  // 무한 루프 방지: useEffect 제거하고 useState 함수형 초기화 사용
  // useEffect(() => {
  //   if (isOpen) {
  //     document.body.style.overflow = 'hidden';
  //     
  //     // 서버에서 로드된 데이터 또는 기본 생성된 데이터로 초기화
  //     const initialStatuses = existingStatuses.length > 0 ? existingStatuses : members.map((member, index) => ({
  //       id: `${member.id}_${currentAssignment?.id || 'temp'}`,
  //       studentId: member.id,
  //       assignmentId: currentAssignment?.id || 'temp',
  //       status: 'pending' as AssignmentStatusValue,
  //       earnedPoints: getBasePoints(),
  //       updatedAt: new Date(),
  //       memo: undefined
  //       // confirmedAt은 status가 'confirmed'일 때만 설정
  //     }));
  //     
  //     // 현재 상태를 초기화
  //     setStudentStatuses([...initialStatuses]);
  //     
  //     // 모달 상태 초기화
  //     setSelectedStudents(new Set());
  //     setIsPointsEnabled(false);
  //     setIsAutoIncrementEnabled(false);
  //     setIsMemoEnabled(false);
  //     setIsOptionsVisible(false);
  //     setSelectedStatus('confirmed');
  //     setPointsInput(getBasePoints().toString());
  //     setCheckInput('');
  //     setMemoInput('');
  // 
  //     setHoveredMemo(null);
  //     setIsMemoEditModalOpen(false);
  //     setEditingMemoStudent(null);
  //     setEditingMemoValue('');
  //     
  //     // 변경사항 추적기 초기화
  //     setChangeTracker({});
  //   } else {
  //     document.body.style.overflow = 'auto';
  //   }
  //   return () => { document.body.style.overflow = 'auto'; };
  // }, [isOpen, members, existingStatuses, mode]);

  // 디버깅: 컴포넌트 렌더링 횟수 추적 (무한 루프 확인용)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔄 AssignmentCheckModal 렌더링됨 - isOpen:', isOpen);
  }

  // 메모 표시 (메모 입력 필드에 표시)
  const showMemo = (studentId: string, memo: string) => {
    // 선택되지 않은 학생에 마우스 오버 시 메모 입력 필드에 표시
    if (!selectedStudents.has(studentId) || selectedStudents.size === 0) {
      setMemoInput(memo);
      setIsHoveringMemo(true);
    }
  };

  // 메모 숨기기 (메모 입력 필드 초기화 또는 선택된 학생의 메모로 복원)
  const hideMemo = () => {
    setIsHoveringMemo(false);
    // 선택된 학생이 있는 경우 해당 학생의 메모로 복원
    if (selectedStudents.size === 1) {
      const selectedStudentId = Array.from(selectedStudents)[0];
      const status = studentStatuses.find(s => s.studentId === selectedStudentId);
      if (status?.memo && status.memo.trim()) {
        setMemoInput(status.memo);
      } else {
        setMemoInput('');
      }
    } else if (selectedStudents.size > 1) {
      // 복수 선택 시 공통 메모 입력을 위해 초기화
      setMemoInput('');
    } else {
      // 선택 해제 시 초기화
      setMemoInput('');
    }
  };

  const getStudentMemoForPrint = useCallback((studentId: string) => {
    if (isMemoViewMode && memoViewMemos[studentId]?.trim()) {
      return memoViewMemos[studentId].trim();
    }
    return studentStatuses.find(s => s.studentId === studentId)?.memo || '';
  }, [isMemoViewMode, memoViewMemos, studentStatuses]);

  const buildStudentsForPrint = useCallback((): AssignmentPrintStudent[] => {
    const students = members.map(member => {
      const status = studentStatuses.find(s => s.studentId === member.id);
      return {
        studentNumber: member.number,
        studentName: member.name || member.id,
        status: status?.status || 'pending',
        memo: getStudentMemoForPrint(member.id)
      };
    });
    return sortStudentsForPrint(students);
  }, [members, studentStatuses, getStudentMemoForPrint]);

  const handlePrintAssignment = useCallback(async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      if (mode === 'project') {
        if (!projectAssignment) {
          alert('프린트할 과제 정보를 찾을 수 없습니다.');
          return;
        }

        const activeSubTaskId = currentSubTask?.id || selectedSubTaskId;
        const subTaskSources = (projectSubTasks && projectSubTasks.length > 0)
          ? projectSubTasks
          : (currentSubTask ? [{ subTask: currentSubTask }] : []);

        const subTasksData = await Promise.all(subTaskSources.map(async (pt: any) => {
          const subTask = pt.subTask;
          if (!subTask) {
            return { subTaskTitle: '', subTaskPoints: 0, students: [] as AssignmentPrintStudent[] };
          }

          let statuses = studentStatuses;
          if (subTask.id !== activeSubTaskId && userId && classId) {
            statuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
              userId,
              classId,
              projectAssignment.id,
              subTask.id,
              memberGroup
            );
          }

          const students = sortStudentsForPrint(statuses.map(s => {
            const member = members.find(m => m.id === s.studentId);
            return {
              studentNumber: member?.number,
              studentName: member?.name || s.studentId,
              status: s.status,
              memo: subTask.id === activeSubTaskId
                ? getStudentMemoForPrint(s.studentId)
                : (s.memo || '')
            };
          }));

          return {
            subTaskTitle: subTask.title,
            subTaskPoints: subTask.points || 0,
            students
          };
        }));

        const due = projectAssignment.dueDate ? new Date(projectAssignment.dueDate) : undefined;
        const dueText = due ? formatAssignmentDueDate(due) : '마감일 없음';
        const totalPoints = subTasksData.reduce((sum, st) => sum + st.subTaskPoints, 0);
        const rewardCoins = projectAssignment.rewardEnabled ? (projectAssignment.rewardCoins || 0) : 0;
        const rewardItemCardCount = projectAssignment.rewardEnabled
          ? (projectAssignment.rewardItemCardIds?.length || 0)
          : 0;

        const html = buildProjectAssignmentsExportHtml([{
          project: projectAssignment,
          dueDateText: dueText,
          basePoints: totalPoints,
          rewardCoins,
          rewardItemCardCount,
          subTasks: subTasksData
        }]);
        openAssignmentPrintWindow(html);
        return;
      }

      const printAssignment = mode === 'routine' ? routine : assignment;
      if (!printAssignment) {
        alert('프린트할 과제 정보를 찾을 수 없습니다.');
        return;
      }

      let dueDateText = '마감일 없음';
      if (mode === 'routine') {
        dueDateText = `과제일 ${formatAssignmentDueDate(currentDate)}`;
      } else {
        const due = (printAssignment as Assignment).dueDate
          ? new Date((printAssignment as Assignment).dueDate!)
          : undefined;
        dueDateText = due ? formatAssignmentDueDate(due) : '마감일 없음';
      }

      const ass = printAssignment as Assignment;
      const rewardCoins = ass.rewardEnabled ? (ass.rewardCoins || 0) : 0;
      const rewardItemCardCount = ass.rewardEnabled ? (ass.rewardItemCardIds?.length || 0) : 0;
      const basePoints = mode === 'routine'
        ? (routine?.basePoints || 0)
        : (ass.basePoints || 0);

      const html = buildGeneralAssignmentsExportHtml([{
        assignment: ass,
        categoryName: mode === 'routine'
          ? (category?.name || '루틴 과제')
          : (category?.name || '전체 카테고리'),
        dueDateText,
        basePoints,
        rewardCoins,
        rewardItemCardCount,
        students: buildStudentsForPrint()
      }]);
      openAssignmentPrintWindow(html);
    } catch (error) {
      console.error('과제 프린트 실패:', error);
      alert('프린트에 실패했습니다.');
    } finally {
      setIsPrinting(false);
    }
  }, [
    isPrinting,
    mode,
    projectAssignment,
    currentSubTask,
    selectedSubTaskId,
    projectSubTasks,
    studentStatuses,
    userId,
    classId,
    memberGroup,
    members,
    getStudentMemoForPrint,
    routine,
    assignment,
    currentDate,
    category,
    buildStudentsForPrint
  ]);

  // 보드 페이지로 메모 전송
  const handleSendMemosToBoard = () => {
    console.log('📤 보내기 버튼 클릭 - 메모 수집 시작');
    console.log('📤 studentStatuses:', studentStatuses.map(s => ({ studentId: s.studentId, memo: s.memo })));
    console.log('📤 members:', members.map(m => ({ id: m.id, name: m.name, number: m.number })));
    console.log('📤 memoViewMemos:', memoViewMemos);
    
    // 과제 제목 가져오기
    let assignmentTitle = '';
    if (mode === 'general' && assignment?.title) {
      assignmentTitle = assignment.title;
    } else if (mode === 'routine' && routine?.title) {
      assignmentTitle = routine.title;
    } else if (mode === 'project') {
      // 프로젝트 모드에서는 현재 하위 과제 제목 사용, 없으면 프로젝트 과제 제목 사용
      if (currentSubTask?.title) {
        assignmentTitle = currentSubTask.title;
      } else if (projectAssignment?.title) {
        assignmentTitle = projectAssignment.title;
      }
    }
    
    console.log('📤 과제 제목:', assignmentTitle);
    
    // 학생별 메모 수집 (studentStatuses에서)
    const memos: Record<string, string> = {};
    
    // 1. studentStatuses에서 메모 수집
    studentStatuses.forEach(status => {
      console.log(`📤 상태 확인: studentId=${status.studentId}, memo=${status.memo}`);
      if (status.memo && status.memo.trim()) {
        // members에 해당 studentId가 있는지 확인
        const member = members.find(m => m.id === status.studentId);
        if (member) {
          // 과제 제목을 앞에 추가하고 내보내기 메모임을 표시하기 위해 "*" 추가
          const memoWithTitle = assignmentTitle 
            ? `*(${assignmentTitle}) ${status.memo.trim()}`
            : `*${status.memo.trim()}`;
          memos[status.studentId] = memoWithTitle;
          console.log(`✅ 메모 추가 (studentStatuses): ${status.studentId} (${member.name}) -> ${memoWithTitle}`);
    } else {
          console.warn(`⚠️ studentId ${status.studentId}에 해당하는 member를 찾을 수 없음`);
        }
      }
    });
    
    // 2. memoViewMemos에서도 메모 수집 (메모 보기 모드에서 수정된 메모)
    Object.entries(memoViewMemos).forEach(([studentId, memo]) => {
      if (memo && memo.trim()) {
        const member = members.find(m => m.id === studentId);
        if (member) {
          // 이미 studentStatuses에서 메모가 있으면 병합하지 않고, 없으면 추가
          if (!memos[studentId]) {
            // 과제 제목을 앞에 추가하고 내보내기 메모임을 표시하기 위해 "*" 추가
            const memoWithTitle = assignmentTitle 
              ? `*(${assignmentTitle}) ${memo.trim()}`
              : `*${memo.trim()}`;
            memos[studentId] = memoWithTitle;
            console.log(`✅ 메모 추가 (memoViewMemos): ${studentId} (${member.name}) -> ${memoWithTitle}`);
          } else {
            console.log(`ℹ️ ${studentId}의 메모는 이미 studentStatuses에 있음`);
          }
        }
      }
    });

    console.log('📤 수집된 메모:', memos);
    console.log('📤 메모 개수:', Object.keys(memos).length);
    console.log('📤 메모가 있는 학생 ID 목록:', Object.keys(memos));

    if (Object.keys(memos).length === 0) {
      console.log('❌ 전송할 메모가 없음');
      setToastMessage('전송할 메모가 없습니다.');
      setShowToast(true);
      return;
    }

    console.log('📤 보드 페이지로 메모 전송:', memos);
    console.log('📤 userId:', userId, 'classId:', classId);
    
    // localStorage에 저장 (BoardPage가 열려있지 않을 때를 대비)
    if (userId && classId) {
      const storageKey = `pendingMemosToBoard_${userId}_${classId}`;
      console.log('📤 localStorage 키:', storageKey);
      
      const existingMemos = localStorage.getItem(storageKey);
      let mergedMemos = { ...memos };
      
      if (existingMemos) {
        try {
          const parsed = JSON.parse(existingMemos);
          console.log('📤 기존 메모 발견:', parsed);
          // 기존 메모와 병합 (기존 텍스트가 있으면 줄바꿈 후 누적)
          Object.entries(parsed).forEach(([studentId, memo]: [string, any]) => {
            if (mergedMemos[studentId] && mergedMemos[studentId].trim()) {
              // 줄바꿈으로 메모 추가 (내보내기 메모는 이미 "*"가 포함되어 있음)
              mergedMemos[studentId] = `${memo}\n${mergedMemos[studentId]}`;
            } else {
              mergedMemos[studentId] = memo;
            }
          });
        } catch (e) {
          console.error('기존 메모 파싱 실패:', e);
    }
      }
      
      console.log('📤 localStorage에 저장할 메모:', mergedMemos);
      localStorage.setItem(storageKey, JSON.stringify(mergedMemos));
      console.log('✅ localStorage 저장 완료');
    }
    
    // 전역 이벤트 발생 (BoardPage에서 수신)
    console.log('📤 이벤트 발생:', memos);
    const event = new CustomEvent('sendMemosToBoard', { detail: memos });
    window.dispatchEvent(event);
    console.log('✅ 이벤트 전송 완료');
    
    setToastMessage(`${Object.keys(memos).length}명의 학생 메모가 보드 페이지로 전송되었습니다.`);
    setShowToast(true);
  };

  // 학생 상세 모달 열기
  const openStudentDetailModal = (studentId: string) => {
    // 학생 정보 찾기
    const student = members.find(m => m.id === studentId);
    if (!student) return;
    
    // 현재 학생 인덱스 찾기
    const assignedStudents = members.filter(m => isStudentAssigned(m.id));
    const studentIndex = assignedStudents.findIndex(m => m.id === studentId);
    
    setSelectedStudentForDetail(studentId);
    setCurrentStudentIndex(studentIndex);
    setIsStudentDetailModalOpen(true);
  };
  
  // 학생 상세 모달 닫기
  const closeStudentDetailModal = () => {
    setStudentDetailDraft({});
    setIsStudentDetailModalOpen(false);
    setSelectedStudentForDetail(null);
  };

  // 학생 상세 모달: <> 네비 시 현재 편집 내용 임시 저장
  const handleStudentNavigation = (
    direction: 'prev' | 'next',
    currentState?: {
      status: string;
      points: number;
      memo: string;
      submissionLink?: string;
      submissionMessage?: string;
      isDeleted?: boolean;
      isImageDeleted?: boolean;
    }
  ) => {
    if (!selectedStudentForDetail) return;

    if (currentState) {
      const currentStatusRow = studentStatuses.find(
        (s) => s.studentId === selectedStudentForDetail
      ) as any;
      setStudentDetailDraft((prev) => ({
        ...prev,
        [selectedStudentForDetail]: {
          status: currentState.status,
          points: currentState.points,
          memo: currentState.memo,
          submissionLink:
            currentState.submissionLink ?? currentStatusRow?.submissionLink ?? '',
          submissionMessage:
            currentState.submissionMessage ?? currentStatusRow?.submissionMessage ?? '',
          submissionImagePath: currentStatusRow?.submissionImagePath,
          submissionImageUpdatedAt: currentStatusRow?.submissionImageUpdatedAt,
          submissionImageSizeBytes: currentStatusRow?.submissionImageSizeBytes,
          isDeleted: currentState.isDeleted,
          isImageDeleted: currentState.isImageDeleted ?? currentState.isDeleted
        }
      }));
    }

    const assignedStudents = members.filter(m => isStudentAssigned(m.id));
    const currentIndex = assignedStudents.findIndex(m => m.id === selectedStudentForDetail);
    let newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0) newIndex = assignedStudents.length - 1;
    if (newIndex >= assignedStudents.length) newIndex = 0;
    const newStudentId = assignedStudents[newIndex]?.id;
    if (newStudentId) {
      setSelectedStudentForDetail(newStudentId);
      setCurrentStudentIndex(newIndex);
    }
  };

  // 학생 상세 모달 저장: 현재 학생 포함 draft 전부 반영 후 닫기
  const handleStudentDetailModalSave = async (
    status: string,
    points: number,
    memo: string,
    submissionData?: {
      link: string;
      message: string;
      isDeleted: boolean;
      isImageDeleted?: boolean;
      replacementImage?: { blob: Blob; mimeType: string };
    }
  ) => {
    if (!selectedStudentForDetail) return;

    const imageDeleteForStudent =
      submissionData?.isImageDeleted === true;

    let replacementMeta: {
      path: string;
      updatedAt: string;
      sizeBytes: number;
    } | null = null;

    if (submissionData?.replacementImage && userId && classId) {
      const assignmentId =
        mode === 'project' ? projectAssignment?.id : assignment?.id;
      const subTaskId = mode === 'project' ? selectedSubTaskId ?? undefined : undefined;
      if (assignmentId) {
        try {
          const { assignmentSubmissionImageService } = await import(
            '../services/assignmentSubmissionImageService'
          );
          const { buildAssignmentSubmissionImagePath } = await import(
            '../utils/assignmentSubmissionImagePaths'
          );
          const { blob } = submissionData.replacementImage;
          const ext = 'webp';
          const uploadMime = 'image/webp';
          const path = buildAssignmentSubmissionImagePath({
            teacherUserId: userId,
            classId,
            memberGroup,
            assignmentType: mode === 'project' ? 'project' : 'general',
            assignmentId,
            studentId: selectedStudentForDetail,
            subTaskId: subTaskId ?? undefined,
            extension: ext
          });
          await assignmentSubmissionImageService.uploadTeacherBlob(path, blob, uploadMime, userId);
          replacementMeta = {
            path,
            updatedAt: new Date().toISOString(),
            sizeBytes: blob.size
          };
        } catch (error) {
          console.error('제출 이미지 교체 업로드 실패:', error);
        }
      }
    }

    const mergedDraft: Record<string, {
      status: string;
      points: number;
      memo: string;
      submissionLink?: string;
      submissionMessage?: string;
      isDeleted?: boolean;
      isImageDeleted?: boolean;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }> = {
      ...studentDetailDraft,
      [selectedStudentForDetail]: {
        status,
        points,
        memo,
        submissionLink: submissionData?.link,
        submissionMessage: submissionData?.message,
        isDeleted: submissionData?.isDeleted,
        isImageDeleted: imageDeleteForStudent,
        ...(replacementMeta
          ? {
              submissionImagePath: replacementMeta.path,
              submissionImageUpdatedAt: replacementMeta.updatedAt,
              submissionImageSizeBytes: replacementMeta.sizeBytes
            }
          : {})
      }
    };

    if (imageDeleteForStudent) {
      const current = studentStatuses.find((s) => s.studentId === selectedStudentForDetail) as any;
      const imagePath = current?.submissionImagePath as string | undefined;
      const imageUpdatedAt = current?.submissionImageUpdatedAt as string | undefined;
      if (imagePath) {
        try {
          const { assignmentSubmissionImageService } = await import(
            '../services/assignmentSubmissionImageService'
          );
          const { assignmentSubmissionImageCache } = await import(
            '../utils/assignmentSubmissionImageCache'
          );
          await assignmentSubmissionImageService.deleteByPath(imagePath);
          assignmentSubmissionImageCache.revoke(imagePath, imageUpdatedAt);
        } catch (error) {
          console.error('제출 이미지 Storage 삭제 실패:', error);
        }
      }
    }

    setStudentStatuses(prev => {
      let next = [...prev];
      Object.entries(mergedDraft).forEach(([studentId, d]) => {
        const idx = next.findIndex(s => s.studentId === studentId);
        if (idx >= 0) {
          const s = next[idx] as any;
          const submissionLink = d.isDeleted ? undefined : (d.submissionLink ?? s.submissionLink);
          const submissionMessage = d.isDeleted ? undefined : (d.submissionMessage ?? s.submissionMessage);
          let submissionImagePath = d.isImageDeleted ? undefined : s.submissionImagePath;
          let submissionImageUpdatedAt = d.isImageDeleted ? undefined : s.submissionImageUpdatedAt;
          let submissionImageSizeBytes = d.isImageDeleted ? undefined : s.submissionImageSizeBytes;
          if (d.submissionImagePath) {
            submissionImagePath = d.submissionImagePath;
            submissionImageUpdatedAt = d.submissionImageUpdatedAt;
            submissionImageSizeBytes = d.submissionImageSizeBytes;
          }
          next[idx] = {
            ...s,
            status: d.status as AssignmentStatusValue,
            earnedPoints: d.points,
            memo: d.memo || undefined,
            updatedAt: new Date(),
            submissionLink,
            submissionMessage,
            submissionImagePath,
            submissionImageUpdatedAt,
            submissionImageSizeBytes
          };
        }
      });
      return next;
    });

    Object.entries(mergedDraft).forEach(([studentId, d]) => {
      trackChange(studentId, {
        status: d.status as AssignmentStatusValue,
        earnedPoints: d.points,
        memo: d.memo || undefined
      });
    });

    setSubmissionChangeTracker(prev => {
      const next = { ...prev };
      Object.keys(mergedDraft).forEach(studentId => delete next[studentId]);
      return next;
    });

    setStudentDetailDraft({});
    setIsStudentDetailModalOpen(false);
    setSelectedStudentForDetail(null);
  };
  
  // 멤버별 기록 편집 모달 열기
  const openMemoEditModal = (studentId: string, currentMemo: string) => {
    // 모달을 열 때 현재 상태를 초기 상태로 저장 (복원용)
    setInitialStudentStatuses([...studentStatuses]);
    
    setEditingMemoStudent(studentId);
    setEditingMemoValue(currentMemo);
    
    // 해당 멤버의 현재 상태 가져오기
    const currentStatus = studentStatuses.find(s => s.studentId === studentId) as any;
    if (currentStatus) {
      setEditingMemoStatus(currentStatus.status);
      
      // 제출 확인 데이터 초기화 - 캐시된 변경사항이 있으면 우선 사용
      const cachedSubmission = submissionChangeTracker[studentId];
      if (cachedSubmission) {
        // 캐시된 변경사항 로드
        setEditedSubmissionLink(cachedSubmission.link);
        setEditedSubmissionMessage(cachedSubmission.message);
        setIsSubmissionDeleted(cachedSubmission.isDeleted);
        setIsEditingSubmission(false);
      } else {
        // 원본 데이터 로드
        setEditedSubmissionLink(currentStatus.submissionLink || '');
        setEditedSubmissionMessage(currentStatus.submissionMessage || '');
        setIsSubmissionDeleted(false);
        setIsEditingSubmission(false);
      }
    }
    
    setIsMemoEditModalOpen(true);
    hideMemo(); // 기존 메모 표시 숨기기
    
    // 모바일에서 페이지 전체 스크롤 허용
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'auto';
    }
  };

  // 개별 편집 모달 닫기 (초기 상태로 복원 - [닫기] 버튼용)
  const closeMemoEditModal = () => {
    // 초기 상태로 복원
    setStudentStatuses([...initialStudentStatuses]);
    
    // 변경사항 추적기 초기화 (모달에서 변경한 내용은 취소)
    setChangeTracker({});
    
    setIsMemoEditModalOpen(false);
    setEditingMemoStudent(null);
    setEditingMemoValue('');
    setEditingMemoStatus('pending');
    
    // 모바일에서 페이지 전체 스크롤 복원
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    }
  };

  // 개별 편집 모달 저장 (로컬에 저장하여 멤버 블럭에 반영)
  const saveMemo = () => {
    if (!editingMemoStudent) return;
    
    const memoValue = editingMemoValue.trim();
    
    // 현재 멤버의 포인트 가져오기 (UI에서 표시되는 값)
    const currentPoints = studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints || 0;
    
    // 제출 확인 데이터를 캐시에 저장 (서버에는 아직 반영하지 않음)
    const currentStatus = studentStatuses.find(s => s.studentId === editingMemoStudent) as any;
    const originalLink = currentStatus?.submissionLink || '';
    const originalMessage = currentStatus?.submissionMessage || '';
    
    // 변경사항이 있으면 캐시에 저장
    if (editedSubmissionLink !== originalLink || 
        editedSubmissionMessage !== originalMessage || 
        isSubmissionDeleted) {
      setSubmissionChangeTracker(prev => ({
        ...prev,
        [editingMemoStudent]: {
          link: editedSubmissionLink,
          message: editedSubmissionMessage,
          isDeleted: isSubmissionDeleted
        }
      }));
      
      console.log(`📦 제출 확인 데이터 캐시 저장:`, {
        studentId: editingMemoStudent,
        link: editedSubmissionLink,
        message: editedSubmissionMessage,
        isDeleted: isSubmissionDeleted
      });
    }
    
    // 로컬 상태 업데이트 (UI에 즉시 반영) - 제출 확인 데이터는 제외
    setStudentStatuses(prev => 
      prev.map(s => 
        s.studentId === editingMemoStudent 
          ? { 
              ...s, 
              memo: memoValue || undefined,
              status: editingMemoStatus,
              earnedPoints: currentPoints,
              updatedAt: new Date()
            }
          : s
      )
    );
    
    // 변경사항 추적 (trackChange 함수 사용) - 과제 체크 모달의 최종 저장용
    trackChange(editingMemoStudent, { 
      memo: memoValue || undefined,
      status: editingMemoStatus,
      earnedPoints: currentPoints
    });
    
    // 디버깅용 로그
    console.log(`멤버 ${editingMemoStudent} 기록 변경 (로컬 저장):`, { 
      memo: memoValue, 
      status: editingMemoStatus,
      earnedPoints: currentPoints
    });
    
    // 개별 편집 모달 닫기 (초기 상태로 복원하지 않음)
    setIsMemoEditModalOpen(false);
    setEditingMemoStudent(null);
    setEditingMemoValue('');
    setEditingMemoStatus('pending');
    
    // 모바일에서 페이지 전체 스크롤 복원
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    }
  };

  // 멤버에게 메모가 있는지 확인 (MemoIndicator 표시용)
  const hasStudentMemo = (studentId: string): boolean => {
    const status = studentStatuses.find(s => s.studentId === studentId);
    return Boolean(status?.memo && status.memo.trim().length > 0);
  };
  // 다음 멤버 번호나 이름을 찾는 함수
  const findNextStudent = (currentInput: string): { display: string; id: string | null } => {
    console.log('=== findNextStudent 디버깅 시작 ===');
    console.log('입력값:', currentInput);
    console.log('입력값 타입:', typeof currentInput);
    console.log('입력값 길이:', currentInput?.length);
    console.log('전체 members:', members);
    console.log('members 길이:', members?.length);
    
    // 입력값이 비어있거나 공백만 있는 경우 첫 번째 멤버 반환
    if (!currentInput || !currentInput.trim()) {
      console.log('입력값이 비어있음, 첫 번째 멤버 반환');
      const firstStudent = members[0];
      console.log('첫 번째 멤버:', firstStudent);
      return firstStudent ? { display: firstStudent.number, id: firstStudent.id } : { display: '', id: null };
    }
    
    const trimmedInput = currentInput.trim();
    console.log('trimmed 입력값:', trimmedInput);
    
    // "0. 이름" 형식에서 번호와 이름 추출
    const numberNamePattern = /^(\d+)\.\s*(.+)$/;
    const match = trimmedInput.match(numberNamePattern);
    console.log('정규식 매치 결과:', match);
    
    if (match) {
      const [, numberStr, name] = match;
      console.log('번호와 이름 추출됨:', numberStr, name);
      
      // 번호로 다음 멤버 찾기
      const currentNumber = parseInt(numberStr);
      console.log('현재 번호:', currentNumber);
      const nextMember = members.find(m => parseInt(m.number) === currentNumber + 1);
      console.log('번호로 다음 멤버 찾기 결과:', nextMember);
      if (nextMember) {
        console.log('번호로 다음 멤버 찾음:', nextMember);
        return { display: `${nextMember.number}. ${nextMember.name}`, id: nextMember.id };
      }
      
      // 이름으로 다음 멤버 찾기
      const currentIndex = members.findIndex(m => m.name === name);
      console.log('이름으로 현재 멤버 인덱스:', currentIndex);
      if (currentIndex !== -1 && currentIndex < members.length - 1) {
        const nextStudent = members[currentIndex + 1];
        console.log('이름으로 다음 멤버 찾음:', nextStudent);
        return { display: `${nextStudent.number}. ${nextStudent.name}`, id: nextStudent.id };
      }
      
      console.log('번호나 이름으로 다음 멤버를 찾을 수 없음');
      return { display: '', id: null };
    }
    
    // 현재 입력이 번호인지 확인
    const isNumber = /^\d+$/.test(trimmedInput);
    console.log('입력값이 번호인가?', isNumber);
    
    if (isNumber) {
      // 번호인 경우 다음 번호 찾기
      const currentNumber = parseInt(trimmedInput);
      console.log('현재 번호:', currentNumber);
      const nextMember = members.find(m => parseInt(m.number) === currentNumber + 1);
      console.log('다음 번호 멤버:', nextMember);
      return nextMember ? { display: `${nextMember.number}. ${nextMember.name}`, id: nextMember.id } : { display: '', id: null };
    } else {
      // 이름인 경우 다음 멤버 찾기
      const currentIndex = members.findIndex(m => m.name === trimmedInput);
      console.log('현재 이름 인덱스:', currentIndex);
      if (currentIndex !== -1 && currentIndex < members.length - 1) {
        const nextStudent = members[currentIndex + 1];
        console.log('다음 멤버:', nextStudent);
        return { display: `${nextStudent.number}. ${nextStudent.name}`, id: nextStudent.id };
      }
      console.log('다음 멤버를 찾을 수 없음');
      return { display: '', id: null };
    }
  };
  // 이전 멤버으로 이동
  const handlePrevStudent = () => {
    if (!editingMemoStudent) return;
    
    // 현재 멤버의 변경 데이터를 임시 저장
    saveCurrentStudentChanges();
    
    const currentIndex = members.findIndex(m => m.id === editingMemoStudent);
    if (currentIndex > 0) {
      const prevStudent = members[currentIndex - 1];
      const prevStatus = studentStatuses.find(s => s.studentId === prevStudent.id);
      
      // 이전 멤버의 데이터로 모달 업데이트
      setEditingMemoStudent(prevStudent.id);
      setEditingMemoValue(prevStatus?.memo || '');
      setEditingMemoStatus(prevStatus?.status || 'pending');
    }
  };

  // 다음 멤버으로 이동
  const handleNextStudent = () => {
    if (!editingMemoStudent) return;
    
    // 현재 멤버의 변경 데이터를 임시 저장
    saveCurrentStudentChanges();
    
    const currentIndex = members.findIndex(m => m.id === editingMemoStudent);
    if (currentIndex < members.length - 1) {
      const nextStudent = members[currentIndex + 1];
      const nextStatus = studentStatuses.find(s => s.studentId === nextStudent.id);
      
      // 다음 멤버의 데이터로 모달 업데이트
      setEditingMemoStudent(nextStudent.id);
      setEditingMemoValue(nextStatus?.memo || '');
      setEditingMemoStatus(nextStatus?.status || 'pending');
    }
  };

  // 현재 멤버의 변경 데이터를 임시 저장 (네비게이션용)
  const saveCurrentStudentChanges = () => {
    if (!editingMemoStudent) return;
    
    const memoValue = editingMemoValue.trim();
    
    // 현재 멤버의 포인트 가져오기 (UI에서 표시되는 값)
    const currentPoints = studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints || 0;
    
    // 로컬 상태 업데이트 (메모, 상태, 포인트 모두 포함)
    setStudentStatuses(prev => 
      prev.map(s => 
        s.studentId === editingMemoStudent 
          ? { 
              ...s, 
              memo: memoValue || undefined,
              status: editingMemoStatus,
              earnedPoints: currentPoints,
              updatedAt: new Date()
            }
          : s
      )
    );
    
    // 변경사항 추적 (trackChange 함수 사용)
    trackChange(editingMemoStudent, { 
      memo: memoValue || undefined,
      status: editingMemoStatus,
      earnedPoints: currentPoints
    });
  };

  // 이전 멤버으로 이동 가능한지 확인
  const canNavigateToPrevStudent = (): boolean => {
    if (!editingMemoStudent) return false;
    const currentIndex = members.findIndex(m => m.id === editingMemoStudent);
    return currentIndex > 0;
  };

  // 다음 멤버으로 이동 가능한지 확인
  const canNavigateToNextStudent = (): boolean => {
    if (!editingMemoStudent) return false;
    const currentIndex = members.findIndex(m => m.id === editingMemoStudent);
    return currentIndex < members.length - 1;
  };

  // 과제 체크 입력 처리 (임시 상태 변경)
  const handleCheckSubmit = (shouldClear: boolean = true) => {
    // 마우스 클릭 제출 시 자동 채움(useEffect)로 인한 깜빡임 방지
    if (shouldClear) {
      setIsSubmittingViaClick(true);
      setIsUserTyping(true);
    }
    // 🎯 현재 상태를 저장해두고 나중에 사용 (마우스 클릭 중 상태 변경 방지)
    const currentCheckInput = checkInput.trim();
    const currentSelectedStudents = new Set(selectedStudents);
    
    console.log('🎯 입력 버튼 클릭:', {
      checkInput: currentCheckInput,
      selectedStudentsSize: currentSelectedStudents.size,
      timestamp: new Date().toISOString()
    });
    
    if (currentCheckInput) {
      console.log('🎯 입력 버튼 클릭: 대상 필드에서 멤버 찾기 및 상태 적용 시작');
      
      let targetStudent: ClassMember | null = null;
      
      // 1순위: 입력값으로 멤버 찾기 (번호 또는 이름 2글자 이상)
      targetStudent = findStudentByInput(currentCheckInput);
      console.log('🎯 입력값으로 멤버 찾음:', targetStudent?.name);
      
      // 2순위: 입력값으로 못 찾았을 때만 선택된 멤버 사용
      if (!targetStudent && currentSelectedStudents.size > 0) {
        const selectedStudentId = Array.from(currentSelectedStudents)[0];
        targetStudent = members.find(m => m.id === selectedStudentId) || null;
        console.log('🎯 입력값 매칭 실패 → 선택된 멤버 사용:', targetStudent?.name);
      }
      
      if (!targetStudent) {
        alert('해당하는 멤버를 찾을 수 없습니다.');
        return;
      }
      
      applyStatusToStudent(targetStudent, true, shouldClear);
      return;
    }
    
    // 선택된 멤버가 있는 경우 일괄 처리
    if (currentSelectedStudents.size > 0) {
      console.log('🎯 선택된 멤버들에게 상태 적용');
      applyStatusToSelectedStudents(shouldClear);
    }
    // 제출 후 상태 플래그 복구 (안전망)
    if (shouldClear) {
      setTimeout(() => {
        setIsSubmittingViaClick(false);
        setIsUserTyping(false);
      }, 120);
    }
  };

  // 상태를 선택된 멤버들에게 적용하는 함수
  const applyStatusToSelectedStudents = (shouldClear: boolean = true) => {
      // 포인트 설정: 포인트 입력이 활성화된 경우 입력값 사용, 그렇지 않으면 과제 상태에 따른 기본 포인트 사용
      let finalPoints: number;
      if (isPointsEnabled && pointsInput && pointsInput.trim() !== '' && !isNaN(Number(pointsInput))) {
        // 포인트 입력이 활성화되고 유효한 값이 입력된 경우
        finalPoints = Number(pointsInput);
      } else {
        // 포인트 입력이 비활성화된 경우 과제 상태에 따른 기본 포인트 사용
        switch (selectedStatus) {
          case 'pending':
            finalPoints = 0; // 미확인: 0 포인트
            break;
          case 'confirmed':
            finalPoints = getBasePoints(); // 확인: 과제 기본 포인트
            break;
          case 'delayed':
            finalPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
            break;
          case 'exempted':
            finalPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
            break;
          case 'submitted':
            finalPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
            break;
          default:
            finalPoints = 0;
        }
      }

              // 임시 상태로 변경 (저장 버튼을 눌러야 실제 반영)
        setStudentStatuses(prev => 
          prev.map(status => 
            selectedStudents.has(status.studentId)
              ? {
                  ...status,
                  status: selectedStatus,
                  earnedPoints: finalPoints,
                  memo: isMemoEnabled && memoInput.trim() ? memoInput : status.memo,
                  updatedAt: new Date(),
                  confirmedAt: selectedStatus === 'confirmed' ? new Date() : undefined
                }
              : status
          )
        );
      
    // 변경사항 추적에 추가
    selectedStudents.forEach(studentId => {
      const currentStatus = studentStatuses.find(s => s.studentId === studentId);
      trackChange(studentId, {
        status: selectedStatus,
        earnedPoints: finalPoints,
        memo: isMemoEnabled && memoInput.trim() ? memoInput : currentStatus?.memo,
        confirmedAt: selectedStatus === 'confirmed' ? new Date() : undefined
      });
    });

    // 성공 메시지 (alert 대신)
    console.log(`${selectedStudents.size}명의 멤버에게 임시 적용되었습니다. (저장 버튼을 눌러야 실제 반영)`);
    
    // 🎯 연속 토글 처리 (return 전에 실행!)
    console.log('🔍 연속 토글 상태 확인:', { 
      isAutoIncrementEnabled, 
      checkInput, 
      membersLength: members?.length,
      연속토글_활성화됨: isAutoIncrementEnabled ? 'YES' : 'NO'
    });
    
    if (isAutoIncrementEnabled) {
      console.log('=== 연속 토글 처리 시작 ===');
      console.log('현재 입력값:', checkInput);
      
      // 🎯 > 버튼 기능(handleNextStudentInCheckModal)을 자동 실행
      console.log('🎯 다음 멤버으로 자동 이동 시작');
      handleNextStudentInCheckModal();
      console.log('🎯 다음 멤버으로 자동 이동 완료');
      
      // 🎯 연속 토글: isUserTyping을 true로 설정하여 useEffect의 setCheckInput 실행 방지
      setIsUserTyping(true);
      console.log('🎯 연속 토글: isUserTyping을 true로 설정하여 useEffect 방지');
    } else {
      console.log('❌ 연속 토글이 비활성화됨');
    }
    
    // 🎯 연속 토글 여부에 따른 선택 처리
    if (isAutoIncrementEnabled) {
      // 연속 토글 활성화: 다음 번호 멤버 선택 (이미 위에서 처리됨)
      console.log('🎯 연속 토글 활성화 - 다음 번호 멤버 선택됨');
    } else {
      console.log('❌ 연속 토글이 비활성화됨 - 입력 필드 초기화');
      
      if (shouldClear) {
        // 🎯 상태 적용 후 필드 초기화 (마우스 클릭 시에만)
        setTimeout(() => {
          setSelectedStudents(new Set());
          setCheckInput('');
          console.log('🎯 연속 토글 비활성화 - 모든 멤버 선택 해제 및 입력 필드 초기화됨');
          
          // 포커스 제거
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
            console.log('🎯 연속 토글 비활성화 - 모든 포커스 제거됨');
          }
          // 깜빡임 방지용 플래그 해제
          setIsUserTyping(false);
        }, 50);
      }
    }
    
    // 메모 입력 필드 초기화
    if (isMemoEnabled) {
      setMemoInput('');
    }
    
    // 🎯 모든 포커스 제거 - 깔끔하게 초기화 (마우스 클릭 시에만)
    if (shouldClear) {
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
        console.log('🎯 모든 포커스 제거됨');
      }
    }
  };

  // 변경사항 추적 함수
  const trackChange = (studentId: string, changes: Partial<AssignmentStudentStatus>) => {
    setChangeTracker(prev => {
      // 현재 멤버의 기존 상태 가져오기
      const currentStatus = studentStatuses.find(s => s.studentId === studentId);
      
      return {
        ...prev,
        [studentId]: {
          ...currentStatus, // 기존 상태 전체를 먼저 복사
          ...prev[studentId], // 이전 변경사항 유지
          ...changes, // 새로운 변경사항 적용
          updatedAt: new Date()
        }
      };
    });
  };

  // 메모 입력에서 Enter 시: 현재 메모 적용 후 다음 멤버로 이동 (Alt+Enter는 줄바꿈)
  const applyMemoAndGoToNextMember = useCallback(() => {
    const memoValue = memoInput.trim() || undefined;
    if (selectedStudents.size > 0) {
      const updatedStatuses = [...studentStatuses];
      selectedStudents.forEach(studentId => {
        const idx = updatedStatuses.findIndex(s => s.studentId === studentId);
        if (idx >= 0) {
          updatedStatuses[idx] = { ...updatedStatuses[idx], memo: memoValue };
        } else {
          updatedStatuses.push({
            id: `${studentId}_${currentAssignment?.id || 'temp'}`,
            studentId,
            assignmentId: currentAssignment?.id || 'temp',
            status: 'pending',
            earnedPoints: getBasePoints(),
            memo: memoValue,
            updatedAt: new Date()
          });
        }
        trackChange(studentId, { memo: memoValue });
      });
      setStudentStatuses(updatedStatuses);
    }
    let currentIndex = -1;
    if (selectedStudents.size >= 1) {
      const indices = Array.from(selectedStudents).map(id => members.findIndex(m => m.id === id)).filter(i => i >= 0);
      currentIndex = indices.length > 0 ? Math.max(...indices) : -1;
    } else if (currentSelectedStudentId) {
      currentIndex = members.findIndex(m => m.id === currentSelectedStudentId);
    }
    const nextIndex = currentIndex + 1;
    if (nextIndex < members.length) {
      const nextMember = members[nextIndex];
      setSelectedStudents(new Set([nextMember.id]));
      setCurrentSelectedStudentId(nextMember.id);
      setCheckInput(`${nextMember.number}. ${nextMember.name}`);
      const nextStatus = studentStatuses.find(s => s.studentId === nextMember.id);
      setMemoInput(nextStatus?.memo ?? '');
      setTimeout(() => memoInputRef.current?.focus(), 0);
    }
  }, [selectedStudents, studentStatuses, memoInput, currentAssignment, currentSelectedStudentId, members, getBasePoints, trackChange]);

  // 최종 저장 함수 (서버)
  const handleFinalSave = async (options?: { closeParentModalAfter?: boolean }) => {
    setIsFinalSaveInProgress(true);
    try {
      // 메모 보기 모드에서 변경된 메모를 확인하고 변경사항 추적
      const memoChangedStudentIds = new Set<string>();
      
      if (Object.keys(memoViewMemos).length > 0) {
        // 변경사항이 있는 메모만 추적
        members.forEach((member) => {
          const isAssigned = getAssignedStudentIds().includes(member.id);
          if (!isAssigned) return;
          
          const newMemo = memoViewMemos[member.id];
          const currentStatus = studentStatuses.find(s => s.studentId === member.id);
          const initialStatus = initialStudentStatuses.find(s => s.studentId === member.id);
          
          // 새로 추가된 학생이거나 메모가 변경된 경우
          const newMemoValue = newMemo !== undefined ? (newMemo.trim() || undefined) : undefined;
          const initialMemoValue = initialStatus?.memo || undefined;
          
          if (!currentStatus) {
            // 새로 추가된 학생
            if (newMemoValue) {
              const newStatus: AssignmentStudentStatus = {
                id: `${member.id}_${currentAssignment?.id || 'temp'}`,
                studentId: member.id,
                assignmentId: currentAssignment?.id || 'temp',
                status: 'pending',
                earnedPoints: getBasePoints(),
                memo: newMemoValue,
                updatedAt: new Date()
              };
              setStudentStatuses(prev => [...prev, newStatus]);
              trackChange(member.id, { memo: newMemoValue });
              memoChangedStudentIds.add(member.id);
            }
          } else {
            // 기존 학생의 메모 변경 확인
            if (newMemoValue !== initialMemoValue) {
              // 변경사항 추적
              trackChange(member.id, { memo: newMemoValue });
              // studentStatuses 업데이트
              setStudentStatuses(prev => 
                prev.map(s => 
                  s.studentId === member.id 
                    ? { ...s, memo: newMemoValue, updatedAt: new Date() }
                    : s
                )
              );
              memoChangedStudentIds.add(member.id);
            }
          }
        });
      }
      
      // 변경사항 추적기를 기반으로 저장할 멤버들 선택
      // trackChange가 비동기적으로 처리되므로 약간의 지연 후 확인
      await new Promise(resolve => setTimeout(resolve, 10));
      
      const changedStudentIds = Object.keys(changeTracker);
      const submissionChangedStudentIds = Object.keys(submissionChangeTracker);
      
      // 모든 변경사항을 합침 (메모 변경사항 포함)
      const allChangedStudentIds = Array.from(new Set([
        ...changedStudentIds, 
        ...submissionChangedStudentIds,
        ...Array.from(memoChangedStudentIds)
      ]));
      
      if (allChangedStudentIds.length === 0) {
        // 변경사항이 없는 경우 메시지 팝업 표시
        setIsNoChangesModalOpen(true);
        return;
      }
      
      // 변경사항이 있는 멤버들의 현재 상태만 저장 (서버용)
      const statusesToSave: any[] = [];
      allChangedStudentIds.forEach(studentId => {
        let currentStatus = studentStatuses.find(s => s.studentId === studentId);
        
        // studentStatuses에 없는 경우 (새로 추가된 학생) changeTracker에서 생성
        if (!currentStatus) {
          const trackedChanges = changeTracker[studentId];
          if (trackedChanges) {
            currentStatus = {
              id: `${studentId}_${currentAssignment?.id || 'temp'}`,
              studentId: studentId,
              assignmentId: currentAssignment?.id || 'temp',
              status: trackedChanges.status || 'pending',
              earnedPoints: trackedChanges.earnedPoints !== undefined ? trackedChanges.earnedPoints : getBasePoints(),
              memo: trackedChanges.memo,
              updatedAt: new Date()
            } as AssignmentStudentStatus;
          }
        }
        
        if (currentStatus) {
          const statusToSave = { ...currentStatus };
          
          // 메모 보기 모드에서 변경된 메모 값이 있으면 적용 (memoViewMemos의 최신 값 사용)
          if (memoViewMemos[studentId] !== undefined) {
            const newMemoValue = memoViewMemos[studentId].trim() || undefined;
            statusToSave.memo = newMemoValue;
            console.log(`📝 멤버 ${studentId}의 메모 값 적용:`, newMemoValue);
          }
          
          // changeTracker에서 메모 값이 있으면 적용 (memoViewMemos보다 우선)
          // memo: undefined(삭제)도 반영해야 하므로 hasOwnProperty로 확인
          if (changeTracker[studentId] && Object.prototype.hasOwnProperty.call(changeTracker[studentId], 'memo')) {
            statusToSave.memo = changeTracker[studentId].memo;
            console.log(`📝 멤버 ${studentId}의 changeTracker 메모 값 적용:`, changeTracker[studentId].memo);
          }
          
          // 제출 확인 데이터가 캐시에 있으면 적용
          const cachedSubmission = submissionChangeTracker[studentId];
          if (cachedSubmission) {
            console.log(`🔍 멤버 ${studentId}의 캐시된 제출 확인 데이터:`, cachedSubmission);
            if (cachedSubmission.isDeleted) {
              // 삭제된 경우 undefined로 설정
              statusToSave.submissionLink = undefined;
              statusToSave.submissionMessage = undefined;
              statusToSave.submittedAt = undefined;
              console.log(`🗑️ 삭제 처리: submissionLink, submissionMessage, submittedAt = undefined`);
            } else {
              // 변경된 경우 새 값으로 설정
              statusToSave.submissionLink = cachedSubmission.link || undefined;
              statusToSave.submissionMessage = cachedSubmission.message || undefined;
              console.log(`✏️ 수정 처리:`, {
                submissionLink: statusToSave.submissionLink,
                submissionMessage: statusToSave.submissionMessage
              });
            }
          }
          
          console.log(`📤 멤버 ${studentId}의 최종 저장 데이터:`, {
            submissionLink: statusToSave.submissionLink,
            submissionMessage: statusToSave.submissionMessage,
            status: statusToSave.status,
            earnedPoints: statusToSave.earnedPoints,
            memo: statusToSave.memo
          });
          
          statusesToSave.push(statusToSave);
        }
      });
      
      console.log('저장할 멤버 ID들:', statusesToSave.map(s => s.studentId));
      console.log('변경사항 추적기:', changeTracker);
      console.log('제출 확인 변경사항:', submissionChangeTracker);
      console.log('저장할 데이터 상세:', statusesToSave);
      
              // =========================
              // 레벨업/레벨다운 감지 준비
              // - 과제 상태 저장 시점에 "총점" 변화로 레벨 변화가 생기면
              //   보상 동기화 + 토스트 표시를 1회만 수행
              // =========================
              let levelChangeToastLines: string[] = [];

              const shouldCheckLevelForStudent = (studentId: string) => {
                const currentStatus = studentStatuses.find(s => s.studentId === studentId);
                const savedStatus = statusesToSave.find(s => s.studentId === studentId);
                if (!currentStatus || !savedStatus) return false;
                const oldP = typeof currentStatus.earnedPoints === 'number' ? currentStatus.earnedPoints : 0;
                const newP = typeof savedStatus.earnedPoints === 'number' ? savedStatus.earnedPoints : 0;
                return oldP !== newP;
              };

              const levelCheckStudentIds = statusesToSave
                .map(s => s.studentId)
                .filter((id): id is string => !!id)
                .filter((id, idx, arr) => arr.indexOf(id) === idx)
                .filter(shouldCheckLevelForStudent);
      
              // 서버에 저장
        if (onSave) {
          // 변경된 멤버들의 상태와 현재 선택된 날짜를 onSave로 전달
          // 루틴 과제 모드에서는 ref를 사용하여 최신 날짜 값 보장 (클로저 문제 방지)
          let dateToSave: Date | undefined;
          if (mode === 'routine') {
            // 추가 날짜는 routineDates 인덱스와 어긋날 수 있음 → 화면에 맞춘 currentDateRef를 단일 기준으로 저장
            const cur = currentDateRef.current;
            dateToSave = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate());
            console.log('💾 루틴 과제 저장 - currentDateRef 기준:', {
              dateToSave,
              dateId: `${dateToSave.getFullYear()}-${String(dateToSave.getMonth() + 1).padStart(2, '0')}-${String(dateToSave.getDate()).padStart(2, '0')}`,
            });
          } else {
            dateToSave = selectedDate || currentDate;
          }
          console.log('💾 최종 저장할 날짜:', dateToSave);
          await onSave(statusesToSave, dateToSave);

          // 일반 과제: 저장 시 서버에서 보상 지급 ID가 갱신되나 assignment prop은 그대로일 수 있음 → 즉시 동기화
          const generalAss = mode === 'general' ? assignment : undefined;
          const shouldSyncRewardGrantedIds =
            !!userId &&
            !!classId &&
            !!generalAss?.id &&
            generalAss.rewardEnabled === true &&
            ((generalAss.rewardCoins ?? 0) >= 1 || (generalAss.rewardItemCardIds?.length ?? 0) > 0);
          if (shouldSyncRewardGrantedIds) {
            try {
              const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
              const fresh = await consolidatedService.loadGeneralAssignment(generalAss.id);
              setGeneralRewardGrantedOverride(fresh?.rewardGrantedStudentIds ? [...fresh.rewardGrantedStudentIds] : []);
            } catch (e) {
              console.warn('보상 지급 목록 동기화 실패:', e);
            }
          }
        }
        
        // 저장 성공 후 처리
        console.log('서버 저장 완료:', statusesToSave);

        // ✅ 저장 완료 UI는 먼저 띄우고(체감 속도 개선) — 닫기 경고에서「저장하고 닫기」면 과제체크 모달만 닫음
        if (options?.closeParentModalAfter) {
          onClose();
        } else {
          setIsSaveSuccessModalOpen(true);
        }

        // =========================
        // 레벨업/레벨다운 감지 + 보상 동기화 + 토스트
        // - "포인트 조회 페이지" 집계 방식과 일치하도록,
        //   (MembersPoint 포인트 합계) + (membersPoints earnedPoints 합계) 기반으로 계산
        // =========================
        void (async () => {
          // 레벨업/레벨다운 감지 + 보상 동기화 (백그라운드)
          if (userId && classId && levelCheckStudentIds.length > 0) {
            try {
              const { PointTypeManagementService } = await import('../services/pointTypeManagementService');
              const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');
              const { PointsQueryService } = await import('../services/pointsQueryService');

              const pointTypeService = new PointTypeManagementService(userId, classId);
              const pointTypeData = await pointTypeService.getPointTypeData();
              const levelUpPoints = pointTypeData.levelUpPoints || 50;

              const pointsQueryService = new PointsQueryService(userId, classId, memberGroup);

              const calcLevel = (totalPoints: number) => Math.floor((totalPoints || 0) / levelUpPoints);

              // ⚠️ 비용이 큰 전체 집계. UI는 이미 저장완료를 띄운 상태에서 수행.
              const pointsTableData = await pointsQueryService.getPointsTableData(members);

              const results = await Promise.all(
                levelCheckStudentIds.map(async (studentId) => {
                  const member = members.find(m => m.id === studentId);
                  const studentLabel = member ? `${member.number}번 ${member.name}` : studentId;

                  const row = pointsTableData.students.find(s => s.studentId === studentId);
                  const newTotal = row?.totalPoints || 0;
                  const newLevel = calcLevel(newTotal);

                  // onSave(과제 포인트 동기화)가 먼저 currentLevel을 갱신할 수 있어, DB currentLevel 비교는 신뢰할 수 없음
                  const currentStatus = studentStatuses.find(s => s.studentId === studentId);
                  const savedStatus = statusesToSave.find(s => s.studentId === studentId);
                  const oldEarned =
                    typeof currentStatus?.earnedPoints === 'number' ? currentStatus.earnedPoints : 0;
                  const newEarned =
                    typeof savedStatus?.earnedPoints === 'number' ? savedStatus.earnedPoints : 0;
                  const earnedDelta = newEarned - oldEarned;
                  const oldTotal = Math.max(0, newTotal - earnedDelta);
                  const oldLevel = calcLevel(oldTotal);

                  if (oldLevel !== newLevel) {
                    await syncLevelUpRewardsByPoints(
                      userId,
                      classId,
                      studentId,
                      oldTotal,
                      newTotal,
                      levelUpPoints,
                      memberGroup,
                      oldLevel,
                      newLevel
                    );
                  }

                  return { studentLabel, oldLevel, newLevel };
                })
              );

              const levelLines: string[] = [];
              results.forEach(({ studentLabel, oldLevel, newLevel }) => {
                if (newLevel > oldLevel) levelLines.push(`${studentLabel} 레벨업: Lv.${oldLevel} → Lv.${newLevel}`);
                else if (newLevel < oldLevel) levelLines.push(`${studentLabel} 레벨다운: Lv.${oldLevel} → Lv.${newLevel}`);
              });

              if (levelLines.length > 0) {
                setToastMessage(levelLines.join('\n'));
                setShowToast(true);
              }
            } catch (error) {
              console.error('레벨업/레벨다운 감지/동기화 실패:', error);
            }
          }

          // 완료 보상 처리 (일반 과제만) - 직렬 처리 → 병렬 처리로 총 소요시간 단축
          const completionRewards = assignment?.completionRewards;
          if (mode === 'general' && completionRewards && completionRewards.length > 0 && userId && classId && assignment) {
            try {
              const rewardService = new AssignmentRewardService(userId, classId);
              const changed = statusesToSave.filter((status) => {
                const originalStatus = initialStudentStatuses.find(s => s.studentId === status.studentId);
                const oldStatus = originalStatus?.status || 'pending';
                return oldStatus !== status.status;
              });

              await Promise.all(
                changed.map((status) =>
                  rewardService.checkAndProcessRewards(
                    assignment.id,
                    status.studentId,
                    status.status,
                    completionRewards
                  )
                )
              );
            } catch (error) {
              console.error('❌ 완료 보상 처리 실패:', error);
            }
          }
        })();
        
        // 변경사항 추적기 초기화
        setChangeTracker({});
        
        // 제출 확인 변경사항 캐시 초기화
        setSubmissionChangeTracker({});
        
        // 저장 성공 후에만 pendingCount 업데이트
        updatePendingCountAfterSave();
        
        // 프로젝트 과제 모드일 때 하위 과제 변경 시 데이터 다시 로드
        if (mode === 'project' && onSubTaskChange && currentSubTaskIndex !== undefined) {
          console.log('🔄 프로젝트 과제 저장 후 하위 과제 데이터 다시 로드');
          onSubTaskChange(currentSubTaskIndex);
        }
        
        console.log('저장 완료');
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsFinalSaveInProgress(false);
    }
  };
  
  // 저장 완료 후 편집 계속
  const handleContinueEditing = () => {
    setIsSaveSuccessModalOpen(false);
    
    // 저장된 현재 상태를 초기 값으로 설정 (변경사항 감지 기준점 업데이트)
    setInitialStudentStatuses([...studentStatuses]);
    
    // 변경사항 추적기 초기화 (이미 handleFinalSave에서 초기화되지만 확실하게)
    setChangeTracker({});
    setSubmissionChangeTracker({});
    
    // 메모 보기 모드의 메모 값도 현재 상태로 초기화
    const memos: Record<string, string> = {};
    studentStatuses.forEach(status => {
      memos[status.studentId] = status.memo || '';
    });
    setMemoViewMemos(memos);
    
    console.log('✅ 편집계속: 현재 상태를 초기 값으로 설정 완료');
  };
  
  // 저장 완료 후 모달 닫기
  const handleCloseAfterSave = () => {
    setIsSaveSuccessModalOpen(false);
    onClose();
  };
  
  // 변경사항이 있는지 확인하는 함수
  const hasUnsavedChanges = useCallback(() => {
    // changeTracker에 변경사항이 있는지 확인
    if (Object.keys(changeTracker).length > 0) {
      return true;
    }
    
    // submissionChangeTracker에 변경사항이 있는지 확인
    if (Object.keys(submissionChangeTracker).length > 0) {
      return true;
    }
    
    // memoViewMemos에 변경사항이 있는지 확인
    if (isMemoViewMode && Object.keys(memoViewMemos).length > 0) {
      // memoViewMemos와 initialStudentStatuses 비교
      for (const [studentId, memo] of Object.entries(memoViewMemos)) {
        const initialStatus = initialStudentStatuses.find(s => s.studentId === studentId);
        if (initialStatus && initialStatus.memo !== memo) {
          return true;
        }
      }
    }
    
    // studentStatuses와 initialStudentStatuses 비교
    const hasStatusChanges = studentStatuses.some(status => {
      const originalStatus = initialStudentStatuses.find(s => s.studentId === status.studentId);
      if (!originalStatus) return true;
      return (
        status.status !== originalStatus.status ||
        status.earnedPoints !== originalStatus.earnedPoints ||
        status.memo !== originalStatus.memo ||
        status.notes !== originalStatus.notes
      );
    });
    
    return hasStatusChanges;
  }, [changeTracker, submissionChangeTracker, memoViewMemos, isMemoViewMode, studentStatuses, initialStudentStatuses]);
  
  // 모달 닫기 핸들러 (변경사항 확인)
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setIsCloseConfirmModalOpen(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);
  
  // 경고 모달: 저장 없이 과제체크 모달까지 닫기
  const handleDiscardAndClose = useCallback(() => {
    setIsCloseConfirmModalOpen(false);
    onClose();
  }, [onClose]);

  // 경고 모달: 저장 후 과제체크 모달 닫기 (저장 완료 서브 팝업 없이)
  const handleSaveAndCloseFromWarning = async () => {
    setIsCloseConfirmModalOpen(false);
    await handleFinalSave({ closeParentModalAfter: true });
  };

  // 경고 모달 바깥(오버레이) 클릭: 경고만 닫고 편집 유지
  const handleCloseWarningOnly = useCallback(() => {
    setIsCloseConfirmModalOpen(false);
  }, []);
  
  // 선택된 멤버들의 메모 삭제 함수
  const handleDeleteSelectedMemos = () => {
    // 선택된 멤버들 중 메모가 있는 멤버들만 필터링
    const studentsWithMemos = Array.from(selectedStudents).filter(studentId => {
      const status = studentStatuses.find(s => s.studentId === studentId);
      const memoViewMemo = memoViewMemos[studentId];
      return (status && status.memo && status.memo.trim().length > 0) || 
             (memoViewMemo && memoViewMemo.trim().length > 0);
    });
    
    if (studentsWithMemos.length === 0) {
      alert('선택된 멤버 중 메모가 있는 멤버가 없습니다.');
      setIsMemoDeleteModalOpen(false);
      return;
    }
    
    setStudentStatuses(prev => 
      prev.map(s => 
        selectedStudents.has(s.studentId) && s.memo && s.memo.trim().length > 0
          ? {
              ...s,
              memo: undefined,
              updatedAt: new Date()
            }
          : s
      )
    );
    
    // 메모 보기 모드에서도 메모 삭제
    if (isMemoViewMode) {
      setMemoViewMemos(prev => {
        const updated = { ...prev };
        selectedStudents.forEach(studentId => {
          updated[studentId] = '';
        });
        return updated;
      });
    }
    
    // 일반 메모 입력 필드도 초기화 (선택된 학생 중 하나가 현재 입력 필드에 표시되고 있는 경우)
    if (selectedStudents.size === 1) {
      const selectedStudentId = Array.from(selectedStudents)[0];
      const status = studentStatuses.find(s => s.studentId === selectedStudentId);
      if (status && status.memo && status.memo.trim().length > 0) {
        setMemoInput('');
      }
    } else if (selectedStudents.size > 1) {
      // 복수 선택 시에도 초기화
      setMemoInput('');
    }
    
    // 선택된 멤버들의 메모 삭제를 변경사항으로 추적
    studentsWithMemos.forEach(studentId => {
      trackChange(studentId, { memo: undefined });
    });
    
    // 메모 삭제 모달 닫기
    setIsMemoDeleteModalOpen(false);
    
    // 선택된 멤버들 모두 선택 취소
    setSelectedStudents(new Set());
    
    console.log(`${studentsWithMemos.length}명의 멤버의 메모가 삭제되었습니다.`);
  };



  // 멤버 상태 직접 변경
  const handleStudentStatusChange = (studentId: string, newStatus: AssignmentStatusValue) => {
    // 현재 상태 확인
    const currentStatus = studentStatuses.find(s => s.studentId === studentId);
    const isRevertingToPending = currentStatus && currentStatus.status !== 'pending' && newStatus === 'pending';
    
    // 미확인 상태로 되돌리는 경우 사용자 확인
    if (isRevertingToPending) {
      const confirmed = window.confirm(
        '미확인 상태로 되돌리면 입력한 메모와 포인트가 모두 삭제됩니다.\n계속하시겠습니까?'
      );
      if (!confirmed) return;
    }
    
    // 과제 상태에 따른 포인트 자동 설정
    let newPoints: number;
    let newMemo: string | undefined;
    let newNotes: string | undefined;
    
    switch (newStatus) {
      case 'pending':
        newPoints = 0; // 미확인: 0 포인트
        newMemo = undefined; // 메모 삭제
        newNotes = undefined; // 노트 삭제
        break;
      case 'confirmed':
        newPoints = getBasePoints(); // 확인: 과제 기본 포인트
        newMemo = currentStatus?.memo; // 기존 메모 유지
        newNotes = currentStatus?.notes; // 기존 노트 유지
        break;
      case 'delayed':
        newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
        newMemo = currentStatus?.memo; // 기존 메모 유지
        newNotes = currentStatus?.notes; // 기존 노트 유지
        break;
      case 'exempted':
        newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
        newMemo = currentStatus?.memo; // 기존 메모 유지
        newNotes = currentStatus?.notes; // 기존 노트 유지
        break;
      case 'submitted':
        newPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
        newMemo = currentStatus?.memo; // 기존 메모 유지
        newNotes = currentStatus?.notes; // 기존 노트 유지
        break;
      default:
        newPoints = 0;
        newMemo = currentStatus?.memo;
        newNotes = currentStatus?.notes;
    }

    setStudentStatuses(prev => 
      prev.map(s => 
        s.studentId === studentId 
          ? { 
              ...s, 
              status: newStatus, 
              earnedPoints: newPoints, // 포인트 자동 설정
              memo: newMemo, // 메모 상태에 따라 설정
              notes: newNotes, // 노트 상태에 따라 설정
              updatedAt: new Date(),
              confirmedAt: newStatus === 'confirmed' ? new Date() : undefined
            }
          : s
      )
    );
    
    // 변경사항 추적
    trackChange(studentId, {
      status: newStatus,
      earnedPoints: newPoints, // 포인트도 추적
      memo: newMemo, // 메모 상태도 추적
      notes: newNotes, // 노트 상태도 추적
      confirmedAt: newStatus === 'confirmed' ? new Date() : undefined
    });
    
    console.log(`📝 멤버 ${studentId} 상태 변경: ${currentStatus?.status} → ${newStatus}`, {
      isRevertingToPending,
      newPoints,
      newMemo,
      newNotes
    });
  };

  // 포인트 직접 변경
  const handlePointsChange = (studentId: string, newPoints: number) => {
    setStudentStatuses(prev => 
      prev.map(s => 
        s.studentId === studentId 
          ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
          : s
      )
    );
    
    // 변경사항 추적
    trackChange(studentId, { earnedPoints: newPoints });
  };



  // 멤버 선택/해제 처리
  const handleStudentSelect = (studentId: string) => {
    console.log(`handleStudentSelect(${studentId}):`, {
      mode,
      currentAssignmentType: currentAssignment?.constructor?.name,
      isAssigned: isStudentAssigned(studentId)
    });
    
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }

      // 규칙 적용
      // - 연속모드가 아닐 때: 단일 선택이면 대상 필드에 "번호. 이름" 자동 입력
      // - 연속모드일 때: 선택 해제(0명) 시 대상 필드 초기화, 단일 선택 시에도 대상과 일치하도록 동기화
      try {
        if (newSet.size === 0) {
          if (isAutoIncrementEnabled) {
            setCheckInput('');
          }
        } else if (newSet.size === 1) {
          const onlyId = Array.from(newSet)[0];
          const student = members.find(m => m.id === onlyId);
          if (student) {
            const displayText = `${student.number}. ${student.name}`;
            if (!isAutoIncrementEnabled) {
              // 비연속 모드: 단일 선택 자동 입력
              setCheckInput(displayText);
            } else {
              // 연속 모드: 기본적으로 대상과 선택을 일치시킴
              setCheckInput(displayText);
            }
          }
          // 단일 선택 시 해당 학생의 메모를 메모 입력 필드에 로드 (편집 가능 모드)
          setIsHoveringMemo(false); // 호버 모드 해제하여 편집 가능하게
          const status = studentStatuses.find(s => s.studentId === onlyId);
          if (status?.memo && status.memo.trim()) {
            setMemoInput(status.memo);
          } else {
            setMemoInput('');
          }
        } else {
          // 복수 선택 시 메모 입력 필드 초기화 (공통 메모 입력을 위해)
          setIsHoveringMemo(false); // 호버 모드 해제하여 편집 가능하게
          setMemoInput('');
        }
        if (newSet.size === 0) {
          // 선택 해제 시 메모 입력 필드 초기화
          setMemoInput('');
        }
      } catch (e) {
        console.warn('handleStudentSelect sync warning:', e);
      }

      return newSet;
    });

  };

  // 전체 선택 처리
  const handleSelectAll = () => {
    if (mode === 'routine') {
      // 루틴 과제의 경우 모든 멤버를 선택
      const allStudentIds = members.map(member => member.id);
      setSelectedStudents(new Set(allStudentIds));
    } else {
      // 일반 과제의 경우 할당된 멤버만 선택
      const allAssignedStudentIds = members
        .filter(member => isStudentAssigned(member.id))
        .map(member => member.id);
      setSelectedStudents(new Set(allAssignedStudentIds));
    }
  };

  // 전체 선택 해제 처리
  const handleDeselectAll = () => {
    setSelectedStudents(new Set());
  };

  // 일반 과제 보상 지급 핸들러
  const handleGeneralAssignmentRewardGrant = useCallback(async () => {
    if (!currentAssignment || !userId || !classId || selectedStudents.size === 0) {
      return;
    }

    try {
      const rewardService = new GeneralAssignmentRewardService(userId, classId);
      const result = await rewardService.grantRewardsToStudents(
        currentAssignment.id,
        Array.from(selectedStudents)
      );

      if (result.success) {
        // 토스트 메시지 표시
        setToastMessage(result.message);
        setShowToast(true);
        
        if (assignment) {
          setGeneralRewardGrantedOverride((prev) => {
            const base = prev ?? assignment.rewardGrantedStudentIds ?? [];
            const updatedIds = new Set(base);
            Array.from(selectedStudents).forEach(id => updatedIds.add(id));
            return Array.from(updatedIds);
          });
        }
        
        // 선택 해제
        setSelectedStudents(new Set());
        
        // 과제 데이터 새로고침
        if (onSave) {
          await onSave(studentStatuses);
        }
      } else {
        setToastMessage(result.message);
        setShowToast(true);
      }
    } catch (error) {
      console.error('일반 과제 보상 지급 실패:', error);
      setToastMessage('보상 지급 중 오류가 발생했습니다.');
      setShowToast(true);
    }
  }, [currentAssignment, assignment, userId, classId, selectedStudents, onSave, studentStatuses]);

  // 일반 과제 보상 회수 핸들러
  const handleGeneralAssignmentRewardRevoke = useCallback(async () => {
    if (!currentAssignment || !userId || !classId || selectedStudents.size === 0) {
      return;
    }

    try {
      const rewardService = new GeneralAssignmentRewardService(userId, classId);
      const result = await rewardService.revokeRewardsFromStudents(
        currentAssignment.id,
        Array.from(selectedStudents)
      );

      if (result.success) {
        // 토스트 메시지 표시
        setToastMessage(result.message);
        setShowToast(true);
        
        if (assignment) {
          setGeneralRewardGrantedOverride((prev) => {
            const base = prev ?? assignment.rewardGrantedStudentIds ?? [];
            const currentIds = new Set(base);
            Array.from(selectedStudents).forEach(id => currentIds.delete(id));
            return Array.from(currentIds);
          });
        }
        
        // 선택 해제
        setSelectedStudents(new Set());
        
        // 과제 데이터 새로고침
        if (onSave) {
          await onSave(studentStatuses);
        }
      } else {
        setToastMessage(result.message);
        setShowToast(true);
      }
    } catch (error) {
      console.error('일반 과제 보상 회수 실패:', error);
      setToastMessage('보상 회수 중 오류가 발생했습니다.');
      setShowToast(true);
    }
  }, [currentAssignment, assignment, userId, classId, selectedStudents, onSave, studentStatuses]);

  // 선택된 멤버 기준 보상 지급/회수 버튼 활성화 여부
  const { rewardGrantDisabled, rewardRevokeDisabled } = useMemo(() => {
    const grantedIds =
      mode === 'general' && assignment
        ? (generalRewardGrantedOverride ?? assignment.rewardGrantedStudentIds ?? [])
        : (currentAssignment && 'rewardGrantedStudentIds' in currentAssignment)
          ? ((currentAssignment as any).rewardGrantedStudentIds as string[] || [])
          : [];
    const selectedList = Array.from(selectedStudents);
    const hasAnyGranted = selectedList.some((id) => grantedIds.includes(id));
    const hasAnyNotGranted = selectedList.some((id) => !grantedIds.includes(id));
    return {
      rewardGrantDisabled: selectedList.length === 0 || !hasAnyNotGranted,
      rewardRevokeDisabled: selectedList.length === 0 || !hasAnyGranted,
    };
  }, [selectedStudents, currentAssignment, mode, assignment, generalRewardGrantedOverride]);



  // 선택된 멤버들에게 일괄 적용
  const handleBatchUpdate = () => {
    if (selectedStudents.size === 0) {
      alert('선택된 멤버가 없습니다.');
      return;
    }

          const finalPoints: number = selectedStatus === 'confirmed' 
        ? (isPointsEnabled ? (pointsInput ? Number(pointsInput) : 0) : getBasePoints())
        : 0;

    setStudentStatuses(prev => 
      prev.map(status => 
        selectedStudents.has(status.studentId)
          ? {
              ...status,
              status: selectedStatus,
              earnedPoints: finalPoints,
              updatedAt: new Date(),
              confirmedAt: selectedStatus === 'confirmed' ? new Date() : status.confirmedAt
            }
          : status
      )
    );

    alert(`${selectedStudents.size}명의 멤버에게 일괄 적용되었습니다.`);
  };


  // 멤버 블럭 클릭 처리 (커스텀 더블클릭 로직 포함)
  const handleStudentSingleClick = (studentId: string) => {
    console.log(`handleStudentSingleClick(${studentId}):`, {
      mode,
      isAssigned: isStudentAssigned(studentId)
    });
    
    if (!isStudentAssigned(studentId)) {
      console.log(`멤버 ${studentId}가 과제에 할당되지 않음`);
      return;
    }
    
    const now = Date.now();
    const timeDiff = now - lastClickTime;
    
    // 더블클릭 체크 (200ms 내에 같은 멤버를 클릭했는지)
    if (timeDiff < 200 && lastClickStudentId === studentId) {
      console.log('더블클릭 감지 - 학생 상세 모달 열기');
      // 더블클릭 처리 - 학생 상세 모달 열기
      openStudentDetailModal(studentId);
      // 더블클릭 후 상태 초기화
      setLastClickTime(0);
      setLastClickStudentId(null);
      return;
    }
    
    // 일반 클릭 처리
    // handleStudentSelect가 이미 메모 입력 필드를 올바르게 처리하므로
    // 여기서는 추가로 메모를 로드하지 않음
    handleStudentSelect(studentId);
    
    // 클릭 정보 저장
    setLastClickTime(now);
    setLastClickStudentId(studentId);
  };


  // 저장 처리 (기존 함수 - handleFinalSave로 대체됨)
  const handleSave = () => {
    handleFinalSave();
  };

  // props 검증 및 에러 처리
  if (!currentAssignment) {
    console.error('AssignmentCheckModal: mode에 따라 assignment 또는 routine이 필요합니다.');
    return null;
  }

  // mode에 따른 props 검증
  if (mode === 'routine' && !routine) {
    console.error('AssignmentCheckModal: routine 모드에서는 routine prop이 필요합니다.');
    return null;
  }
  
  if (mode === 'general' && !assignment) {
    console.error('AssignmentCheckModal: general 모드에서는 assignment prop이 필요합니다.');
    return null;
  }

  if (!isOpen) return null;
  
  // 첫 번째 렌더링 차단을 위한 조건부 렌더링
  if (!isInitialized || (mode === 'routine' && isLoadingData)) {
    return (
      <div className="modal-overlay">
        <div className="modal-content loading-modal">
          <div className="loading-spinner">
            <div>🔄 데이터 로딩 중...</div>
            <div>루틴 과제 정보를 준비하고 있습니다.</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <Overlay onClick={handleClose} />
      <Wrapper onClick={(e) => {
        // Wrapper 내부의 Content가 아닌 곳을 클릭하면 닫기
        if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('[data-modal-content]')) {
          handleClose();
        }
      }}>
        <Content onClick={(e) => e.stopPropagation()} data-modal-content>
          {/* 과제 정보 섹션 */}
          <AssignmentInfo>
            <CategoryDisplay color={mode === 'routine' ? '#4F47E5' : (mode === 'project' ? '#FC5555' : (category?.color || 'rgba(162, 162, 162, 1)'))}>
              {mode === 'routine' ? '루틴' : (mode === 'project' ? '프로젝트' : (category?.name || '카테고리 없음'))}
            </CategoryDisplay>
            <AssignmentTitleRow>
              <AssignmentTitleMetaContainer>
            <AssignmentTitle>
              {mode === 'routine' 
                ? (routine?.title || '루틴 과제')
                : mode === 'project'
                ? (projectAssignment?.title || '프로젝트 과제')
                : (currentAssignment as Assignment)?.title || '과제'
              }
            </AssignmentTitle>
            <AssignmentMeta>
              {mode === 'routine' ? (
                <>
                  <AssignmentMetaLine>
                    {routine?.dueDate ? new Date(routine.dueDate).toLocaleDateString('ko-KR') : '날짜 없음'} / 획득 포인트: {getBasePoints()}
                  </AssignmentMetaLine>
                  {(routine?.rewardEnabled && ((routine?.rewardCoins !== undefined && routine.rewardCoins >= 1) || (routine?.rewardItemCardIds && routine.rewardItemCardIds.length > 0))) && (
                    <AssignmentMetaRewardRow style={{ marginTop: 0 }}>
                      <AssignmentMetaRewardInfo>
                        {(routine?.rewardCoins !== undefined && routine.rewardCoins >= 1) && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaCoinIcon>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                                <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                                <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                              </svg>
                            </AssignmentMetaCoinIcon>
                            <AssignmentMetaRewardText>{routine.rewardCoins}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                        {routine?.rewardItemCardIds && routine.rewardItemCardIds.length > 0 && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaItemCardIcon>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                              </svg>
                            </AssignmentMetaItemCardIcon>
                            <AssignmentMetaRewardText>{routine.rewardItemCardIds.length}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                      </AssignmentMetaRewardInfo>
                    </AssignmentMetaRewardRow>
                  )}
                  <span style={{ fontSize: '12px', color: 'var(--color-check-modal-text-gray)', display: 'block', marginBottom: '8px' }}>
                    반복 요일: {getRoutineDaysText()}
                  </span>
                </>
              ) : mode === 'project' ? (
                <>
                  <AssignmentMetaLine>
                    {projectAssignment?.dueDate ? new Date(projectAssignment.dueDate).toLocaleDateString('ko-KR') : '날짜 없음'} / 하위과제 포인트: {getBasePoints()}
                  </AssignmentMetaLine>
                  {(projectAssignment?.rewardEnabled && ((projectAssignment?.rewardCoins !== undefined && projectAssignment.rewardCoins >= 1) || (projectAssignment?.rewardItemCardIds && projectAssignment.rewardItemCardIds.length > 0))) && (
                    <AssignmentMetaRewardRow>
                      <AssignmentMetaRewardInfo>
                        {(projectAssignment?.rewardCoins !== undefined && projectAssignment.rewardCoins >= 1) && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaCoinIcon>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                                <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                                <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                              </svg>
                            </AssignmentMetaCoinIcon>
                            <AssignmentMetaRewardText>{projectAssignment.rewardCoins}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                        {projectAssignment?.rewardItemCardIds && projectAssignment.rewardItemCardIds.length > 0 && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaItemCardIcon>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                              </svg>
                            </AssignmentMetaItemCardIcon>
                            <AssignmentMetaRewardText>{projectAssignment.rewardItemCardIds.length}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                      </AssignmentMetaRewardInfo>
                    </AssignmentMetaRewardRow>
                  )}
                </>
              ) : (
                <>
                  <AssignmentMetaLine>
                    {(currentAssignment as Assignment)?.dueDate ? new Date((currentAssignment as Assignment).dueDate).toLocaleDateString('ko-KR') : '날짜 없음'} / 획득 포인트: {(currentAssignment as Assignment)?.basePoints || 0}
                  </AssignmentMetaLine>
                  {(currentAssignment as Assignment)?.rewardEnabled && (((currentAssignment as Assignment)?.rewardCoins !== undefined && (currentAssignment as Assignment).rewardCoins! >= 1) || ((currentAssignment as Assignment)?.rewardItemCardIds && (currentAssignment as Assignment).rewardItemCardIds!.length > 0)) && (
                    <AssignmentMetaRewardRow>
                      <AssignmentMetaRewardInfo>
                        {(currentAssignment as Assignment)?.rewardCoins !== undefined && (currentAssignment as Assignment).rewardCoins! >= 1 && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaCoinIcon>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                                <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                                <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                              </svg>
                            </AssignmentMetaCoinIcon>
                            <AssignmentMetaRewardText>{(currentAssignment as Assignment).rewardCoins}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                        {(currentAssignment as Assignment)?.rewardItemCardIds && (currentAssignment as Assignment).rewardItemCardIds!.length > 0 && (
                          <AssignmentMetaRewardItem>
                            <AssignmentMetaItemCardIcon>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                              </svg>
                            </AssignmentMetaItemCardIcon>
                            <AssignmentMetaRewardText>{(currentAssignment as Assignment).rewardItemCardIds!.length}</AssignmentMetaRewardText>
                          </AssignmentMetaRewardItem>
                        )}
                      </AssignmentMetaRewardInfo>
                    </AssignmentMetaRewardRow>
                  )}
                </>
              )}
            </AssignmentMeta>
              </AssignmentTitleMetaContainer>
              <TitleActionButtons>
                <TitleActionButtonContainer>
                  <SendToBoardButton
                    onClick={handlePrintAssignment}
                    disabled={isPrinting}
                    title="프린트"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                    </svg>
                  </SendToBoardButton>
                  <SendToBoardTooltip>
                    과제 내용<br />
                    인쇄
                  </SendToBoardTooltip>
                </TitleActionButtonContainer>
                <TitleActionButtonContainer>
                  <SendToBoardButton onClick={handleSendMemosToBoard}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </SendToBoardButton>
                  <SendToBoardTooltip>
                    과제 메모<br />
                    개별 공지로보내기
                  </SendToBoardTooltip>
                </TitleActionButtonContainer>
              </TitleActionButtons>
            </AssignmentTitleRow>
            
            {/* 루틴 과제 날짜 네비게이션 */}
            {mode === 'routine' && (
              // 로딩 중이거나 날짜가 있으면 네비게이션 표시
              (isLoadingData || routineDates.length > 0) ? (
                <DateNavigationContainer>
                  <DateNavigationButton 
                    onClick={goToPrevDate}
                    disabled={currentDateIndex === 0 || !!customDate}
                    title={customDate ? "커스텀 날짜 모드에서는 사용할 수 없습니다" : "이전 날짜"}
                  >
                    ‹
                  </DateNavigationButton>
                  <CurrentDateDisplay>
                    {currentDate.toLocaleDateString('ko-KR', { 
                      month: 'long', 
                      day: 'numeric',
                      weekday: 'long'
                    })}
                    <span style={{ fontSize: '12px', color: 'var(--color-check-modal-text-gray)', marginLeft: '8px' }}>
                      {customDate ? '' : `(${currentDateIndex + 1} / ${routineDates.length})`}
                    </span>
                  </CurrentDateDisplay>
                  <DateNavigationButton 
                    onClick={goToNextDate}
                    disabled={currentDateIndex === routineDates.length - 1 || !!customDate}
                    title={customDate ? "커스텀 날짜 모드에서는 사용할 수 없습니다" : "다음 날짜"}
                  >
                    ›
                  </DateNavigationButton>
                </DateNavigationContainer>
              ) : (
                <div style={{ 
                  color: '#ff6b6b', 
                  fontSize: '14px', 
                  textAlign: 'center', 
                  padding: '15px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: '8px',
                  margin: '10px 0'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    ⚠️ 유효한 루틴 날짜가 없습니다
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-gray)' }}>
                    마감일({routine?.dueDate ? new Date(routine.dueDate).toLocaleDateString('ko-KR') : '날짜 없음'}) 안에 
                    설정된 요일({getRoutineDaysText()})이 포함되지 않습니다.
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-gray)', marginTop: '5px' }}>
                    해결방법: 루틴 과제 설정에서 마감일을 오늘 이후로 변경하거나, 반복 요일을 조정해주세요.
                  </div>
                </div>
              )
            )}
              
              {/* 프로젝트 과제 하위과제 네비게이션 */}
              {mode === 'project' && projectSubTasks && projectSubTasks.length > 0 && (
                <DateNavigationContainer>
                  <DateNavigationButton 
                    onClick={() => {
                      const newIndex = Math.max(0, currentSubTaskIndex - 1);
                      onSubTaskChange?.(newIndex);
                    }}
                    disabled={currentSubTaskIndex === 0}
                    title="이전 하위과제"
                  >
                    ‹
                  </DateNavigationButton>
                  <CurrentDateDisplay>
                    {currentSubTask?.title || '하위과제 없음'}
                    <span style={{ fontSize: '12px', color: 'var(--color-check-modal-text-gray)', marginLeft: '8px' }}>
                      ({currentSubTaskIndex + 1} / {projectSubTasks.length})
                    </span>
                  </CurrentDateDisplay>
                  <DateNavigationButton 
                    onClick={() => {
                      const newIndex = Math.min(projectSubTasks.length - 1, currentSubTaskIndex + 1);
                      onSubTaskChange?.(newIndex);
                    }}
                    disabled={currentSubTaskIndex === projectSubTasks.length - 1}
                    title="다음 하위과제"
                  >
                    ›
                  </DateNavigationButton>
                </DateNavigationContainer>
              )}
          </AssignmentInfo>

          {/* 컨텐츠 영역 시작 */}
          <div>
            {/* 섹션 1: 과제 안내 */}
            {assignmentNotes && assignmentNotes.trim() && (
              <Section data-section>
                <SectionTitle>과제 안내</SectionTitle>
                <AssignmentNotesText>
                  {assignmentNotes}
                </AssignmentNotesText>
              </Section>
            )}
            
            {/* 섹션 2: 멤버별 과제 체크 */}
            <Section data-section>
            <SectionTitleContainer>
            <SectionTitle>멤버별 과제 체크</SectionTitle>
            </SectionTitleContainer>

            {/* 일반 과제 보상 지급/회수 버튼 */}
            {mode === 'general' && currentAssignment && 'rewardEnabled' in currentAssignment && currentAssignment.rewardEnabled && (
              <AssignmentRewardButtonContainer>
                <AssignmentRewardGrantButton 
                  onClick={handleGeneralAssignmentRewardGrant}
                  disabled={rewardGrantDisabled}
                  title="선택한 멤버들에게 보상 지급"
                >
                  선택멤버 보상 지급
                </AssignmentRewardGrantButton>
                <AssignmentRewardRevokeButton 
                  onClick={handleGeneralAssignmentRewardRevoke}
                  disabled={rewardRevokeDisabled}
                  title="선택한 멤버들의 보상 회수"
                >
                  선택멤버 보상 회수
                </AssignmentRewardRevokeButton>
              </AssignmentRewardButtonContainer>
            )}
            
            <LegendContainer data-legend-container>
                <StatusButtonsRow>
                  {statusOptions.map((option, index) => (
                    <StatusButton
                      key={option.value}
                      $isSelected={true}
                      onClick={() => {
                        // 버튼 클릭 시 해당 상태로 전체 선택된 멤버들의 상태 변경
                        if (selectedStudents.size > 0) {
                          const updatedStatuses = [...studentStatuses];
                          let pointsToSet: number;
                          
                          // 상태별 포인트 자동 설정
                          switch (option.value) {
                            case 'pending':
                              pointsToSet = 0; // 미확인: 0점
                              break;
                            case 'confirmed':
                              pointsToSet = getBasePoints(); // 확인: 과제 기본 포인트
                              break;
                            case 'delayed':
                              pointsToSet = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
                              break;
                            case 'exempted':
                              pointsToSet = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
                              break;
                            case 'submitted':
                              pointsToSet = getBasePoints(); // 제출확인: 과제 기본 포인트
                              break;
                            default:
                              pointsToSet = getBasePoints();
                          }
                          
                          selectedStudents.forEach(studentId => {
                            const existingIndex = updatedStatuses.findIndex(s => s.studentId === studentId);
                            if (existingIndex >= 0) {
                              updatedStatuses[existingIndex] = {
                                ...updatedStatuses[existingIndex],
                                status: option.value,
                                earnedPoints: pointsToSet
                              };
                            } else {
                              updatedStatuses.push({
                                id: `${studentId}_${currentAssignment?.id || 'temp'}`,
                                studentId,
                                assignmentId: currentAssignment?.id || 'temp',
                                status: option.value,
                                earnedPoints: pointsToSet,
                                updatedAt: new Date()
                              });
                            }
                          });
                          
                          setStudentStatuses(updatedStatuses);
                          
                          // 변경사항 추적 (각 멤버별로)
                          selectedStudents.forEach(studentId => {
                            trackChange(studentId, { 
                              status: option.value, 
                              earnedPoints: pointsToSet 
                            });
                          });
                          
                          // 선택 영역 초기화
                          setSelectedStudents(new Set());
                        }
                      }}
                    >
                      <StatusButtonColorDot 
                        $isSelected={true}
                        style={{ backgroundColor: option.bgColor }}
                      >
                        {index + 1}
                      </StatusButtonColorDot>
                      <StatusButtonText $isSelected={true}>
                        {option.label}
                      </StatusButtonText>
                    </StatusButton>
                  ))}
                </StatusButtonsRow>
            </LegendContainer>
            
            {/* 포인트 필드 */}
            <PointsInputWrapper>
              <PointsInputField
                type={selectedStudents.size > 1 ? "text" : "number"}
                value={selectedStudents.size === 0 ? '' : (selectedStudents.size > 1 ? (selectedPoints === 0 && !isUserModifiedPoints ? '공통 포인트' : selectedPoints.toString()) : (isUserModifiedPoints ? selectedPoints.toString() : (() => {
                  const studentId = Array.from(selectedStudents)[0];
                  const status = studentStatuses.find(s => s.studentId === studentId);
                  return status?.earnedPoints || 0;  // getBasePoints() 대신 0 반환
                })()))}
                style={{
                  color: selectedStudents.size > 1 ? (selectedPoints === 0 && !isUserModifiedPoints ? 'var(--color-text-muted)' : 'var(--color-text-primary)') : 'var(--color-text-primary)',
                  fontSize: selectedStudents.size > 1 ? (selectedPoints === 0 && !isUserModifiedPoints ? '13px' : '14px') : '14px',
                  fontWeight: selectedStudents.size > 1 ? (selectedStudents.size > 1 ? (selectedPoints === 0 && !isUserModifiedPoints ? '400' : '400') : '400') : '400'
                }}
                onChange={(e) => {
                  const inputValue = e.target.value;

                  console.log('📝 필드 입력:', {
                    inputValue,
                    selectedStudentsSize: selectedStudents.size,
                    selectedPoints
                  });

                  // 숫자 입력 처리 (단일/복수 선택 모두 동일)
                  if (inputValue === '') {
                    setSelectedPoints(0);
                    setIsUserModifiedPoints(true);
                  } else {
                    const value = parseInt(inputValue);
                    if (!isNaN(value) && value >= 0) {
                      setSelectedPoints(value);
                      setIsUserModifiedPoints(true);
                    }
                  }
                }}
                onFocus={(e) => {
                  if (selectedStudents.size <= 1) {
                    // 단일 선택 시에만 전체 선택
                    e.target.select();
                  }
                }}
                min="0"
                disabled={false}
                placeholder={selectedStudents.size === 0 ? '포인트' : (selectedStudents.size > 1 ? '공통 포인트' : '')}
              />
              <PointsResetBtn
                onClick={handlePointsReset}
                disabled={false}
                title="포인트 0으로 설정"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="4" width="16" height="16" rx="2" stroke="var(--color-text-muted)" strokeWidth={2} fill="none"/>
                </svg>
              </PointsResetBtn>
              <PointsMinusBtn
                onClick={handleSelectedPointsDecrease}
                disabled={false}
                title={selectedStudents.size > 1 ? "공통 포인트 감소" : "포인트 감소"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13H5v-3h14v3z" fill="currentColor"/>
                </svg>
              </PointsMinusBtn>
              <PointsPlusBtn
                onClick={handleSelectedPointsIncrease}
                disabled={false}
                title={selectedStudents.size > 1 ? "공통 포인트 증가" : "포인트 증가"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-3v-6H5v-3h6V5h3v6h6v3z" fill="currentColor"/>
                </svg>
              </PointsPlusBtn>
            </PointsInputWrapper>

            {/* 메모 입력 필드 행 */}
            <MemoInputWrapper>
              <MemoInputField
                type="text"
                placeholder={selectedStudents.size > 1 ? "공통 메모" : (selectedStudents.size === 1 ? "메모 입력" : "메모 입력")}
                value={memoInput}
                onChange={handleMemoInputChange}
                readOnly={isHoveringMemo}
                style={{
                  flex: '1', // 남은 공간 모두 차지 (입력 버튼까지)
                  color: isHoveringMemo ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                  textAlign: isHoveringMemo ? 'center' : 'left',
                  cursor: isHoveringMemo ? 'default' : 'text'
                }}
              />
              <PointsApplyBtn
                onClick={() => {
                  console.log('🔍 PointsApplyBtn onClick 이벤트 시작:', {
                    selectedStudentsSize: selectedStudents.size,
                    selectedPoints,
                    isUserModifiedPoints,
                    timestamp: new Date().toISOString()
                  });
                  handleApplyPoints();
                }}
                disabled={false}
                title="메모 적용"
              >
                입력
              </PointsApplyBtn>
            </MemoInputWrapper>



            {/* 전체 선택/해제 버튼 */}
            <SelectAllSection>
              {!isMemoViewMode && (
                <MemberViewModeToggle
                  className="touch-compact"
                  role="button"
                  tabIndex={0}
                  $isSeatMode={memberViewMode === 'seat'}
                  onClick={() => setMemberViewMode(prev => (prev === 'number' ? 'seat' : 'number'))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setMemberViewMode(prev => (prev === 'number' ? 'seat' : 'number'));
                    }
                  }}
                  aria-label="번호 자리 보기 전환"
                >
                  <MemberViewModeOption $isActive={memberViewMode === 'number'}>번호</MemberViewModeOption>
                  <MemberViewModeOption $isActive={memberViewMode === 'seat'}>자리</MemberViewModeOption>
                </MemberViewModeToggle>
              )}
              <SelectAllButton onClick={handleSelectAll}>
                전체 선택
              </SelectAllButton>
              <SelectAllButton onClick={handleDeselectAll}>
                선택 해제
              </SelectAllButton>
              <SelectAllButton 
                onClick={() => setIsMemoDeleteModalOpen(true)}
                disabled={selectedStudents.size === 0}
                title="선택된 멤버들의 메모 삭제"
              >
                선택 메모 삭제
              </SelectAllButton>
              <SelectAllButton 
                onClick={() => {
                  if (isMemoViewMode) {
                    setIsMemoViewMode(false);
                  } else {
                    // 메모 보기 모드 시작: 현재 studentStatuses에서 메모를 로드
                    const memos: Record<string, string> = {};
                    studentStatuses.forEach(status => {
                      memos[status.studentId] = status.memo || '';
                    });
                    // members에 있지만 studentStatuses에 없는 학생도 추가
                    members.forEach(member => {
                      if (!(member.id in memos)) {
                        memos[member.id] = '';
                      }
                    });
                    setMemoViewMemos(memos);
                    setIsMemoViewMode(true);
                  }
                }}
                title={isMemoViewMode ? '메모 닫기' : '메모 열기'}
              >
                {isMemoViewMode ? '메모 닫기' : '메모 열기'}
              </SelectAllButton>
            </SelectAllSection>

            {/* 구분선 */}
            <DividerLine />
            
            <StudentGridContainer $isMemoViewMode={isMemoViewMode}>
              {isMemoViewMode ? (
                <MemoViewLayout>
                  {members.map((member, memoRowIndex) => {
                    const isAssigned = getAssignedStudentIds().includes(member.id);
                    const status = studentStatuses.find(s => s.studentId === member.id) || {
                      id: `${member.id}_${currentAssignment?.id || 'temp'}`,
                      studentId: member.id,
                      assignmentId: currentAssignment?.id || 'temp',
                      status: 'pending' as AssignmentStatusValue,
                      earnedPoints: getBasePoints(),
                      memo: undefined,
                      updatedAt: new Date()
                    };
                    
                    const hasReward = isGeneralMemberRewardGranted(member.id);
                    
                    const rewardStatusColor = hasReward 
                      ? (() => {
                          // submitted 상태는 검정색으로 표시
                          if (status.status === 'submitted') {
                            return '#000000';
                          }
                          const option = statusOptions.find(opt => opt.value === status.status);
                          const colorMap: { [key: string]: string } = {
                            'var(--status-pending)': '#FF316B',
                            'var(--status-confirmed)': '#227971',
                            'var(--status-delayed)': '#65A994',
                            'var(--status-exempted)': '#FFCC6E'
                          };
                          return colorMap[option?.bgColor || ''] || option?.bgColor;
                        })()
                      : undefined;
                    
                    const currentMemo = memoViewMemos[member.id] ?? (status.memo || '');
                    
                    return (
                      <MemoViewRow key={member.id}>
                        <MemoViewStudentItemWrapper>
                          <StudentItem 
                            $status={status.status}
                            $isAssigned={isAssigned}
                            $isSelected={selectedStudents.has(member.id)}
                            $hasReward={hasReward}
                            $rewardStatusColor={rewardStatusColor}
                            onClick={() => isAssigned ? handleStudentSingleClick(member.id) : undefined}
                            role="button"
                            tabIndex={isAssigned ? 0 : -1}
                            data-student-id={member.id}
                          >
                            <StudentItemBorder 
                              $isAssigned={isAssigned}
                              $isSelected={selectedStudents.has(member.id)}
                            />
                            {hasStudentMemo(member.id) && (
                              <MemoIndicator />
                            )}
                            <StudentNumber
                              $hasSubmission={!!(
                                (status as any).submissionLink?.trim?.() ||
                                (status as any).submissionMessage?.trim?.() ||
                                submissionChangeTracker[member.id]?.link?.trim?.() ||
                                submissionChangeTracker[member.id]?.message?.trim?.()
                              )}
                              style={isAssigned ? getNumberStatusStyle(status.status) : { backgroundColor: 'var(--color-surface-highlight)', color: '#999' }}
                            >
                              <span className="number">{formatStudentNumber(member.number)}</span>
                              <span className="name">{member.name}</span>
                            </StudentNumber>
                            <StudentControls>
                              <PointsText
                                $isAssigned={isAssigned}
                                $isEditing={false}
                                $hasReward={hasReward}
                                $rewardStatusColor={rewardStatusColor}
                                title={isAssigned ? "포인트" : "과제가 할당되지 않음"}
                              >
                                {formatPoints(isAssigned ? status.earnedPoints : 0)}
                              </PointsText>
                            </StudentControls>
                          </StudentItem>
                        </MemoViewStudentItemWrapper>
                        <MemoViewMemoFieldWrapper>
                          <MemoViewMemoTextarea
                            data-memo-student-id={member.id}
                            value={currentMemo}
                            onChange={(e) => {
                              setMemoViewMemos(prev => ({
                                ...prev,
                                [member.id]: e.target.value
                              }));
                            }}
                            onKeyUp={(e) => {
                              // 커서 위치에 따라 스크롤 조정
                              const textarea = e.currentTarget;
                              if (!textarea) return;
                              
                              const cursorPosition = textarea.selectionStart ?? 0;
                              const textBeforeCursor = textarea.value.substring(0, cursorPosition);
                              
                              // 텍스트 너비 계산을 위한 임시 요소
                              const measureElement = document.createElement('span');
                              measureElement.style.visibility = 'hidden';
                              measureElement.style.position = 'absolute';
                              measureElement.style.fontSize = '14px';
                              measureElement.style.fontFamily = getComputedStyle(textarea).fontFamily;
                              measureElement.style.whiteSpace = 'nowrap';
                              measureElement.textContent = textBeforeCursor;
                              document.body.appendChild(measureElement);
                              
                              const textWidth = measureElement.offsetWidth;
                              document.body.removeChild(measureElement);
                              
                              // textarea의 가시 영역 너비
                              const visibleWidth = textarea.clientWidth - 24; // padding 제외
                              const scrollLeft = textWidth - visibleWidth + 12; // 약간의 여유 공간
                              
                              if (scrollLeft > 0) {
                                textarea.scrollLeft = scrollLeft;
                              } else {
                                textarea.scrollLeft = 0;
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                // IME 조합 중에는 이동 금지
                                if (e.nativeEvent.isComposing) return;
                                // Shift+Enter: 줄바꿈만 (포커스 이동 없음)
                                if (e.shiftKey) {
                                  e.stopPropagation();
                                  return;
                                }
                                // Enter: 다음 필드로 이동
                                e.preventDefault();
                                e.stopPropagation();
                                const currentId = e.currentTarget.getAttribute('data-memo-student-id');
                                const textareas = Array.from(document.querySelectorAll<HTMLTextAreaElement>('textarea[data-memo-student-id]'));
                                const currentIndex = textareas.findIndex(el => el.getAttribute('data-memo-student-id') === currentId);
                                if (currentIndex >= 0) {
                                  let nextIndex = currentIndex + 1;
                                  while (nextIndex < textareas.length && textareas[nextIndex].disabled) {
                                    nextIndex += 1;
                                  }
                                  if (nextIndex < textareas.length) {
                                    // 키 이벤트가 다음 필드에 전달되지 않도록 프레임 뒤 포커스 이동
                                    setTimeout(() => textareas[nextIndex].focus(), 0);
                                  }
                                }
                                return;
                              }
                              // 화살표 키나 Home/End 키 사용 시에도 스크롤 조정
                              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                setTimeout(() => {
                                  const textarea = e.currentTarget;
                                  if (!textarea) return;
                                  
                                  const cursorPosition = textarea.selectionStart ?? 0;
                                  const textBeforeCursor = textarea.value.substring(0, cursorPosition);
                                  
                                  const measureElement = document.createElement('span');
                                  measureElement.style.visibility = 'hidden';
                                  measureElement.style.position = 'absolute';
                                  measureElement.style.fontSize = '14px';
                                  measureElement.style.fontFamily = getComputedStyle(textarea).fontFamily;
                                  measureElement.style.whiteSpace = 'nowrap';
                                  measureElement.textContent = textBeforeCursor;
                                  document.body.appendChild(measureElement);
                                  
                                  const textWidth = measureElement.offsetWidth;
                                  document.body.removeChild(measureElement);
                                  
                                  const visibleWidth = textarea.clientWidth - 24;
                                  const scrollLeft = textWidth - visibleWidth + 12;
                                  
                                  if (scrollLeft > 0) {
                                    textarea.scrollLeft = scrollLeft;
                                  } else {
                                    textarea.scrollLeft = 0;
                                  }
                                }, 0);
                              }
                            }}
                            placeholder="메모를 입력하세요..."
                            disabled={!isAssigned}
                          />
                        </MemoViewMemoFieldWrapper>
                      </MemoViewRow>
                    );
                  })}
                </MemoViewLayout>
              ) : (
              memberViewMode === 'seat' ? (
                <SeatModeArea>
                  <SeatModeScroll>
                    <SeatModeGrid $gridTemplateColumns={seatViewData.colTemplate} $gridTemplateRows={seatViewData.rowTemplate}>
                      {seatViewData.assigned.map(({ member, isAssigned, displayRow, displayCol }) => {
                        const status = studentStatuses.find(s => s.studentId === member.id) || {
                          id: `${member.id}_${currentAssignment?.id || 'temp'}`,
                          studentId: member.id,
                          assignmentId: currentAssignment?.id || 'temp',
                          status: 'pending' as AssignmentStatusValue,
                          earnedPoints: getBasePoints(),
                          updatedAt: new Date()
                        };
                        const hasReward = isGeneralMemberRewardGranted(member.id);
                        const rewardStatusColor = hasReward
                          ? (() => {
                              if (status.status === 'submitted') return '#000000';
                              const option = statusOptions.find(opt => opt.value === status.status);
                              const colorMap: { [key: string]: string } = {
                                'var(--status-pending)': '#FF316B',
                                'var(--status-confirmed)': '#227971',
                                'var(--status-delayed)': '#65A994',
                                'var(--status-exempted)': '#FFCC6E'
                              };
                              return colorMap[option?.bgColor || ''] || option?.bgColor;
                            })()
                          : undefined;

                        return (
                          <SeatModeCell
                            key={member.id}
                            style={{ gridRow: `${displayRow + 1}`, gridColumn: `${displayCol + 1} / span 2` }}
                          >
                            <StudentItem
                              $status={status.status}
                              $isAssigned={isAssigned}
                              $isSelected={selectedStudents.has(member.id)}
                              $hasReward={hasReward}
                              $rewardStatusColor={rewardStatusColor}
                              onMouseLeave={() => {
                                if (selectedStudents.size === 0) hideMemo();
                              }}
                              onMouseEnter={() => {
                                if (selectedStudents.size === 0) {
                                  const hoveredStatus = studentStatuses.find(s => s.studentId === member.id);
                                  if (hoveredStatus?.memo && hoveredStatus.memo.trim().length > 0) {
                                    showMemo(member.id, hoveredStatus.memo);
                                  }
                                }
                              }}
                              onClick={() => isAssigned ? handleStudentSingleClick(member.id) : undefined}
                              role="button"
                              tabIndex={isAssigned ? 0 : -1}
                              data-student-id={member.id}
                            >
                              <StudentItemBorder
                                $isAssigned={isAssigned}
                                $isSelected={selectedStudents.has(member.id)}
                              />
                              {hasStudentMemo(member.id) && <MemoIndicator />}
                              <StudentNumber
                                $hasSubmission={!!(
                                  (status as any).submissionLink?.trim?.() ||
                                  (status as any).submissionMessage?.trim?.() ||
                                  submissionChangeTracker[member.id]?.link?.trim?.() ||
                                  submissionChangeTracker[member.id]?.message?.trim?.()
                                )}
                                style={isAssigned ? getNumberStatusStyle(status.status) : { backgroundColor: 'var(--color-surface-highlight)', color: '#999' }}
                              >
                                <span className="number">{formatStudentNumber(member.number)}</span>
                                <span className="name">{member.name}</span>
                              </StudentNumber>
                              <StudentControls>
                                <PointsText
                                  $isAssigned={isAssigned}
                                  $isEditing={false}
                                  $hasReward={hasReward}
                                  $rewardStatusColor={rewardStatusColor}
                                  title={isAssigned ? "포인트" : "과제가 할당되지 않음"}
                                >
                                  {formatPoints(isAssigned ? status.earnedPoints : 0)}
                                </PointsText>
                              </StudentControls>
                            </StudentItem>
                          </SeatModeCell>
                        );
                      })}
                    </SeatModeGrid>
                  </SeatModeScroll>
                  {seatViewData.assigned.length > 0 && (
                    <SeatModeRotateButtonContainer>
                      <SeatModeRotateButton onClick={() => setIsSeatRotated(prev => !prev)} title="180도 회전">
                        <SeatModeRotateIconWrapper>
                          <SeatModeRotateArrowIcon>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                            </svg>
                          </SeatModeRotateArrowIcon>
                          <SeatModeRotateNumberIcon>180</SeatModeRotateNumberIcon>
                        </SeatModeRotateIconWrapper>
                      </SeatModeRotateButton>
                    </SeatModeRotateButtonContainer>
                  )}

                  {seatUnassignedGroupedMembers.length > 0 && (
                    <>
                      <SeatUnassignedTitle>자리 미지정 멤버</SeatUnassignedTitle>
                      <StudentGrid $numColumns={seatUnassignedGroupedMembers.length}>
                        {seatUnassignedGroupedMembers.map((column, colIdx) => (
                          <StudentColumn key={`unassigned-${colIdx}`} $numColumns={seatUnassignedGroupedMembers.length}>
                            {column.map(({ member, isAssigned }) => {
                              const status = studentStatuses.find(s => s.studentId === member.id) || {
                                id: `${member.id}_${currentAssignment?.id || 'temp'}`,
                                studentId: member.id,
                                assignmentId: currentAssignment?.id || 'temp',
                                status: 'pending' as AssignmentStatusValue,
                                earnedPoints: getBasePoints(),
                                updatedAt: new Date()
                              };
                              const hasReward = isGeneralMemberRewardGranted(member.id);
                              const rewardStatusColor = hasReward
                                ? (() => {
                                    if (status.status === 'submitted') return '#000000';
                                    const option = statusOptions.find(opt => opt.value === status.status);
                                    const colorMap: { [key: string]: string } = {
                                      'var(--status-pending)': '#FF316B',
                                      'var(--status-confirmed)': '#227971',
                                      'var(--status-delayed)': '#65A994',
                                      'var(--status-exempted)': '#FFCC6E'
                                    };
                                    return colorMap[option?.bgColor || ''] || option?.bgColor;
                                  })()
                                : undefined;
                              return (
                                <StudentItem
                                  key={member.id}
                                  $status={status.status}
                                  $isAssigned={isAssigned}
                                  $isSelected={selectedStudents.has(member.id)}
                                  $hasReward={hasReward}
                                  $rewardStatusColor={rewardStatusColor}
                                  onClick={() => isAssigned ? handleStudentSingleClick(member.id) : undefined}
                                  role="button"
                                  tabIndex={isAssigned ? 0 : -1}
                                  data-student-id={member.id}
                                >
                                  <StudentItemBorder $isAssigned={isAssigned} $isSelected={selectedStudents.has(member.id)} />
                                  <StudentNumber style={isAssigned ? getNumberStatusStyle(status.status) : { backgroundColor: 'var(--color-surface-highlight)', color: '#999' }}>
                                    <span className="number">{formatStudentNumber(member.number)}</span>
                                    <span className="name">{member.name}</span>
                                  </StudentNumber>
                                  <StudentControls>
                                    <PointsText
                                      $isAssigned={isAssigned}
                                      $isEditing={false}
                                      $hasReward={hasReward}
                                      $rewardStatusColor={rewardStatusColor}
                                      title={isAssigned ? "포인트" : "과제가 할당되지 않음"}
                                    >
                                      {formatPoints(isAssigned ? status.earnedPoints : 0)}
                                    </PointsText>
                                  </StudentControls>
                                </StudentItem>
                              );
                            })}
                          </StudentColumn>
                        ))}
                      </StudentGrid>
                    </>
                  )}
                </SeatModeArea>
              ) : (
              <StudentGrid $numColumns={groupedMembers.length}>
                {groupedMembers.map((column, colIdx) => (
                  <StudentColumn key={colIdx} $numColumns={groupedMembers.length}>
                    {column.map(({ member, isAssigned }) => {
                                      // studentStatuses에서 상태를 찾거나 기본값 사용
                const status = studentStatuses.find(s => s.studentId === member.id) || {
                  id: `${member.id}_${currentAssignment?.id || 'temp'}`,
                  studentId: member.id,
                  assignmentId: currentAssignment?.id || 'temp',
                  status: 'pending' as AssignmentStatusValue,
                          earnedPoints: getBasePoints(),
                  updatedAt: new Date()
                };
                
                // 일반 과제에서 보상 받은 멤버 확인
                const hasReward = isGeneralMemberRewardGranted(member.id);
                
                // 보상 받은 멤버의 경우 해당 멤버의 과제 상태 색상 가져오기 (실제 색상 값으로 변환)
                const rewardStatusColor = hasReward 
                  ? (() => {
                      // submitted 상태는 검정색으로 표시
                      if (status.status === 'submitted') {
                        return '#000000';
                      }
                      const option = statusOptions.find(opt => opt.value === status.status);
                      // CSS 변수를 실제 색상 값으로 변환 (라이트/다크모드 동일하게 표시)
                      const colorMap: { [key: string]: string } = {
                        'var(--status-pending)': '#FF316B',
                        'var(--status-confirmed)': '#227971',
                        'var(--status-delayed)': '#65A994',
                        'var(--status-exempted)': '#FFCC6E'
                      };
                      return colorMap[option?.bgColor || ''] || option?.bgColor;
                    })()
                  : undefined;
                      
                      return (
                      <StudentItem 
                        key={member.id} 
                        $status={status.status}
                        $isAssigned={isAssigned}
                        $isSelected={selectedStudents.has(member.id)}
                        $hasReward={hasReward}
                        $rewardStatusColor={rewardStatusColor}
                        onMouseLeave={() => {
                          // 마우스가 벗어날 때 선택된 학생이 없을 때만 메모 숨기기
                          if (selectedStudents.size === 0) {
                            hideMemo();
                          }
                        }}
                        onMouseEnter={() => {
                          // 선택된 학생이 없을 때만 마우스 오버로 메모 표시
                          if (selectedStudents.size === 0) {
                          const status = studentStatuses.find(s => s.studentId === member.id);
                          if (status?.memo && status.memo.trim().length > 0) {
                            showMemo(member.id, status.memo);
                            }
                          }
                        }}
                        onClick={() => isAssigned ? handleStudentSingleClick(member.id) : undefined}
                        onKeyDown={(e) => {
                          if (!isAssigned) return;
                          
                          if (e.key === 'Tab') {
                            e.preventDefault();
                            // 현재 멤버의 인덱스 찾기
                            const currentIndex = members.findIndex(m => m.id === member.id);
                            
                            if (e.shiftKey) {
                              // Shift+Tab: 이전 멤버으로 이동
                              if (currentIndex > 0) {
                                const prevStudent = members[currentIndex - 1];
                                if (isStudentAssigned(prevStudent.id)) {
                                  setSelectedStudents(new Set([prevStudent.id]));
                                  const prevElement = document.querySelector(`[data-student-id="${prevStudent.id}"]`) as HTMLElement;
                                  if (prevElement) {
                                    prevElement.focus();
                                  }
                                }
                              }
                            } else {
                              // Tab: 다음 멤버으로 이동
                              if (currentIndex < members.length - 1) {
                                const nextStudent = members[currentIndex + 1];
                                if (isStudentAssigned(nextStudent.id)) {
                                  setSelectedStudents(new Set([nextStudent.id]));
                                  const nextElement = document.querySelector(`[data-student-id="${nextStudent.id}"]`) as HTMLElement;
                                  if (nextElement) {
                                    nextElement.focus();
                                  }
                                }
                              }
                            }
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            // 복수 선택인 경우 현재 멤버만 선택
                            if (selectedStudents.size > 1) {
                              setSelectedStudents(new Set([member.id]));
                            }
                            // 인라인 포인트 편집 모드 활성화
                            const currentPoints = status.earnedPoints || 0;
                            setInlinePointsValue(currentPoints.toString());
                            setInlineEditingStudentId(member.id);
                          } else if (e.key === 'Escape') {
                            // ESC 키로 인라인 편집 취소
                            if (inlineEditingStudentId === member.id) {
                              setInlineEditingStudentId(null);
                              setInlinePointsValue('');
                            }
                          }
                        }}
                        role="button"
                        tabIndex={isAssigned ? 0 : -1}
                        data-student-id={member.id}
                      >
                        <StudentItemBorder 
                          $isAssigned={isAssigned}
                          $isSelected={selectedStudents.has(member.id)}
                        />

                        {hasStudentMemo(member.id) && (
                          <MemoIndicator />
                        )}
                        <StudentNumber
                          $hasSubmission={!!(
                            (status as any).submissionLink?.trim?.() ||
                            (status as any).submissionMessage?.trim?.() ||
                            submissionChangeTracker[member.id]?.link?.trim?.() ||
                            submissionChangeTracker[member.id]?.message?.trim?.()
                          )}
                          style={isAssigned ? getNumberStatusStyle(status.status) : { backgroundColor: 'var(--color-surface-highlight)', color: '#999' }}
                        >
                          <span className="number">{formatStudentNumber(member.number)}</span>
                          <span className="name">{member.name}</span>
                        </StudentNumber>
                        <StudentControls>
                          {inlineEditingStudentId === member.id ? (
                            <InlinePointsInput
                              type="text"
                              value={inlinePointsValue}
                              onChange={(e) => setInlinePointsValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // 포인트 저장
                                  const newPoints = parseInt(inlinePointsValue) || 0;
                                  setStudentStatuses(prev => 
                                    prev.map(s => 
                                      s.studentId === member.id 
                                        ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
                                        : s
                                    )
                                  );
                                  // 변경사항 추적
                                  setChangeTracker(prevTracker => ({
                                    ...prevTracker,
                                    [member.id]: {
                                      ...prevTracker[member.id],
                                      earnedPoints: newPoints,
                                      updatedAt: new Date()
                                    }
                                  }));
                                  // 인라인 편집 종료
                                  setInlineEditingStudentId(null);
                                  setInlinePointsValue('');
                                  // 멤버 블럭으로 포커스 복귀
                                  const studentElement = document.querySelector(`[data-student-id="${member.id}"]`) as HTMLElement;
                                  if (studentElement) {
                                    studentElement.focus();
                                  }
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // 편집 취소
                                  setInlineEditingStudentId(null);
                                  setInlinePointsValue('');
                                  // 멤버 블럭으로 포커스 복귀
                                  const studentElement = document.querySelector(`[data-student-id="${member.id}"]`) as HTMLElement;
                                  if (studentElement) {
                                    studentElement.focus();
                                  }
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // 포인트 저장 후 다음 멤버으로 이동
                                  const newPoints = parseInt(inlinePointsValue) || 0;
                                  setStudentStatuses(prev => 
                                    prev.map(s => 
                                      s.studentId === member.id 
                                        ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
                                        : s
                                    )
                                  );
                                  setChangeTracker(prevTracker => ({
                                    ...prevTracker,
                                    [member.id]: {
                                      ...prevTracker[member.id],
                                      earnedPoints: newPoints,
                                      updatedAt: new Date()
                                    }
                                  }));
                                  // 인라인 편집 종료
                                  setInlineEditingStudentId(null);
                                  setInlinePointsValue('');
                                  // 다음 멤버으로 이동
                                  const currentIndex = members.findIndex(m => m.id === member.id);
                                  if (currentIndex < members.length - 1) {
                                    const nextStudent = members[currentIndex + 1];
                                    if (isStudentAssigned(nextStudent.id)) {
                                      setSelectedStudents(new Set([nextStudent.id]));
                                      const nextElement = document.querySelector(`[data-student-id="${nextStudent.id}"]`) as HTMLElement;
                                      if (nextElement) {
                                        nextElement.focus();
                                      }
                                    }
                                  }
                                }
                              }}
                              onBlur={() => {
                                // 포커스 잃으면 편집 취소
                                setTimeout(() => {
                                  setInlineEditingStudentId(null);
                                  setInlinePointsValue('');
                                }, 100);
                              }}
                              autoFocus
                              onFocus={(e) => e.target.select()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <PointsText
                              $isAssigned={isAssigned}
                              $isEditing={false}
                              $hasReward={hasReward}
                              $rewardStatusColor={rewardStatusColor}
                              title={isAssigned ? "포인트" : "과제가 할당되지 않음"}
                            >
                              {formatPoints(isAssigned ? status.earnedPoints : 0)}
                            </PointsText>
                          )}
                        </StudentControls>
                      </StudentItem>
                      );
                    })}
                  </StudentColumn>
                ))}
              </StudentGrid>
              )
              )}
            </StudentGridContainer>
            
            {/* 토글 버튼들과 3개 필드 */}
            <BatchInputSection>
                
             {/* 키보드 과제체크 도우미 토글 버튼 */}
             <KeyboardHelperToggleButton
               type="button"
               onClick={() => setIsKeyboardHelperOpen(!isKeyboardHelperOpen)}
               title={isKeyboardHelperOpen ? "키보드 도우미 닫기" : "키보드 도우미 열기"}
             >
               {isKeyboardHelperOpen ? "키보드 과제 체크 도우미 닫기" : "키보드 과제 체크 도우미 열기"}
             </KeyboardHelperToggleButton>
             
             {/* 키보드 도우미 내용 (토글 가능) */}
             {isKeyboardHelperOpen && (
               <>
                {/* 키보드 도우미 사용 안내 */}
                <KeyboardHelperInfo>
                  키보드 탭과 엔터로 필드값을 입력할 수 있습니다
                </KeyboardHelperInfo>
                
                <BatchInputContainer>
                                                  <BatchInputGroup>
                  <BatchLabel>대상</BatchLabel>
                  <BatchInput
                    tabIndex={1}
                    data-field="target-input"
                    value={checkInput}
                    onChange={(e) => {
                      console.log('🔄 대상 필드 onChange:', e.target.value);
                      setIsUserTyping(true); // 🎯 사용자 입력 중임을 표시
                      setCheckInput(e.target.value);
                    }}
                    placeholder="번호 또는 이름"
                    onFocus={(e) => {
                      console.log('🎯 대상 필드 onFocus');
                      e.target.select();
                    }}
                    onBlur={() => {
                      // 클릭 제출 중에는 자동채움(useEffect) 재가동을 지연해 깜빡임 방지
                      if (!isSubmittingViaClick) {
                        setIsUserTyping(false); // 🎯 포커스 해제 시 사용자 입력 상태 리셋
                      }
                    }}
                    onKeyPress={(e) => {
                      console.log('⌨️ 대상 필드 onKeyPress:', e.key);
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleTabOnTargetField(); // 🎯 Enter 키도 탭처럼 작동 (대상 찾기 및 선택)
                      }
                    }}
                    onKeyDown={(e) => {
                      console.log('⌨️ 대상 필드 onKeyDown:', e.key);
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        // 🎯 Tab 키: 대상 찾기 및 선택
                        handleTabOnTargetField();
                      }
                    }}
                    disabled={selectedStudents.size > 1} // 🎯 다중 선택(2명 이상) 시에만 비활성화
                  />
                </BatchInputGroup>
                
                <BatchInputGroup>
                  <BatchLabel>과제 상태</BatchLabel>
                                    <div style={{ position: 'relative', width: '100%' }}>
                    <BatchStatusInput
                      tabIndex={2}
                      data-field="status-input"
                      value={statusOptions.find(opt => opt.value === selectedStatus)?.label || ''}
                      onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                      onFocus={() => setIsStatusFieldFocused(true)}
                      onBlur={() => setIsStatusFieldFocused(false)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          moveToNextEnabledField(2);
                        }
                      }}
                      readOnly
                    />
                    {/* 색상 도형과 텍스트를 그룹으로 묶어 중앙정렬 */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        pointerEvents: 'none'
                      }}
                    >
                      {/* 색상 도형 */}
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: statusOptions.find(opt => opt.value === selectedStatus)?.bgColor || 'var(--color-border)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '700',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}
                      >
                        {getStatusNumber(selectedStatus)}
                      </div>
                      {/* 텍스트 */}
                      {!isMobile && (
                        <span
                          style={{
                            color: 'var(--color-text-primary)',
                            fontSize: '14px',
                            fontWeight: 'normal'
                          }}
                          className="status-text"
                        >
                          {statusOptions.find(opt => opt.value === selectedStatus)?.label || ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* 상태 드롭다운 */}
                  {isStatusDropdownOpen && (
                    <>
                      <StatusDropdownOverlay onClick={() => setIsStatusDropdownOpen(false)} />
                      <StatusDropdown>
                        {statusOptions.map((option, index) => (
                          <StatusDropdownOption 
                            key={option.value}
                            onClick={() => {
                              // 과제 체크 모달용 상태 변경
                              setSelectedStatus(option.value);
                              
                              // 과제 상태에 따른 포인트 자동 설정
                              let newPoints: number;
                              switch (option.value) {
                                case 'pending':
                                  newPoints = 0; // 미확인: 0 포인트
                                  break;
                                case 'confirmed':
                                  newPoints = getBasePoints(); // 확인: 과제 기본 포인트
                                  break;
                                case 'delayed':
                                  newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.delayed ?? 0);
                                  break;
                                case 'exempted':
                                  newPoints = getPointsByConfirmedPercent(getBasePoints(), statusDefaultPoints?.exempted ?? 0);
                                  break;
                                case 'submitted':
                                  newPoints = getBasePoints(); // 제출확인: 과제 기본 포인트
                                  break;
                                default:
                                  newPoints = 0;
                              }
                              setPointsInput(newPoints.toString());
                              
                              setIsStatusDropdownOpen(false);
                            }}
                            style={{
                              opacity: 1,
                              cursor: 'pointer'
                            }}
                          >
                            <StatusDropdownColorDot style={{ backgroundColor: option.bgColor }}>
                              {index + 1}
                            </StatusDropdownColorDot>
                            <StatusDropdownText>{option.label}</StatusDropdownText>
                          </StatusDropdownOption>
                        ))}
                      </StatusDropdown>
                    </>
                  )}
                </BatchInputGroup>
                
                <BatchInputGroup>
                  <BatchLabel>포인트</BatchLabel>
                  <BatchInput
                    tabIndex={3}
                    data-field="points-input"
                    type="text"
                    value={pointsInput}
                    onChange={(e) => setPointsInput(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                      if (!pointsInput.trim()) {
                        setPointsInput('0');
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        moveToNextEnabledField(3);
                      }
                    }}
                    disabled={!isPointsEnabled}
                  />
                </BatchInputGroup>
              </BatchInputContainer>
              
              {/* 메모 입력 필드 */}
              {isMemoEnabled && (
                <MemoInputSection>
                  <KeyboardHelperMemoInput
                    ref={memoInputRef}
                    tabIndex={4}
                    data-field="memo-input"
                    placeholder="과제 메모를 입력하세요..."
                    value={memoInput}
                    onChange={handleKeyboardHelperMemoChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const submitButton = document.querySelector('[data-field="submit-button"]') as HTMLElement | null;
                        if (submitButton) {
                          submitButton.focus();
                        }
                      }
                    }}
                  />
                </MemoInputSection>
              )}
              
              <ButtonRow>
                <PrevStudentButton 
                  type="button"
                  aria-label="이전 멤버"
                  onClick={handlePrevStudentInCheckModal}
                  disabled={!canNavigateToPrevStudentInCheckModal()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </PrevStudentButton>
                
                <SubmitButton 
                  type="button"
                  tabIndex={5}
                  data-field="submit-button"
                  onMouseDown={() => handleCheckSubmit(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      // 🎯 Tab 키: 첫 번째 입력 필드로 포커스 이동
                      const firstInput = document.querySelector('input[tabindex="1"]') as HTMLInputElement;
                      if (firstInput) {
                        firstInput.focus();
                        console.log('🎯 Tab 키: 대상 입력 필드로 포커스 이동됨');
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      // 🎯 Enter 키: 과제 체크 실행
                      console.log('🎯 Enter 키: 과제 체크 실행 시작');
                      handleCheckSubmit(true);
                      // 🎯 Enter 후 포커스가 사라지는 문제 방지: 대상 필드로 포커스 복귀
                      setTimeout(() => {
                        const firstInput = document.querySelector('input[tabindex="1"]') as HTMLInputElement;
                        if (firstInput) {
                          firstInput.focus();
                          firstInput.select();
                          console.log('🎯 Enter 키: 대상 입력 필드로 포커스 복귀');
                        }
                      }, 50);
                    }
                  }}
                  title={selectedStudents.size > 0 
                    ? `선택멤버 입력 (${selectedStudents.size}명)`
                    : '입력'
                  }
                >
                  {selectedStudents.size > 0 
                    ? `선택멤버 입력 (${selectedStudents.size}명)`
                    : '입력'
                  }
                </SubmitButton>
                
                <NextStudentButton 
                  type="button"
                  aria-label="다음 멤버"
                  onClick={handleNextStudentInCheckModal}
                  disabled={!canNavigateToNextStudentInCheckModal()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </NextStudentButton>
              </ButtonRow>
              
              {/* 토글 버튼 옵션 섹션 - 해당 섹션 맨 마지막 행 */}
              <OptionsContainer>
                <AutoIncrementSection>
                  <AutoIncrementToggle
                    type="button"
                    onClick={() => {
                  const newValue = !isAutoIncrementEnabled;
                  console.log('🔄 연속 토글 버튼 클릭:', { 
                    이전값: isAutoIncrementEnabled, 
                    새값: newValue 
                  });
                  setIsAutoIncrementEnabled(newValue);
                }}
                    $isEnabled={isAutoIncrementEnabled}
                    title={isAutoIncrementEnabled ? '연속 번호 입력 활성화' : '연속 번호 입력 비활성화'}
                  />
                  <AutoIncrementLabel>
                    <span className="full-text">연속 번호 입력하기</span>
                    <span className="short-text">연속 번호</span>
                  </AutoIncrementLabel>
                  
                  <ToggleButton
                    type="button"
                    onClick={() => {
                      const newEnabled = !isPointsEnabled;
                      setIsPointsEnabled(newEnabled);
                      
                      if (newEnabled) {
                        // 포인트 활성화 시: 미확인이 선택되어 있으면 확인으로 변경하고 포인트도 자동 설정
                        if (selectedStatus === 'pending') {
                          setSelectedStatus('confirmed');
                          setPointsInput(getBasePoints().toString());
                        }
                        // 과제 상태 필드는 비활성화하지 않음 (미확인만 선택 불가)
                      } else {
                        // 포인트 비활성화 시: 과제 상태 필드 완전 활성화
                        // 현재 선택된 상태에 따른 포인트 자동 설정
                        handleStatusChange(selectedStatus);
                      }
                    }}
                    $isEnabled={isPointsEnabled}
                    title={isPointsEnabled ? '포인트 수동 입력 활성화' : '포인트 자동 설정'}
                  />
                  <TogglePointsLabel>
                    <span className="full-text">포인트 입력 활성화</span>
                    <span className="short-text">포인트 입력</span>
                  </TogglePointsLabel>
                  
                  <MemoToggleButton
                    type="button"
                    onClick={() => setIsMemoEnabled(!isMemoEnabled)}
                    $isEnabled={isMemoEnabled}
                    title={isMemoEnabled ? '메모 기능 활성화' : '메모 기능 비활성화'}
                  />
                  <ToggleMemoLabel>
                    <span className="full-text">메모 기능 활성화</span>
                    <span className="short-text">메모 기능</span>
                  </ToggleMemoLabel>
                </AutoIncrementSection>
              </OptionsContainer>
               </>
             )}
            </BatchInputSection>
          </Section>

          {/* 버튼 영역 */}
            <ButtonContainer>
              <CloseButton data-variant="modal-secondary" onClick={handleClose} disabled={isFinalSaveInProgress}>닫기</CloseButton>
              <SaveButton type="button" onClick={handleSave} disabled={isFinalSaveInProgress} aria-busy={isFinalSaveInProgress}>
                {isFinalSaveInProgress ? (
                  <>
                    <SaveButtonSpinner aria-hidden />
                    저장 중
                  </>
                ) : (
                  saveButtonText
                )}
              </SaveButton>
            </ButtonContainer>
        </div>
        </Content>
      </Wrapper>
      {/* 멤버별 기록 편집 모달 */}
      {isMemoEditModalOpen && (
        <>
          <MemoEditOverlay onClick={closeMemoEditModal} />
          <MemoEditContainer ref={memoEditContainerRef}>
            <MemoEditHeader>
              <MemoEditTitle>
                {editingMemoStudent && (() => {
                  const student = members.find(m => m.id === editingMemoStudent);
                  return student ? `${student.number}. ${student.name}` : '멤버 기록 편집';
                })()}
              </MemoEditTitle>
              <MemoEditNavigation>
                <PrevStudentButton 
                  type="button"
                  aria-label="이전 멤버"
                  onClick={handlePrevStudent}
                  disabled={!canNavigateToPrevStudent()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </PrevStudentButton>
                <NextStudentButton 
                  type="button"
                  aria-label="다음 멤버"
                  onClick={handleNextStudent}
                  disabled={!canNavigateToNextStudent()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </NextStudentButton>
              </MemoEditNavigation>
            </MemoEditHeader>
            <MemoEditContent>
                            <StatusButtonsRow>
                {statusOptions.map((option, index) => (
                  <StatusButton
                    key={option.value}
                    $isSelected={editingMemoStatus === option.value}
                    onClick={() => {
                      if (!editingMemoStudent) return;
                      handleStatusChange(option.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // 포인트 필드로 포커스 이동
                        const pointsField = document.querySelector('.BatchInput') as HTMLInputElement;
                        if (pointsField) {
                          pointsField.focus();
                        }
                      }
                    }}
                  >
                    <StatusButtonColorDot 
                      $isSelected={editingMemoStatus === option.value}
                      style={{ 
                        '--original-color': option.bgColor,
                        backgroundColor: editingMemoStatus === option.value ? option.bgColor : 'var(--color-border)'
                      } as React.CSSProperties}
                    >
                      {index + 1}
                    </StatusButtonColorDot>
                    <StatusButtonText $isSelected={editingMemoStatus === option.value}>
                      {option.label}
                    </StatusButtonText>
                  </StatusButton>
                ))}
              </StatusButtonsRow>
              
              <BatchInputGroup>
                <BatchLabel>포인트</BatchLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <PointsMinusBtn 
                    type="button" 
                    aria-label="포인트 빼기"
                    onClick={() => {
                      if (editingMemoStudent) {
                        const currentPoints = studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints || 0;
                        // 🎯 루틴 과제는 과제 기본 포인트 단위, 일반 과제는 환경설정 단위
                        const decrement = mode === 'routine' ? (routine?.basePoints || 1) : (statusDefaultPoints?.confirmed || 1);
                        const newPoints = Math.max(0, currentPoints - decrement);
                        // 로컬 상태 업데이트 (UI에 즉시 반영)
                        setStudentStatuses(prev => 
                          prev.map(s => 
                            s.studentId === editingMemoStudent 
                              ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
                              : s
                          )
                        );
                        // 변경사항 추적 (trackChange 함수 사용)
                        trackChange(editingMemoStudent, { earnedPoints: newPoints });
                      }
                    }}
                  >
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </PointsMinusBtn>
                  <div style={{ flex: 1 }}>
                    <BatchInput
                      key={editingMemoStudent ? `memo-pts-${editingMemoStudent}-${studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints ?? '_'}` : 'memo-pts-none'}
                      className="memo-edit-points-input"
                      type="text"
                      value={editingMemoStudent ? (studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints ?? 0).toString() : '0'}
                      onChange={(e) => {
                        if (editingMemoStudent) {
                          const newPoints = parseInt(e.target.value) || 0;
                          // 로컬 상태 업데이트 (UI에 즉시 반영)
                          setStudentStatuses(prev => 
                            prev.map(s => 
                              s.studentId === editingMemoStudent 
                                ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
                                : s
                            )
                          );
                          // 변경사항 추적 (trackChange 함수 사용)
                          trackChange(editingMemoStudent, { earnedPoints: newPoints });
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      onKeyPress={(e) => {
                        console.log('⌨️ 대상 필드 onKeyPress:', e.key);
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleTabOnTargetField(); // 🎯 Enter 키도 탭처럼 작동 (대상 찾기 및 선택)
                        }
                      }}
                      onKeyDown={(e) => {
                        console.log('⌨️ 대상 필드 onKeyDown:', e.key);
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          // 🎯 Tab 키: 대상 찾기 및 선택
                          handleTabOnTargetField();
                        }
                      }}
                    />
                  </div>
                  <PointsPlusBtn 
                    type="button" 
                    aria-label="포인트 더하기"
                    onClick={() => {
                      if (editingMemoStudent) {
                        const currentPoints = studentStatuses.find(s => s.studentId === editingMemoStudent)?.earnedPoints || 0;
                        // 🎯 루틴 과제는 과제 기본 포인트 단위, 일반 과제는 환경설정 단위
                        const increment = mode === 'routine' ? (routine?.basePoints || 1) : (statusDefaultPoints?.confirmed || 1);
                        const newPoints = currentPoints + increment;
                        // 로컬 상태 업데이트 (UI에 즉시 반영)
                        setStudentStatuses(prev => 
                          prev.map(s => 
                            s.studentId === editingMemoStudent 
                              ? { ...s, earnedPoints: newPoints, updatedAt: new Date() }
                              : s
                          )
                        );
                        // 변경사항 추적 (trackChange 함수 사용)
                        trackChange(editingMemoStudent, { earnedPoints: newPoints });
                      }
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </PointsPlusBtn>
                </div>
              </BatchInputGroup>
              
              <MemoEditTextarea
                className="MemoEditTextarea"
                value={editingMemoValue}
                onChange={handleEditingMemoChange}
                placeholder="메모를 입력하세요..."
                rows={4}
                style={{ marginTop: '8px' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    // 포커스 제거 (메모 입력 완료)
                    (e.target as HTMLTextAreaElement).blur();
                  }
                }}
              />
              
              {editingMemoStudent && (() => {
                const studentStatus = studentStatuses.find(s => s.studentId === editingMemoStudent) as any;
                
                // 캐시된 제출 확인 데이터가 있으면 우선 사용
                const cachedSubmission = submissionChangeTracker[editingMemoStudent];
                const submissionLink = cachedSubmission ? cachedSubmission.link : (studentStatus?.submissionLink || '');
                const submissionMessage = cachedSubmission ? cachedSubmission.message : (studentStatus?.submissionMessage || '');
                
                if (submissionLink || cachedSubmission) {
                  return (
                    <SubmissionSection>
                      <SubmissionHeader>
                        <SubmissionTitleWrapper>
                          <SubmissionSectionTitle>제출 확인</SubmissionSectionTitle>
                        </SubmissionTitleWrapper>
                        <SubmissionActionButtons>
                          <SubmissionActionButton
                            type="button"
                            title="수정"
                            onClick={() => setIsEditingSubmission(!isEditingSubmission)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </SubmissionActionButton>
                          <SubmissionActionButton
                            type="button"
                            title="삭제"
                            onClick={() => setIsSubmissionDeleted(!isSubmissionDeleted)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </SubmissionActionButton>
                        </SubmissionActionButtons>
                      </SubmissionHeader>
                      
                      {isEditingSubmission ? (
                        <SubmissionEditForm>
                          <SubmissionEditInput
                            type="text"
                            placeholder="링크 URL"
                            value={editedSubmissionLink}
                            onChange={(e) => setEditedSubmissionLink(e.target.value)}
                          />
                          <SubmissionEditTextarea
                            placeholder="메시지"
                            value={editedSubmissionMessage}
                            onChange={(e) => setEditedSubmissionMessage(e.target.value)}
                          />
                          <SubmissionEditActions>
                            <SubmissionEditButton
                              type="button"
                              onClick={() => {
                                setEditedSubmissionLink(submissionLink || '');
                                setEditedSubmissionMessage(submissionMessage || '');
                                setIsEditingSubmission(false);
                              }}
                            >
                              취소
                            </SubmissionEditButton>
                            <SubmissionEditButton
                              type="button"
                              $primary
                              onClick={() => {
                                setIsEditingSubmission(false);
                              }}
                            >
                              수정
                            </SubmissionEditButton>
                          </SubmissionEditActions>
                        </SubmissionEditForm>
                      ) : (
                        <SubmissionLinkButton
                          href={editedSubmissionLink.startsWith('http://') || editedSubmissionLink.startsWith('https://') 
                            ? editedSubmissionLink 
                            : `https://${editedSubmissionLink}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          $isDeleted={isSubmissionDeleted}
                        >
                          {editedSubmissionMessage || '제출된 과제 링크 보기'}
                        </SubmissionLinkButton>
                      )}
                    </SubmissionSection>
                  );
                }
                return null;
              })()}
            </MemoEditContent>
            <MemoEditFooter>
              <MemoEditButton data-variant="modal-secondary" onClick={closeMemoEditModal}>닫기</MemoEditButton>
              <MemoEditSaveButton 
                onClick={saveMemo}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveMemo();
                  }
                }}
              >
                확인
              </MemoEditSaveButton>
            </MemoEditFooter>
          </MemoEditContainer>
        </>
      )}

      
      {/* 저장 완료 메시지 팝업 */}
      {isSaveSuccessModalOpen && (
        <>
          <SaveSuccessOverlay onClick={() => setIsSaveSuccessModalOpen(false)} />
          <SaveSuccessContainer>
            <SaveSuccessContent>
              <SaveSuccessIcon>✓</SaveSuccessIcon>
              <SaveSuccessTitle>저장이 완료되었습니다</SaveSuccessTitle>
            </SaveSuccessContent>
            <SaveSuccessFooter>
              <ContinueEditingButton onClick={handleContinueEditing}>
                편집 계속
              </ContinueEditingButton>
              <CloseModalButton onClick={handleCloseAfterSave}>
                편집 종료
              </CloseModalButton>
            </SaveSuccessFooter>
          </SaveSuccessContainer>
        </>
      )}
      
      {/* 변경사항 없음 메시지 팝업 */}
      {isNoChangesModalOpen && (
        <>
          <SaveSuccessOverlay onClick={() => setIsNoChangesModalOpen(false)} />
          <SaveSuccessContainer>
            <SaveSuccessContent>
              <NoChangesIcon>!</NoChangesIcon>
              <SaveSuccessTitle>저장할 변경사항이 없습니다</SaveSuccessTitle>
            </SaveSuccessContent>
            <SaveSuccessFooter>
              <CloseModalButton onClick={() => setIsNoChangesModalOpen(false)}>
                확인
              </CloseModalButton>
            </SaveSuccessFooter>
          </SaveSuccessContainer>
        </>
      )}
      
      {/* 메모 삭제 경고 팝업 */}
      {isMemoDeleteModalOpen && (
        <>
          <SaveSuccessOverlay onClick={() => setIsMemoDeleteModalOpen(false)} />
          <SaveSuccessContainer>
            <SaveSuccessContent>
              <NoChangesIcon>!</NoChangesIcon>
              <SaveSuccessTitle>선택된 멤버들의 메모를 삭제하시겠습니까?</SaveSuccessTitle>
            </SaveSuccessContent>
            <SaveSuccessFooter>
              <CloseModalButton onClick={() => setIsMemoDeleteModalOpen(false)}>
                취소
              </CloseModalButton>
              <ContinueEditingButton onClick={handleDeleteSelectedMemos}>
                삭제
              </ContinueEditingButton>
            </SaveSuccessFooter>
          </SaveSuccessContainer>
        </>
      )}
      
      {/* 학생 상세 모달 */}
      {isStudentDetailModalOpen && selectedStudentForDetail && (() => {
        const student = members.find(m => m.id === selectedStudentForDetail);
        const status = studentStatuses.find(s => s.studentId === selectedStudentForDetail);
        const cachedSubmission = submissionChangeTracker[selectedStudentForDetail];
        const draft = studentDetailDraft[selectedStudentForDetail];
        
        if (!student) return null;
        
        const studentName = student.name;
        const studentNumber = student.number || '';
        const currentStatus = draft?.status ?? status?.status ?? 'pending';
        const currentPoints = draft?.points ?? status?.earnedPoints ?? 0;
        const currentMemo = draft?.memo ?? status?.memo ?? '';
        
        // 과제 정보
        const assignmentTitle = mode === 'routine' 
          ? (routine?.title || '루틴 과제')
          : mode === 'project'
          ? (projectAssignment?.title || '프로젝트 과제')
          : (assignment?.title || '과제');
        
        const assignmentType: 'assignment' | 'project' | 'routine' = mode === 'routine' ? 'routine' : mode === 'project' ? 'project' : 'assignment';
        
        // 삭제 예정(draft)이어도 제출 데이터는 유지 — UI에서 오버레이만 표시, <> 탐색 시 상태 복원
        const submissionLink =
          draft?.submissionLink !== undefined
            ? draft.submissionLink
            : cachedSubmission
              ? cachedSubmission.link
              : (status as any)?.submissionLink || '';
        const submissionMessage =
          draft?.submissionMessage !== undefined
            ? draft.submissionMessage
            : cachedSubmission
              ? cachedSubmission.message
              : (status as any)?.submissionMessage || '';
        const submittedAt = (status as any)?.submittedAt ? new Date((status as any).submittedAt) : undefined;
        const initialIsDeleted =
          draft?.isDeleted ?? draft?.isImageDeleted ?? cachedSubmission?.isDeleted ?? false;
        const submissionImagePath =
          draft?.submissionImagePath ?? ((status as any)?.submissionImagePath as string | undefined);
        const submissionImageUpdatedAt =
          draft?.submissionImageUpdatedAt ??
          ((status as any)?.submissionImageUpdatedAt as string | undefined);
        const initialIsImageDeleted = draft?.isImageDeleted ?? draft?.isDeleted ?? false;
        
        // 네비게이션 정보
        const assignedStudents = members.filter(m => isStudentAssigned(m.id));
        const canNavigatePrev = currentStudentIndex > 0;
        const canNavigateNext = currentStudentIndex < assignedStudents.length - 1;
        
        return (
          <StudentDetailModal
            key={selectedStudentForDetail}
            isOpen={isStudentDetailModalOpen}
            onClose={closeStudentDetailModal}
            onSave={handleStudentDetailModalSave}
            studentName={studentName}
            studentNumber={studentNumber}
            currentStatus={currentStatus}
            currentPoints={currentPoints}
            currentMemo={currentMemo}
            assignmentTitle={assignmentTitle}
            assignmentType={assignmentType}
            projectTitle={mode === 'project' ? projectAssignment?.title : undefined}
            subTaskTitle={mode === 'project' && currentSubTask ? currentSubTask.title : undefined}
            routineTitle={mode === 'routine' ? routine?.title : undefined}
            dateId={mode === 'routine' && selectedDate ? selectedDate.toISOString().split('T')[0] : undefined}
            onNavigate={handleStudentNavigation}
            canNavigatePrev={canNavigatePrev}
            canNavigateNext={canNavigateNext}
            defaultPoints={defaultPoints}
            statusDefaultPoints={statusDefaultPoints}
            assignmentBasePoints={getBasePoints()}
            submissionLink={submissionLink}
            submissionMessage={submissionMessage}
            submittedAt={submittedAt}
            submissionImagePath={submissionImagePath}
            submissionImageUpdatedAt={submissionImageUpdatedAt}
            initialIsDeleted={initialIsDeleted}
            initialIsImageDeleted={initialIsImageDeleted}
            elevatedStack
            submissionFeaturesEnabled={isStandardPlanOrAbove(userType)}
          />
        );
      })()}
      
      {/* 토스트 메시지 */}
      {showToast && (
        <Toast 
          message={toastMessage} 
          onClose={() => setShowToast(false)} 
          duration={1000}
        />
      )}
      
      {/* 모달 닫기 확인 팝업 */}
      <ConfirmModal
        isOpen={isCloseConfirmModalOpen}
        onClose={handleCloseWarningOnly}
        onCancel={handleDiscardAndClose}
        onConfirm={handleSaveAndCloseFromWarning}
        title={['변경된 데이터가 있습니다.', '저장하지 않고 닫으시겠습니까?']}
        cancelText="저장하지 않고 닫기"
        confirmText="저장하고 닫기"
        icon="!"
        iconBackground="var(--status-pending)"
      />
    </>
  );
};

export default AssignmentCheckModal;

// 스타일 컴포넌트들
const TitleActionButtons = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleActionButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SendToBoardButtonContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SendToBoardButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-accent-strong);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    width: 18px;
    height: 18px;
    
    path {
      stroke: #999;
    }
  }
`;

const SendToBoardTooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: var(--color-text-primary);
  white-space: normal;
  box-shadow: var(--modal-shadow);
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  text-align: center;
  line-height: 1.5;
  min-width: 140px;
  
  ${SendToBoardButtonContainer}:hover &,
  ${TitleActionButtonContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
  
  /* 화살표 */
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 12px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid var(--color-border);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 13px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid var(--color-surface);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1300;
`;

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1301;
  padding: 40px 20px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 20px 10px;
  }
`;

const Content = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  max-width: 680px;
  width: 100%;
  padding: 16px 24px 24px 24px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: auto;
  
  @media (max-width: 768px) {
    padding: 20px 16px 24px 16px;
  }
`;





const AssignmentInfo = styled.div`
  text-align: center;
  margin-bottom: 8px;
`;

const CategoryDisplay = styled.div<{ color?: string }>`
  display: inline-block;
  background-color: ${props => props.color || 'var(--color-border)'};
  color: ${props => props.color ? '#fff' : 'var(--color-check-modal-text-gray)'};
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  text-shadow: ${props => props.color ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'};
`;

const AssignmentTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const AssignmentTitleMetaContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const AssignmentTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
`;

const AssignmentMeta = styled.div`
  color: var(--color-check-modal-text-gray);
  font-size: 14px;
`;

const AssignmentMetaLine = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 6px;
`;

const AssignmentMetaRewardRow = styled.div`
  display: block;
  margin-top: 4px;
`;

const AssignmentMetaRewardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AssignmentMetaRewardItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const AssignmentMetaCoinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const AssignmentMetaItemCardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const AssignmentMetaRewardText = styled.span`
  font-size: inherit;
  font-weight: 600;
  color: inherit;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: var(--color-border);
  margin: 12px 0;
`;

const Section = styled.div`
  margin-bottom: 16px;
  background: var(--color-section-bg);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-section-border);
  
  &:first-of-type {
    margin-top: 4px;
  }
`;

const SectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
  font-weight: 600;
  background: var(--color-section-title-bg);
  padding: 3px 14px;
  border: 2px solid var(--color-section-title-border);
  border-radius: 20px;
  display: inline-block;
  flex: 1;
  text-align: center;
`;



const AssignmentNotesText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 8px 0 0 0;
  padding: 12px 16px;
  background-color: transparent;
  border-radius: 6px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
`;

const KeyboardHelperToggleButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin: 8px 0 16px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:active {
    background-color: var(--color-panel);
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const KeyboardHelperInfo = styled.div`
  font-size: 12px;
  color: var(--color-text-muted);
  text-align: center;
  margin: 0 0 16px 0;
  line-height: 1.4;
`;

const KeyboardHelperTitle = styled.h5`
  margin: 16px 0 16px 0;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-weight: 500;
  text-align: center;
`;

const CheckToolWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (min-width: 769px) {
    flex-direction: row;
    align-items: end;
    gap: 12px;
  }
`;

const CheckToolContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: end;
  flex: 1;
  
  @media (max-width: 600px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  width: 100%;
  
  @media (max-width: 600px) {
    gap: 8px;
  }
`;



const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToggleButton = styled.button<{ $isEnabled: boolean }>`
  position: relative;
  width: 50px;
  min-height: 1.4em;
  background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$isEnabled ? 'flex-end' : 'flex-start'};
  padding: 0.15em;
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  -webkit-focus-ring-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &::before {
    content: '';
    position: absolute;
    top: 0.15em;
    left: ${props => props.$isEnabled ? 'calc(100% - 1.3em)' : '0.15em'};
    width: 1.1em;
    height: 1.1em;
    background: var(--color-surface);
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: '${props => props.$isEnabled ? 'ON' : 'OFF'}';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.6rem;
    font-weight: bold;
    color: var(--color-check-modal-toggle-text);
    left: ${props => props.$isEnabled ? '1.2em' : 'calc(100% - 2.8em)'};
    transition: all 0.3s ease;
    line-height: 1;
    
    /* 작은 화면에서 간단한 텍스트 표시 */
    @media (max-width: 768px) {
      content: '${props => props.$isEnabled ? '포인트' : '포인트'}';
      font-size: 0.6rem;
      left: ${props => props.$isEnabled ? '40%' : '55%'};
      transform: translate(-50%, -50%);
    }
  }

  &:hover {
    background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
    opacity: 0.9;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  /* 작은 화면에서 토글 버튼 가로 길이 증가 */
  @media (max-width: 768px) {
    width: 60px;
  }
`;

const MemoToggleButton = styled.button<{ $isEnabled: boolean }>`
  position: relative;
  width: 50px;
  min-height: 1.4em;
  background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$isEnabled ? 'flex-end' : 'flex-start'};
  padding: 0.15em;
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  -webkit-focus-ring-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &::before {
    content: '';
    position: absolute;
    top: 0.15em;
    left: ${props => props.$isEnabled ? 'calc(100% - 1.3em)' : '0.15em'};
    width: 1.1em;
    height: 1.1em;
    background: var(--color-surface);
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: '${props => props.$isEnabled ? 'ON' : 'OFF'}';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.6rem;
    font-weight: bold;
    color: var(--color-check-modal-toggle-text);
    left: ${props => props.$isEnabled ? '1.2em' : 'calc(100% - 2.8em)'};
    transition: all 0.3s ease;
    line-height: 1;
    
    /* 작은 화면에서 간단한 텍스트 표시 */
    @media (max-width: 768px) {
      content: '${props => props.$isEnabled ? '메모' : '메모'}';
      font-size: 0.6rem;
      left: ${props => props.$isEnabled ? '40%' : '55%'};
      transform: translate(-50%, -50%);
    }
  }

  &:hover {
    background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
    opacity: 0.9;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  /* 작은 화면에서 토글 버튼 가로 길이 증가 */
  @media (max-width: 768px) {
    width: 60px;
  }
`;

const AutoIncrementToggle = styled.button<{ $isEnabled: boolean }>`
  position: relative;
  width: 50px;
  min-height: 1.4em;
  background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$isEnabled ? 'flex-end' : 'flex-start'};
  padding: 0.15em;
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  -webkit-focus-ring-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &::before {
    content: '';
    position: absolute;
    top: 0.15em;
    left: ${props => props.$isEnabled ? 'calc(100% - 1.3em)' : '0.15em'};
    width: 1.1em;
    height: 1.1em;
    background: var(--color-surface);
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: '${props => props.$isEnabled ? 'ON' : 'OFF'}';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.6rem;
    font-weight: bold;
    color: var(--color-check-modal-toggle-text);
    left: ${props => props.$isEnabled ? '1.2em' : 'calc(100% - 2.8em)'};
    transition: all 0.3s ease;
    line-height: 1;
    
    /* 작은 화면에서 간단한 텍스트 표시 */
    @media (max-width: 768px) {
      content: '${props => props.$isEnabled ? '연속' : '연속'}';
      font-size: 0.6rem;
      left: ${props => props.$isEnabled ? '40%' : '55%'};
      transform: translate(-50%, -50%);
    }
  }

  &:hover {
    background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
    opacity: 0.9;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  /* 작은 화면에서 토글 버튼 가로 길이 증가 */
  @media (max-width: 768px) {
    width: 60px;
  }
`;
const AutoIncrementSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 600px) {
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    gap: 3px;
  }
`;

const AutoIncrementLabel = styled.span`
  font-size: 14px;
  color: var(--color-check-modal-text-gray);
  font-weight: 500;
  
  .full-text {
    display: inline;
  }
  
  .short-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    display: none; /* 작은 화면에서 완전히 숨김 */
  }
  
  @media (max-width: 600px) {
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
  
  @media (max-width: 360px) {
    font-size: 11px;
  }
`;

const TogglePointsLabel = styled.span`
  font-size: 14px;
  color: var(--color-check-modal-text-gray);
  font-weight: 500;
  
  .full-text {
    display: inline;
  }
  
  .short-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    display: none; /* 작은 화면에서 완전히 숨김 */
  }
  
  @media (max-width: 600px) {
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
  
  @media (max-width: 360px) {
    font-size: 11px;
  }
`;

const ToggleMemoLabel = styled.span`
  font-size: 14px;
  color: var(--color-check-modal-text-gray);
  font-weight: 500;
  
  .full-text {
    display: inline;
  }
  
  .short-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    display: none; /* 작은 화면에서 완전히 숨김 */
  }
  
  @media (max-width: 600px) {
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
  
  @media (max-width: 360px) {
    font-size: 11px;
  }
`;

const BatchActionSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
`;

const BatchButton = styled.button<{ disabled?: boolean }>`
  padding: 10px 16px;
  background: ${props => props.disabled ? 'var(--color-border)' : '#007bff'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.disabled ? 'var(--color-border)' : '#0056b3'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }
`;

const SelectionInfo = styled.div`
  text-align: center;
  color: #007bff;
  font-size: 14px;
  font-weight: 500;
  margin-top: 12px;
  padding: 8px;
  background: #e3f2fd;
  border-radius: 6px;
  border: 1px solid #bbdefb;
`;

const BatchInputSection = styled.div`
  margin-top: 16px;
`;

const SelectAllSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const MemberViewModeToggle = styled.div<{ $isSeatMode: boolean }>`
  position: relative;
  display: flex;
  background: #BBBBBB;
  border-radius: 11px;
  padding: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 22px;
  min-width: 98px;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$isSeatMode ? '50%' : '2px')};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 9px;
    transition: left 0.2s ease;
    z-index: 1;
  }
`;

const MemberViewModeOption = styled.div<{ $isActive: boolean }>`
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: ${(p) => (p.$isActive ? '#BBBBBB' : 'var(--color-toggle-inactive)')};
  white-space: nowrap;
  padding: 0 8px;
`;

const SelectAllButton = styled.button`
  padding: 3px 7px;
  background: var(--color-surface);
  color: var(--color-dark-gray-text);
  border: 1px solid var(--color-dark-gray-text);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 19px;
  margin: 0;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const BatchInputTitle = styled.h4`
  text-align: center;
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const BatchInputContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
  
  @media (max-width: 360px) {
    gap: 8px;
  }
`;

const BatchInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  padding-top: 8px;
  margin-bottom: 8px;
`;

const MemoInputSection = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
`;

const OptionsContainer = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px 16px 8px 16px;
  margin: 16px 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const MemoInput = styled.textarea`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  min-width: 0;
  text-align: left;
  resize: none;
  overflow: hidden;
  min-height: 38px;
  max-height: 200px;
  line-height: 1.4;
  font-family: inherit;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const KeyboardHelperMemoInput = styled.input`
  width: 100%;
  height: 38px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.4;
  box-sizing: border-box;
  font-family: inherit;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(95, 133, 129, 0.1);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:disabled {
    background: var(--color-panel);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }
`;

const BatchLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-align: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 2px 6px;
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  white-space: nowrap;
`;

const BatchStatusSelect = styled.select`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  background: var(--color-surface);
  font-weight: normal;
  width: 100%;
  min-width: 0;
  text-align: center;
  transition: all 0.2s ease;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  font-family: inherit;
  
  &:focus-visible {
    outline: none;
    border-color: #000;
  }
  
  &:disabled {
    background: var(--color-panel);
    color: var(--color-check-modal-text-gray);
    cursor: not-allowed;
    border-color: var(--color-border);
  }
`;

const BatchInput = styled.input`
  padding: 8px 4px;
  padding-left: 4px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: normal;
  width: 100%;
  min-width: 0;
  text-align: center;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  font-family: inherit;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  transition: all 0.15s ease-in-out; /* 🎯 부드러운 전환 효과 */
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    background: var(--color-panel);
    color: var(--color-text-muted);
    cursor: not-allowed;
    border-color: var(--color-border);
  }
  
  &::placeholder {
    font-size: 14px;
    font-weight: normal;
    color: var(--color-text-muted);
    transition: opacity 0.15s ease-in-out; /* 🎯 플레이스홀더 부드러운 전환 */
  }
  
  /* 화면 가로 420px 이하일 때 텍스트 크기 작게 */
  @media (max-width: 420px) {
    font-size: 12px;
    
    &::placeholder {
      font-size: 12px;
    }
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  min-width: 0;
  text-align: center
  
  &:focus-visible {
    outline: none;
    border-color: #000;
  }
`;

const StatusSelect = styled.select<{ style?: React.CSSProperties }>`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  background: var(--color-surface);
  font-weight: 500;
  width: 100%;
  min-width: 0;
  transition: all 0.2s ease;
  text-align: center;
  
  &:focus-visible {
    outline: none;
    border-color: #000;
  }
  
  &:disabled {
    background: var(--color-panel);
    color: var(--color-check-modal-text-gray);
    cursor: not-allowed;
    border-color: var(--color-border);
  }
`;





const SubmitButton = styled.button`
  flex: 1;
  height: 32px;
  border-radius: 20px;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  padding: 0 16px;
  font-size: 12px;
  font-weight: 500;
  min-width: 0;
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StudentGridContainer = styled.div<{ $isMemoViewMode?: boolean }>`
  margin: 0 -16px;
  padding: 0 16px;
  width: calc(100% + 32px);
  
  @media (max-width: 768px) {
    margin: 0 -16px;
    padding: 0 16px;
    width: calc(100% + 32px);
  }
  
  @media (max-width: 600px) {
    margin: 0 -16px;
    padding: 0 16px;
    width: calc(100% + 32px);
  }
  
  @media (max-width: 480px) {
    margin: 0 -16px;
    padding: 0 16px;
    width: calc(100% + 32px);
  }
`;

const MemoViewLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const MemoViewRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const MemoViewStudentItemWrapper = styled.div`
  flex: 0 0 20%;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: 0 0 100%;
    width: 100%;
  }
`;

const MemoViewMemoFieldWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: stretch;
  
  @media (max-width: 768px) {
    flex: 0 0 100%;
    width: 100%;
  }
`;

const MemoViewMemoTextarea = styled.textarea`
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding: 6px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  line-height: 28px;
  font-family: inherit;
  background: var(--color-surface);
  color: var(--color-text-primary);
  resize: none;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(95, 133, 129, 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  &:disabled {
    background: var(--color-panel);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }
`;


const StudentGrid = styled.div<{ $numColumns: number }>`
  display: flex;
  justify-content: ${props => props.$numColumns <= 4 ? 'center' : 'flex-start'};
  gap: 14px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
  
  @media (max-width: 600px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const SeatModeArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SeatModeScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: transparent;
  padding: 0 12px;
`;

const SeatModeGrid = styled.div<{ $gridTemplateColumns: string; $gridTemplateRows: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$gridTemplateColumns};
  grid-template-rows: ${(p) => p.$gridTemplateRows};
  gap: 0;
  width: 100%;
  min-width: 100%;
  padding: 8px 4px;
  align-content: start;
  background: transparent;
`;

const SeatModeCell = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 40px;
`;

const SeatUnassignedTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-align: center;
`;

const SeatModeRotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 4px;
`;

const SeatModeRotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:active {
    transform: none;
  }
`;

const SeatModeRotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

const SeatModeRotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  :root[data-theme='light'] & svg {
    stroke: #717785;
  }

  ${SeatModeRotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const SeatModeRotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  :root[data-theme='light'] & {
    color: #717785;
  }

  ${SeatModeRotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const StudentColumn = styled.div<{ $numColumns: number }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: ${props => props.$numColumns <= 4 ? '0 0 calc(25% - 10.5px)' : '1'};
  min-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 10.5px)' : '0'};
  max-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 10.5px)' : 'none'};
  
  @media (max-width: 768px) {
    gap: 12px;
    flex: ${props => props.$numColumns <= 4 ? '0 0 calc(25% - 9px)' : '1'};
    min-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 9px)' : '0'};
    max-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 9px)' : 'none'};
  }
  
  @media (max-width: 600px) {
    gap: 12px;
    flex: ${props => props.$numColumns <= 4 ? '0 0 calc(25% - 7.5px)' : '1'};
    min-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 7.5px)' : '0'};
    max-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 7.5px)' : 'none'};
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    flex: ${props => props.$numColumns <= 4 ? '0 0 calc(25% - 6px)' : '1'};
    min-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 6px)' : '0'};
    max-width: ${props => props.$numColumns <= 4 ? 'calc(25% - 6px)' : 'none'};
  }
`;

const StudentItem = styled.div<{ $status?: AssignmentStatusValue; $isAssigned?: boolean; $isSelected?: boolean; $hasReward?: boolean; $rewardStatusColor?: string }>`
  border: none;
  border-radius: 6px;
  padding: 0;
  background: ${props => {
    if (props.$hasReward && props.$rewardStatusColor) {
      // 흰색 배경에 30% 투명도로 혼합된 색상 계산
      const hex = props.$rewardStatusColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // 검정색(#000000)의 경우 반투명한 검정색으로 표시 (60% 투명도)
      if (r === 0 && g === 0 && b === 0) {
        // 검정색을 60% 투명도로 흰색과 혼합 → 어두운 회색
        const blendedR = Math.round(0 * 0.6 + 255 * 0.4);
        const blendedG = Math.round(0 * 0.6 + 255 * 0.4);
        const blendedB = Math.round(0 * 0.6 + 255 * 0.4);
        return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
      }
      
      // 흰색(#FFFFFF) 배경에 30% 투명도로 혼합
      // 결과 = 색상 * 0.3 + 흰색(255) * 0.7
      const blendedR = Math.round(r * 0.3 + 255 * 0.7);
      const blendedG = Math.round(g * 0.3 + 255 * 0.7);
      const blendedB = Math.round(b * 0.3 + 255 * 0.7);
      return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
    }
    return '#ffffff';
  }};
  transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
  cursor: ${props => props.$isAssigned ? 'pointer' : 'not-allowed'};
  box-sizing: border-box;
  opacity: ${props => props.$isAssigned ? 1 : 0.5};
  position: relative;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  ${props => props.$isAssigned && `
    &:hover {
      box-shadow: 0 4px 8px rgba(0,0,0,0.15);
      transform: translateY(-1px);
    }
    
    &:focus {
      outline: none;
      box-shadow: none;
    }
    
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  `}
  
  @media (max-width: 600px) {
    padding: 1px;
    height: 40px;
    min-height: 40px;
    max-height: 40px;
  }
  
  @media (max-width: 480px) {
    padding: 1px;
    height: 40px;
    min-height: 40px;
    max-height: 40px;
  }
`;

const StudentItemBorder = styled.div<{ $isAssigned?: boolean; $isSelected?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid #6c757d;
  border-radius: 6px;
  background: ${props => props.$isSelected ? 'rgba(0, 0, 0, 0.35)' : 'transparent'};
  pointer-events: none;
  z-index: 2;
  transition: all 0.2s ease;
  
  ${props => props.$isAssigned && `
    &:hover {
      border: 2px solid #000000;
    }
  `}
`;

const TopRightCheckIcon = styled.div<{ $isSelected: boolean }>`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: ${props => props.$isSelected ? 1 : 0};
  transition: opacity 0.2s ease;

  svg {
    width: 8px;
    height: 8px;
    fill: #007bff;
  }
`;

const MemoIndicator = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border: 1px solid #6c757d;
  
  &::before {
    content: 'M';
    font-size: 10px;
    font-weight: 700;
    color: #333333;
    line-height: 1;
  }
`;



// 메모 편집 모달 스타일
const MemoEditOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1420;
  
  @media (max-width: 768px) {
    position: fixed;
    background: rgba(0, 0, 0, 0.3);
  }
`;

const MemoEditContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 1421;
  width: 90vw;
  max-width: 600px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
    max-height: 90vh;
    overflow-y: auto;
    width: calc(100vw - 40px);
  }
  
  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    max-height: 85vh;
  }
`;

const MemoEditHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--color-border);
  position: relative;
`;

const MemoEditTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const MemoEditCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-check-modal-text-gray);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    color: var(--color-text-secondary);
  }
`;

const MemoEditContent = styled.div`
  padding: 20px 24px;
`;

const MemoEditTextarea = styled.textarea`
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--color-border);
    box-shadow: none;
  }
`;

const SubmissionSection = styled.div`
  width: 100%;
  margin-top: 20px;
  background: rgba(var(--color-accent-rgb), 0.1);
  border-radius: 12px;
  padding: 16px;
`;

const SubmissionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  position: relative;
`;

const SubmissionTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const SubmissionSectionTitle = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
  background: var(--color-surface)fff;
  border: 2px solid var(--color-accent-strong);
  border-radius: 20px;
  padding: 4px 16px;
  width: fit-content;
`;

const SubmissionActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  right: 0;
`;
const SubmissionActionButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--color-check-modal-text-gray);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: ${props => props.title === '삭제' ? '#dc3545' : '#007bff'};
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SubmissionLinkButton = styled.a<{ $isDeleted?: boolean }>`
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.$isDeleted ? 'var(--color-panel)' : 'var(--color-surface)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: ${props => props.$isDeleted ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  text-decoration: ${props => props.$isDeleted ? 'line-through' : 'none'};
  cursor: ${props => props.$isDeleted ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.6;
  min-height: 44px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  pointer-events: ${props => props.$isDeleted ? 'none' : 'auto'};

  &:hover {
    background-color: ${props => props.$isDeleted ? 'var(--color-panel)' : 'var(--color-panel)'};
    border-color: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-text-muted)'};
    box-shadow: ${props => props.$isDeleted ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SubmissionEditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmissionEditInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const SubmissionEditTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const SubmissionEditActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const SubmissionEditButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 6px;
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'rgba(var(--color-accent-rgb), 0.9)' : 'var(--color-panel)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MemoEditFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--color-border);
`;

const MemoEditButton = styled.button`
  padding: 10px 20px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-check-modal-text-gray);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }
`;

const MemoEditSaveButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: var(--color-accent-strong);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 2px;
  align-items: center;
  
  @media (max-width: 600px) {
    justify-content: center;
    margin-bottom: 1px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1px;
  }
`;

const StudentNumber = styled.span<{ $hasSubmission?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 55%;
  border-radius: 8px 8px 0 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  padding: 2px 6px;
  overflow: hidden;
  ${props => props.$hasSubmission && `
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 60%;
      background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.55) 100%);
      pointer-events: none;
    }
    .number, .name { position: relative; z-index: 1; }
  `}
  
  .number {
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    text-align: left;
    flex-shrink: 0;
  }
  
  .name {
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    flex: 1;
    margin: 0 3px;
  }
  
  @media (max-width: 600px) {
    padding: 2px 4px;
    
    .number {
      font-size: 13px;
    }
    
    .name {
      font-size: 12px;
      margin: 0 2px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 2px 3px;
    
    .number {
      font-size: 12px;
    }
    
    .name {
      font-size: clamp(8px, 2.2vw, 12px);
      margin: 0 2px;
    }
  }
`;

const StudentName = styled.span`
  font-weight: 500;
  color: #000;
  font-size: 13px;
  
  @media (max-width: 600px) {
    display: none;
  }
`;

// 메모 표시 스타일 (범례 교체용)
const MemoDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px; /* StatusButton과 정확히 동일한 높이 */
  min-height: 32px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 6px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 420px) {
    height: 28px;
    min-height: 28px;
    padding: 4px 8px;
  }
  
  @media (max-width: 480px) {
    height: 28px;
    min-height: 28px;
    padding: 4px 8px;
  }
`;

const MemoDisplay = styled.div`
  color: var(--color-text-secondary);
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  width: 100%;
  max-width: 100%;
  
  /* 한 줄이 넘어가는 경우 ... 처리 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* 말줄임표가 작동하도록 최대 너비 제한 */
  min-width: 0;
  flex: 1;
  
  /* 말줄임표 강제 적용 */
  word-break: break-all;
  
  /* 반응형 처리 */
  @media (max-width: 768px) {
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
  justify-content: center;
  height: 32px;
  min-height: 32px;
  align-items: center;
  width: 100%;
  
  /* 반응형 처리 */
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    height: 28px;
    min-height: 28px;
  }
`;



const StudentControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 45%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 1px;
  
  @media (max-width: 600px) {
    padding: 1px;
    gap: 1px;
  }
  
  @media (max-width: 480px) {
    padding: 1px;
    gap: 1px;
  }
`;

// 멤버 번호 포맷팅 함수 (1 -> 01)
const formatStudentNumber = (number: string): string => {
  const num = parseInt(number);
  return num < 10 ? `0${num}` : `${num}`;
};

// 포인트 포맷팅 함수 (1000 -> 1Kp)
const formatPoints = (points: number): React.ReactElement => {
  if (points >= 1000) {
    return (
      <>
        <span className="points-number">{Math.floor(points / 1000)}K</span>
        <span className="points-unit">p</span>
      </>
    );
  }
  return (
    <>
      <span className="points-number">{points}</span>
      <span className="points-unit">p</span>
    </>
  );
};

const PointsText = styled.div<{ $isAssigned?: boolean; $isEditing?: boolean; $hasReward?: boolean; $rewardStatusColor?: string }>`
  width: 100%;
  height: 100%;
  padding: 1px 3px;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  text-align: center;
  background: transparent;
  color: ${props => {
    // 보상을 받았고 검정색 배경인 경우 흰색 텍스트
    if (props.$hasReward && props.$rewardStatusColor) {
      const hex = props.$rewardStatusColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      // 검정색인 경우 흰색 텍스트
      if (r === 0 && g === 0 && b === 0) {
        return '#ffffff';
      }
    }
    return '#515151';
  }};
  cursor: ${props => props.$isAssigned ? 'pointer' : 'not-allowed'};
`;

const InlinePointsInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  outline: none !important;
  box-shadow: none !important;
  
  &:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: var(--color-border);
  }
  
  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  transition: all 0.2s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .points-number {
    font-size: 12px;
    font-weight: 700;
    color: var(--color-check-modal-text-gray);
    margin-right: 1px;
  }
  
  .points-unit {
    font-size: 9px;
    font-weight: 700;
    color: var(--color-check-modal-text-gray);
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
  
  @media (max-width: 600px) {
    padding: 1px 2px;
    font-size: 9px;
    
    .points-number {
      font-size: 11px;
    }
    
    .points-unit {
      font-size: 8px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1px 2px;
    font-size: 8px;
    
    .points-number {
      font-size: 10px;
    }
    
    .points-unit {
      font-size: 7px;
    }
  }
`;



/* 클래스챗 페이지 새 문제 생성 모달 취소하기/저장하기 버튼 스타일과 동일 */
const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const CloseButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 2px solid var(--color-text-muted);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: #6c757d;
  }

  &:disabled {
    background: var(--color-panel);
    border-color: var(--color-border);
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 일반 과제 보상 버튼 컨테이너
const AssignmentRewardButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 8px 0 16px 0;
`;

// 일반 과제 보상 지급 버튼
const AssignmentRewardGrantButton = styled.button`
  padding: 6px 16px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 일반 과제 보상 회수 버튼
const AssignmentRewardRevokeButton = styled.button`
  padding: 6px 16px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const saveButtonSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SaveButtonSpinner = styled.span`
  width: 15px;
  height: 15px;
  border: 2px solid var(--color-block-border);
  border-top-color: #ffffff;
  border-radius: 50%;
  box-sizing: border-box;
  animation: ${saveButtonSpin} 0.65s linear infinite;
  flex-shrink: 0;
`;

const SaveButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--color-accent-strong);
  color: white;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.9);
    border-color: rgba(var(--color-accent-rgb), 0.9);
  }

  &:disabled {
    background: var(--color-text-muted);
    border-color: var(--color-text-muted);
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* 새 문제 생성 모달과 동일: 라이트 모드에서 비활성화 시 연한 회색 #c6c6c6 (불투명도 없이 동일 표현) */
  :root[data-theme='light'] &:disabled {
    background: #c6c6c6;
    border-color: var(--color-border-strong);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;


// 저장 완료 메시지 팝업 스타일
const SaveSuccessOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1420;
`;

const SaveSuccessContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  z-index: 1421;
  width: 90vw;
  max-width: 400px;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const SaveSuccessContent = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const SaveSuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 20px;
`;

const NoChangesIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--status-exempted);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 20px;
`;

const SaveSuccessTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const SaveSuccessFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;

const ContinueEditingButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-accent-text);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CloseModalButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DeleteAllMemosButton = styled.button`
  padding: 4px 16px;
  background: var(--color-surface)fff;
  color: var(--color-check-modal-text-gray);
  border: 1px solid var(--color-check-modal-text-gray);
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  width: 100%;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    background: var(--color-panel);
    color: var(--color-text-muted);
    border-color: var(--color-border);
    cursor: not-allowed;
  }
`;

// 커스텀 과제 상태 선택 버튼
const StatusSelectButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 38px;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
  }
  
  &:focus-visible {
    outline: none;
    border-color: #000;
  }
  
  &:disabled {
    background: var(--color-border);
    color: var(--color-check-modal-text-gray);
    cursor: not-allowed;
  }
  
  .status-content {
    display: flex;
    align-items: center;
    flex: 1;
  }
  
  .color-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    font-weight: 700;
    font-size: 11px;
    color: white;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }
  
  .status-text {
    font-size: 14px;
    font-weight: 500;
  }
  
  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    flex-shrink: 0;
  }
`;

const StatusDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1400;
`;

const StatusDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: var(--modal-shadow);
  z-index: 1401;
  margin-bottom: 4px;
  overflow: hidden;
  
  @media (max-width: 420px) {
    min-width: 180px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const StatusDropdownOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background-color: var(--color-panel);
    transform: scale(1.02);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
  
  .color-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-weight: 700;
    font-size: 16px;
    color: white;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }
  
  .status-text {
    font-size: 14px;
    font-weight: 500;
  }
`;
// 개선된 상태 팝업 스타일 컴포넌트들
// 개선된 상태 필드 스타일 컴포넌트들
const BatchStatusField = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    background: var(--color-border);
    cursor: not-allowed;
  }
`;

const StatusFieldContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const StatusFieldColorDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
`;

const StatusFieldText = styled.span`
  font-size: 14px;
  color: var(--color-text-primary);
`;

const StatusFieldArrow = styled.span<{ $isOpen?: boolean }>`
  font-size: 12px;
  color: var(--color-check-modal-text-gray);
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const StatusDropdownColorDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
`;

const StatusDropdownText = styled.span`
  font-size: 14px;
  color: var(--color-text-primary);
`;

// 과제 상태 인풋 필드 스타일
const BatchStatusInput = styled.input`
  width: 100%;
  padding: 8px 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  cursor: pointer;
  font-size: 14px;
  color: transparent;
  transition: all 0.2s ease;
  position: relative;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  
  /* 텍스트 선택 방지 */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  @media (max-width: 420px) {
    padding: 8px 2px;
  }
  

  
  &:hover {
    border-color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    background: var(--color-border);
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: var(--color-check-modal-text-gray);
  }
  
  /* 화면 가로 420px 이하일 때 텍스트 크기 작게 */
  @media (max-width: 420px) {
    font-size: 12px;
  }
  

`;



// 과제 상태 div 필드 스타일
const BatchStatusDiv = styled.div<{ $isOpen: boolean }>`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 멤버별 기록 편집 모달용 스타일 컴포넌트들
const StatusButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  justify-content: center;
  width: 100%;
  height: 32px;
  min-height: 32px;
  align-items: center;
  
  @media (max-width: 420px) {
    height: 28px;
    min-height: 28px;
    gap: 4px; /* 간격을 더 줄여서 1행 유지 */
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    flex-wrap: nowrap; /* 2행 방지, 1행 유지 */
    height: 28px;
    min-height: 28px;
  }
  
  @media (max-width: 380px) {
    gap: 2px; /* 매우 작은 화면에서 간격 최소화 */
    flex-wrap: nowrap;
  }
  
  @media (max-width: 350px) {
    gap: 1px; /* 극도로 작은 화면에서 간격 최소화 */
    flex-wrap: nowrap;
  }
`;

const StatusButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  height: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    justify-content: center;
    padding: 4px;
    height: 28px;
    min-width: 24px; /* 원형 도형과 숫자만 표시할 때 최소 너비 설정 */
    flex-shrink: 1; /* 필요시 버튼 크기 축소 허용 */
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    height: 28px;
  }
  
  @media (max-width: 380px) {
    padding: 2px 4px;
    height: 28px;
    min-width: 20px;
  }
  
  @media (max-width: 350px) {
    padding: 1px 2px;
    height: 28px;
    min-width: 18px;
  }
`;



const StatusButtonColorDot = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;
  
  @media (max-width: 420px) {
    margin-right: 0;
    width: 20px; /* 기본 크기와 동일하게 유지 */
    height: 20px; /* 기본 크기와 동일하게 유지 */
    font-size: 12px; /* 기본 크기와 동일하게 유지 */
  }
`;

const StatusButtonText = styled.div<{ $isSelected: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 420px) {
    display: none; /* 텍스트 숨김, 원형 도형과 숫자만 표시 */
  }

  @media (max-width: 380px) {
    display: none;
  }

  @media (max-width: 350px) {
    display: none;
  }
`;

const ShortcutHint = styled.div`
  font-size: 12px;
  color: var(--color-text-muted);
  text-align: center;
  margin-bottom: 16px;
  padding: 8px;
  background-color: var(--color-panel);
  border-radius: 6px;
  border: 1px solid var(--color-border);
  
  @media (max-width: 420px) {
    font-size: 11px;
    padding: 6px;
    margin-bottom: 12px;
  }
`;

const PointsInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 12px;
  margin-bottom: 8px;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
  
  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const PointsFieldContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  padding: 12px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  width: 100%;
`;

const PointsFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  white-space: nowrap;
  
  @media (max-width: 480px) {
    text-align: center;
  }
`;

const PointsInputField = styled.input`
  flex: 1;
  height: 28px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-primary);
  background: var(--color-surface);
  min-width: 60px;

  /* placeholder와 value 모두 같은 스타일로 통일 */
  &::placeholder {
    color: var(--color-text-muted); /* 메모 필드와 동일한 placeholder 색상 */
    font-size: 13px; /* 메모 필드와 동일한 placeholder 크기 */
    font-weight: 400;
    text-align: center; /* 중앙 정렬 */
  }

  &:focus {
    outline: none;
    border-color: #000000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const MemoInputField = styled.input`
  flex: 1;
  height: 28px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0 8px;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-primary);
  background: var(--color-surface);
  min-width: 60px;
  text-align: left;

  &::placeholder {
    color: var(--color-text-muted);
    font-size: 13px;
    font-weight: 400;
    text-align: center;
  }

  &:focus {
    outline: none;
    border-color: #000000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const MemoInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
  
  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--color-border);
  margin: 8px 0;
`;

const MinusIcon = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-muted);
  line-height: 1;
`;

const PlusIcon = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-muted);
  line-height: 1;
`;



const PointsMinusBtn = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 24px;
    min-height: 24px;
    font-size: 0.7rem;
  }

  @media (max-width: 360px) {
    width: 28px;
    font-size: 0.65rem;
  }

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PointsPlusBtn = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 24px;
    min-height: 24px;
    font-size: 0.7rem;
  }

  @media (max-width: 360px) {
    width: 28px;
    font-size: 0.65rem;
  }

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PointsApplyBtn = styled.button`
  width: 100px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 96px;
    height: 24px;
    min-height: 24px;
    font-size: 0.7rem;
  }
  
  @media (max-width: 360px) {
    width: 92px;
    height: 24px;
    min-height: 24px;
    font-size: 0.65rem;
  }
`;

const PointsResetBtn = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 24px;
    min-height: 24px;
    font-size: 0.7rem;
  }

  @media (max-width: 360px) {
    width: 28px;
    font-size: 0.65rem;
  }

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MemoEditNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const PrevStudentButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NextStudentButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 루틴 과제 날짜 네비게이션 스타일 컴포넌트들
const DateNavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  border: none;
  
  /* 반응형 레이아웃: 화면이 작아져도 비율 유지 */
  @media (max-width: 480px) {
    gap: 8px;
  }
  
  @media (max-width: 360px) {
    gap: 6px;
  }
`;
const DateNavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  /* 버튼 크기 비율 유지 */
  flex-shrink: 0;
  
  /* 기호 크기를 멤버 선택 버튼과 동일하게 설정 */
  font-size: 18px;
  font-weight: 600;

  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    background: var(--color-panel);
    border-color: var(--color-border);
    color: var(--color-text-muted);
    box-shadow: none;
  }
  
  /* 작은 화면에서도 버튼 크기 유지 */
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
  
  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    font-size: 16px;
  }
`;

const CurrentDateDisplay = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  min-width: 200px;
  
  /* 반응형 텍스트 크기: 가로 420px부터 비율에 따라 조정 */
  @media (max-width: 480px) {
    font-size: 15px;
    min-width: 180px;
  }
  
  @media (max-width: 420px) {
    font-size: 14px;
    min-width: 160px;
  }
  
  @media (max-width: 380px) {
    font-size: 13px;
    min-width: 140px;
  }
  
  @media (max-width: 340px) {
    font-size: 12px;
    min-width: 120px;
  }
`;